import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  message,
  Modal,
  Pagination,
  Skeleton,
  Space,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  CompassOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileTextOutlined,
  FlagOutlined,
  HomeOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  MailOutlined,
  MessageOutlined,
  PictureOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  SwapOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import useTasks from "hooks/tasks/useTasks";
import apiClient from "utils/apiClient";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { ACTOR_ID_HEADER_KEY, API_BASE_URL, O_API_KEY, O_CLIENT_APP, PAYMENT_SERVICE_BASE_URL, STORAGE_BASE_URL } from "constants";
import "./OlitaskMyTasks.css";

const { Paragraph, Text, Title } = Typography;

const PAGE_SIZE = 10;
const NEARBY_LIST_PAGE_SIZE = 5;
const NEARBY_MAP_PAGE_SIZE = 50;
const DEFAULT_NEARBY_LOCATION = { latitude: 47.6062, longitude: -122.3321 };
const DEFAULT_CHARGE_COUNTRY = "US";
const CHARGES_APP = "OLITASK_MOBILE_UI";
const CHARGES_STAGE = "OLITASK_MOBILE_UI_ALL";
const CHARGE_TARGET = "CHECKOUT";

const ACTIVE_REQUESTER_STATUSES = new Set([
  "NEW",
  "ACCEPTED",
  "ARRIVED",
  "NAVIGATING",
  "IN_PROGRESS",
  "IN_REVIEW",
]);

const FILTER_BUTTONS = [
  {
    value: "ACTIVE",
    label: "Active",
    borderColor: "#7BC69A",
    backgroundColor: "#F3FBF6",
    textColor: "#166534",
    activeBackgroundColor: "#166534",
    activeTextColor: "#F6FFF8",
  },
  {
    value: "NEW",
    label: "New",
    borderColor: OLIVEEB_COLORS.oliveebGreen,
    backgroundColor: "#F2FCF8",
    textColor: OLIVEEB_COLORS.oliveebGreen,
    activeBackgroundColor: OLIVEEB_COLORS.oliveebGreen,
    activeTextColor: "#F0FDFA",
  },
  {
    value: "ACCEPTED",
    label: "Accepted",
    borderColor: "#9CC9F5",
    backgroundColor: "#F3F8FF",
    textColor: "#1D4ED8",
    activeBackgroundColor: "#1D4ED8",
    activeTextColor: "#EFF6FF",
  },
  {
    value: "NAVIGATING",
    label: "OTW",
    borderColor: "#709b04",
    backgroundColor: "#F3F8FF",
    textColor: "#709b04",
    activeBackgroundColor: "#709b04",
    activeTextColor: "#EFF6FF",
  },
  {
    value: "IN_PROGRESS",
    label: "In Progress",
    borderColor: "#A5B4FC",
    backgroundColor: "#F5F5FF",
    textColor: "#4338CA",
    activeBackgroundColor: "#4338CA",
    activeTextColor: "#EEF2FF",
  },
  {
    value: "COMPLETED",
    label: "Completed",
    borderColor: "#B6E3A8",
    backgroundColor: "#F6FCF2",
    textColor: "#3F7A18",
    activeBackgroundColor: "#3F7A18",
    activeTextColor: "#F7FEE7",
  },
  {
    value: "CANCELLED",
    label: "Cancelled",
    borderColor: "#F2A7A7",
    backgroundColor: "#FFF5F5",
    textColor: "#B42318",
    activeBackgroundColor: "#B42318",
    activeTextColor: "#FEF2F2",
  },
  {
    value: "EXPIRED",
    label: "Expired",
    borderColor: "#D4B5F8",
    backgroundColor: "#FBF7FF",
    textColor: "#7C3AED",
    activeBackgroundColor: "#7C3AED",
    activeTextColor: "#F5F3FF",
  },
  {
    value: "ALL",
    label: "All",
    borderColor: "#CBD5E1",
    backgroundColor: "#F8FAFC",
    textColor: "#334155",
    activeBackgroundColor: "#334155",
    activeTextColor: "#F8FAFC",
  },
];

const REQUEST_SHORTCUTS = [
  {
    title: "General Task",
    text: "Everyday help",
    icon: <HomeOutlined />,
    path: paths.taskCreateGeneral,
    tone: "general",
  },
  {
    title: "Request Airport Luggage Help",
    text: "Airport luggage help",
    icon: <RocketOutlined />,
    path: paths.taskCreateTravo,
    tone: "travo",
  },
  {
    title: "Home Care",
    text: "Care and assisted living",
    icon: <CheckSquareOutlined />,
    path: paths.taskCreateCare,
    tone: "care",
  },
];

const TRAVO_SERVICE_LABELS = {
  OFFLOAD_FROM_CAR: "Offload from car",
  LOAD_TO_CAR: "Load to car",
  PICK_FROM_BELT: "Pick from belt",
  CARRY_MOVE: "Carry / move bags",
  CHECKIN_BAGS: "Check-in bags",
  WEIGH_LUGGAGE: "Weigh luggage",
  TROLLEY: "Trolley",
  GUIDANCE: "Guidance",
  TRANSPORTATION: "Transportation",
  OTHER: "Other",
};

const TRAVO_MEETING_LABELS = {
  GATE: "Gate",
  BAGGAGE_CLAIM: "Baggage claim",
  CURBSIDE: "Curbside / pickup",
  CHECKIN: "Check-in",
  SECURITY: "Security",
  ARRIVALS: "Arrivals",
  DEPARTURES: "Departures",
  OTHER: "Other",
};

const BASE_TASK_STAGES = ["NEW", "ACCEPTED", "NAVIGATING", "ARRIVED", "IN_PROGRESS", "COMPLETED"];
const TASK_STAGE_LABELS = {
  NEW: "New",
  ACCEPTED: "Accepted",
  NAVIGATING: "OTW",
  ARRIVED: "Arrived",
  IN_PROGRESS: "In progress",
  COMPLETED: "Done",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
};

const COUNTER_OFFER_REASONS = [
  "Needs more time",
  "Travel is farther",
  "Higher effort",
  "Materials needed",
  "Other",
];
const CHAT_AFTER_COMPLETION_MS = 20 * 60 * 1000;

const isHelperUse = (value) => ["HELPER", "HIRING"].includes(String(value || "").toUpperCase());

const normalizeStatus = (status) => String(status || "NEW").toUpperCase();

const isWithinMs = (value, durationMs = CHAT_AFTER_COMPLETION_MS, now = Date.now()) => {
  if (!value) return false;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return false;
  const elapsedMs = now - date.getTime();
  return elapsedMs >= 0 && elapsedMs <= durationMs;
};

const statusAllowsActiveTaskChat = (status) =>
  ["ACCEPTED", "NAVIGATING", "ARRIVED", "IN_PROGRESS"].includes(normalizeStatus(status));

const taskUpdatedAtValue = (task) =>
  firstText(
    task?.lastUpdatedAt,
    task?.lastModifiedAt,
    task?.modifiedAt,
    task?.updatedAtUtc,
    task?.updatedAt,
    task?.updatedOn,
    task?.updated_at,
  );

const taskPostedAtValue = (task) =>
  firstText(task?.postedAt, task?.createdAt, task?.createdAtUtc, task?.createdOn, task?.created_at);

const googleMapsKeyFromConfigResponse = (response) => {
  const config = response?.data?.data || response?.data || {};
  return firstText(
    config.googleMapsApiKey,
    config.googleMapsApiMatrixKey,
    config.googleMapsApiBrowserKey,
    config.googleMapsKey,
    config.mapsApiKey,
  );
};

const sameTaskTimestamp = (left, right) => {
  if (!left || !right) return true;
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
    return leftTime === rightTime;
  }
  return String(left) === String(right);
};

function useTaskStatusPolling({ taskId, task, refreshTask, enabled = true }) {
  const refreshRef = useRef(refreshTask);
  const currentUpdatedAt = taskUpdatedAtValue(task);

  useEffect(() => {
    refreshRef.current = refreshTask;
  }, [refreshTask]);

  useEffect(() => {
    if (!enabled || !taskId || !task || !currentUpdatedAt) return undefined;
    let cancelled = false;

    const checkTaskUpdate = async () => {
      try {
        const response = await apiClient.get(`/olitask-service/api/v1/tasks/${taskId}/last-updated`);
        if (cancelled) return;
        const payload = response?.data?.data || response?.data || {};
        const latestUpdatedAt = firstText(payload.lastUpdatedAt, payload.updatedAt, payload.updatedAtUtc);
        if (latestUpdatedAt && !sameTaskTimestamp(latestUpdatedAt, currentUpdatedAt)) {
          await refreshRef.current?.({ silent: true });
        }
      } catch {
        // Keep the page quiet; the next poll or user action can recover.
      }
    };

    const timer = window.setInterval(checkTaskUpdate, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [currentUpdatedAt, enabled, task, taskId]);
}

function useTaskDirectPolling({ taskId, refreshTask, enabled = true, intervalMs = 3000 }) {
  const refreshRef = useRef(refreshTask);

  useEffect(() => {
    refreshRef.current = refreshTask;
  }, [refreshTask]);

  useEffect(() => {
    if (!enabled || !taskId) return undefined;
    let cancelled = false;
    let timeoutId = null;
    let inFlight = false;

    const poll = async () => {
      if (cancelled) return;
      if (inFlight) {
        timeoutId = window.setTimeout(poll, intervalMs);
        return;
      }

      inFlight = true;
      try {
        await refreshRef.current?.({ silent: true });
      } catch {
        // Keep polling; transient errors should not stop live status updates.
      } finally {
        inFlight = false;
        if (!cancelled) timeoutId = window.setTimeout(poll, intervalMs);
      }
    };

    timeoutId = window.setTimeout(poll, intervalMs);
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [enabled, intervalMs, taskId]);
}

const isHelperOwnedFilter = (filter) => !["NEW", "ACTIVE"].includes(String(filter || "").toUpperCase());

const radiusToDeltas = (radiusMiles, latitude) => {
  const latDelta = Math.max(0.02, (radiusMiles * 2) / 69);
  const cos = Math.cos((latitude * Math.PI) / 180) || 1;
  const lngDelta = Math.max(0.02, (radiusMiles * 2) / (69 * cos));
  return { latitudeDelta: latDelta, longitudeDelta: lngDelta };
};

const titleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const uniqueTextValues = (...values) =>
  Array.from(
    new Set(
      values
        .flat()
        .map((value) => (typeof value === "number" ? String(value) : typeof value === "string" ? value.trim() : ""))
        .filter(Boolean),
    ),
  );

const userIdOf = (user) =>
  firstText(
    user?.id,
    user?.userId,
    user?.profile?.id,
    user?.profile?.userId,
    user?.user?.id,
    user?.user?.userId,
  );

const numberValue = (...values) => {
  for (const value of values) {
    const next = Number(value);
    if (Number.isFinite(next)) return next;
  }
  return null;
};

const taskCoordinates = (task) => {
  const source = task?.location || task?.taskLocation || task?.address || {};
  const latitude = numberValue(
    task?.latitude,
    task?.lat,
    source.latitude,
    source.lat,
    source.geometry?.location?.lat,
  );
  const longitude = numberValue(
    task?.longitude,
    task?.lon,
    task?.lng,
    source.longitude,
    source.lon,
    source.lng,
    source.geometry?.location?.lng,
  );
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

const selectedHelperContext = ({ selectedWorkspaceId, selectedWorkspace, profileId, backgroundCheckId }) => {
  const helperWorkspaceId = selectedWorkspaceId || undefined;
  const helperOrganizationId = selectedWorkspace?.organizationId || selectedWorkspace?.organization?.id || undefined;
  const helperProfessionProfileId = profileId || undefined;
  const helperBackgroundCheckId = backgroundCheckId || undefined;

  const params = new URLSearchParams();
  if (helperWorkspaceId) {
    params.set("workspaceId", helperWorkspaceId);
    params.set("helperWorkspaceId", helperWorkspaceId);
  }
  if (helperOrganizationId) params.set("helperOrganizationId", helperOrganizationId);
  if (helperProfessionProfileId) params.set("helperProfessionProfileId", helperProfessionProfileId);
  if (helperBackgroundCheckId) params.set("helperBackgroundCheckId", helperBackgroundCheckId);

  const body = {
    ...(helperWorkspaceId ? { workspaceId: helperWorkspaceId, helperWorkspaceId } : {}),
    ...(helperOrganizationId ? { helperOrganizationId } : {}),
    ...(helperProfessionProfileId ? { helperProfessionProfileId } : {}),
    ...(helperBackgroundCheckId ? { helperBackgroundCheckId } : {}),
  };

  return {
    body: Object.keys(body).length ? body : undefined,
    query: params.toString(),
  };
};

const taskActionHeaders = (actorId, { xActor = false, actor = true } = {}) => ({
  ...(actorId && actor ? { [ACTOR_ID_HEADER_KEY]: actorId } : {}),
  ...(actorId && xActor ? { "x-actor-id": actorId } : {}),
});

const withdrawInterestQuery = (selectedWorkspaceId) => {
  const params = new URLSearchParams();
  if (selectedWorkspaceId) {
    params.set("workspaceId", selectedWorkspaceId);
    params.set("helperWorkspaceId", selectedWorkspaceId);
  }
  return params.toString();
};

const withdrawInterestHeaders = () => {
  const clientId = localStorage.getItem("clientId");
  return {
    "Content-Type": "application/json",
    "O-CLIENT-APP": O_CLIENT_APP,
    "O-API-KEY": O_API_KEY,
    "O-SKIP-WORKSPACE-CONTEXT": "1",
    ...(clientId ? { "O-CLIENT-ID": clientId } : {}),
  };
};

const withdrawInterestRequest = (taskId, selectedWorkspaceId) => {
  const query = withdrawInterestQuery(selectedWorkspaceId);
  return apiClient.put(
    `/olitask-service/api/v1/tasks/${taskId}/withdraw-interest${query ? `?${query}` : ""}`,
    undefined,
    { headers: withdrawInterestHeaders() },
  );
};

const acceptHelperHeaders = () => ({
  "Content-Type": "application/json",
  "O-CLIENT-APP": O_CLIENT_APP,
  "O-API-KEY": O_API_KEY,
  "O-SKIP-WORKSPACE-CONTEXT": "1",
  ...(localStorage.getItem("clientId") ? { "O-CLIENT-ID": localStorage.getItem("clientId") } : {}),
});

const locationPermissionMessage = () =>
  [
    "Location access is needed before starting navigation.",
    "Allow location for this site from the browser prompt, or click the site/lock icon in the address bar and set Location to Allow.",
    "Also make sure Location Services are enabled for this browser in your device settings, then try again.",
  ].join(" ");

const locationSettingsUrl = () => {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent || "";
  const site = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  if (/Edg\//i.test(ua)) return site ? `edge://settings/content/siteDetails?site=${site}` : "edge://settings/content/location";
  if (/Chrome|CriOS/i.test(ua) && !/Firefox|FxiOS/i.test(ua)) {
    return site ? `chrome://settings/content/siteDetails?site=${site}` : "chrome://settings/content/location";
  }
  if (/Firefox|FxiOS/i.test(ua)) return "about:preferences#privacy";
  return "";
};

const showLocationPermissionAlert = (detail) => {
  const settingsUrl = locationSettingsUrl();
  Modal.confirm({
    title: "Allow location access",
    content: detail || locationPermissionMessage(),
    okText: settingsUrl ? "Open Browser Settings" : "OK",
    cancelText: "Close",
    onOk: () => {
      if (!settingsUrl) return;
      const opened = window.open(settingsUrl, "_blank", "noopener,noreferrer");
      if (!opened) {
        Modal.info({
          title: "Open location settings",
          content: "Your browser blocked opening settings. Use the site/lock icon in the address bar, set Location to Allow, then try Start Navigation again.",
          okText: "OK",
        });
      }
    },
  });
};

const currentLocationPing = (actorId, fallbackCoords, { requirePermission = false } = {}) =>
  new Promise((resolve) => {
    const clientId = localStorage.getItem("clientId") || null;
    const fallback = () =>
      resolve({
        latitude: fallbackCoords?.latitude || 0,
        longitude: fallbackCoords?.longitude || 0,
        source: O_CLIENT_APP,
        clientId,
        actorId: actorId || null,
        pingAt: null,
        approximate: true,
      });
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      if (requirePermission) {
        showLocationPermissionAlert("This browser does not expose location access. Use a browser with location support or enable location services, then try again.");
        resolve(null);
        return;
      }
      fallback();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          source: O_CLIENT_APP,
          clientId,
          actorId: actorId || null,
          pingAt: null,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        if (requirePermission) {
          const denied = error?.code === 1;
          showLocationPermissionAlert(
            denied
              ? locationPermissionMessage()
              : "Precise location is not available yet. Check that location services are enabled for your browser and try again.",
          );
          resolve(null);
          return;
        }
        fallback();
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  });

const firstNumber = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return 0;
};

const valueAt = (source, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => (current == null ? undefined : current[key]), source);

const firstValue = (source, ...paths) => {
  for (const path of paths) {
    const value = valueAt(source, path);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const boolText = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
};

const displayValue = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "boolean") return boolText(value);
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => displayValue(item)).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return firstText(value.name, value.label, value.title, value.code, value.id);
  }
  return String(value);
};

const compactPayload = (source) =>
  Object.entries(source || {}).reduce((payload, [key, value]) => {
    if (value === undefined || value === null || value === "") return payload;
    payload[key] = value;
    return payload;
  }, {});

const moneyNumber = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};

const normalizeChargeText = (value) => String(value || "").trim().toUpperCase();

const chargeSpecificityScore = (config, location) => {
  const targetCountry = normalizeChargeText(location?.country);
  const targetState = normalizeChargeText(location?.state);
  const targetCounty = normalizeChargeText(location?.county);
  const targetCity = normalizeChargeText(location?.city);
  const configCountry = normalizeChargeText(config?.country);
  const configState = normalizeChargeText(config?.state);
  const configCounty = normalizeChargeText(config?.county);
  const configCity = normalizeChargeText(config?.city);

  if (configCountry && targetCountry && configCountry !== targetCountry) return -1;
  if (configState && targetState && configState !== targetState) return -1;
  if (configCounty && targetCounty && configCounty !== targetCounty) return -1;
  if (configCity && targetCity && configCity !== targetCity) return -1;

  return (configCountry ? 1 : 0) + (configState ? 2 : 0) + (configCounty ? 4 : 0) + (configCity ? 8 : 0);
};

const normalizeChargeConfigs = (charges, location) => {
  const byCode = new Map();
  (charges || []).forEach((config) => {
    if (!config?.code) return;
    byCode.set(config.code, [...(byCode.get(config.code) || []), config]);
  });

  return Array.from(byCode.values())
    .map((configs) =>
      [...configs]
        .map((config) => ({ config, score: chargeSpecificityScore(config, location) }))
        .filter((entry) => entry.score >= 0)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          const versionDelta = Number(b.config?.versionNumber || 0) - Number(a.config?.versionNumber || 0);
          if (versionDelta) return versionDelta;
          return Date.parse(b.config?.effectiveFrom || "") - Date.parse(a.config?.effectiveFrom || "");
        })[0]?.config,
    )
    .filter(Boolean)
    .sort((a, b) => {
      const orderDelta = Number(a?.displayOrder ?? 9999) - Number(b?.displayOrder ?? 9999);
      if (orderDelta) return orderDelta;
      return String(a?.code || "").localeCompare(String(b?.code || ""));
    });
};

const fetchEffectiveCharges = async ({ currency, country, state, county, city }) => {
  const query = new URLSearchParams({
    app: CHARGES_APP,
    stages: CHARGES_STAGE,
    chargeTarget: CHARGE_TARGET,
    currency,
    country,
  });
  if (CHARGE_TARGET) query.set("state", CHARGE_TARGET);
  if (state) query.set("state", state);
  if (county) query.set("county", county);
  if (city) query.set("city", city);

  const pageSize = 50;
  const all = [];
  for (let page = 0; page <= 50; page += 1) {
    let response;
    try {
      response = await apiClient.get(`${PAYMENT_SERVICE_BASE_URL}/api/v1/charges/effective/paged?${query.toString()}&page=${page}&size=${pageSize}`);
    } catch (error) {
      const detail = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      throw new Error(detail || "Failed to calculate charges.");
    }
    const data = response?.data || {};
    all.push(...(Array.isArray(data.content) ? data.content : []));
    if (data.last !== false) break;
  }
  return all.filter((config) => normalizeStatus(config?.status) === "ACTIVE");
};

const computeOfferCharges = async (amount, { currency, country, state, county, city }) => {
  const tokenAmount = moneyNumber(amount);
  const tipAmount = 0;
  const appliedCreditsTotal = 0;
  const feeTaxBase = tokenAmount + tipAmount;
  const chargeConfigs = await fetchEffectiveCharges({ currency, country, state, county, city });
  const chargeLines = normalizeChargeConfigs(chargeConfigs, { country, state, county, city }).map((config) => {
    const applyOn = normalizeStatus(config?.applyOn);
    const valueType = normalizeStatus(config?.valueType);
    const base = applyOn === "TOKEN_ONLY" ? tokenAmount : applyOn === "TIP_ONLY" ? tipAmount : feeTaxBase;
    const amountValue = valueType === "PERCENTAGE"
      ? base * Number(config?.value || 0)
      : valueType === "FIXED"
        ? Number(config?.value || 0)
        : 0;
    return {
      chargeId: config?.id,
      code: config?.code,
      name: config?.name,
      description: config?.description ?? null,
      category: config?.category,
      valueType: config?.valueType,
      rate: valueType === "PERCENTAGE" ? config?.value : undefined,
      fixedAmount: valueType === "FIXED" ? config?.value : undefined,
      applyOn: config?.applyOn,
      amount: moneyNumber(amountValue),
      displayOrder: config?.displayOrder ?? null,
      country: config?.country ?? null,
      state: config?.state ?? null,
      county: config?.county ?? null,
      city: config?.city ?? null,
    };
  });
  const totalCharges = chargeLines.reduce((sum, line) => sum + Number(line.amount || 0), 0);

  return {
    currency,
    units: "DOLLAR",
    tokenAmount,
    tipAmount,
    useCredits: false,
    appliedCreditsTotal,
    cashPortion: tokenAmount,
    creditDeductions: [],
    feeTaxBase: moneyNumber(feeTaxBase),
    country,
    state,
    county,
    city,
    chargeLines,
    totalCharge: moneyNumber(feeTaxBase + totalCharges),
  };
};

const codeLabel = (value, labels = {}) => {
  const normalized = String(value || "").toUpperCase();
  return labels[normalized] || titleCase(normalized || value);
};

const taskKey = (task, index) => String(task?.id || task?.taskId || `${task?.title || "task"}-${index}`);

const taskTitle = (task) => {
  const taskType = String(task?.taskType || task?.type || "").toUpperCase();
  if (task?.title) return task.title;
  if (taskType.includes("TRAVO")) return "Request Airport Luggage Help";
  if (taskType.includes("CARE")) return "Home Care & Assisted Living";
  return "Help Request";
};

const taskDescription = (task) =>
  firstText(
    task?.description,
    task?.details,
    task?.taskCreateRequest?.description,
    "Request details will appear here.",
  );

const photoUrl = (task) => {
  const photos = Array.isArray(task?.photos) ? task.photos : [];
  const photo = firstText(
    task?.mainPhoto,
    task?.mainPhotoUrl,
    task?.photoUrl,
    photos[0]?.url,
    photos[0]?.fileUrl,
    photos[0]?.downloadUrl,
    photos[0],
  );
  return /^https?:\/\//i.test(photo) || /^data:image\//i.test(photo) || /^blob:/i.test(photo)
    ? photo
    : "";
};

const taskPhotos = (task) => {
  const photos = Array.isArray(task?.photos) ? task.photos : [];
  const urls = [
    task?.mainPhoto,
    task?.mainPhotoUrl,
    task?.photoUrl,
    ...photos.map((photo) =>
      firstText(photo?.url, photo?.fileUrl, photo?.downloadUrl, photo?.publicUrl, photo),
    ),
  ].filter((url) => /^https?:\/\//i.test(url) || /^data:image\//i.test(url) || /^blob:/i.test(url));
  return Array.from(new Set(urls)).slice(0, 8);
};

const taskCategories = (task) => {
  const categories = Array.isArray(task?.categories) ? task.categories : [];
  const names = [
    ...categories.map((category) => firstText(category?.name, category?.label, category)),
    ...(Array.isArray(task?.categoryNames) ? task.categoryNames : []),
    firstValue(task, "category.name", "category", "taskCategory.name", "taskCategory", "taskCategories.0.name"),
  ].filter(Boolean);
  return Array.from(new Set(names));
};

const moneyText = (task) => {
  const care = careDetails(task);
  const careCompensation = care?.compensation || {};
  const taskCompensation = task?.compensation || {};
  const charges = task?.taskCharges || task?.compensation || {};
  const type = String(
    careCompensation.payType ||
      taskCompensation.payType ||
      task?.payType ||
      charges.payType ||
      task?.chargeType ||
      charges.chargeType ||
      task?.carePaymentType ||
      "",
  ).toUpperCase();
  const currency = firstText(careCompensation.currency, taskCompensation.currency, task?.currency, charges.currency, "USD").toUpperCase();
  const rate = firstNumber(careCompensation.payRate, taskCompensation.payRate, task?.payRate, charges.payRate, charges.hourlyRate, task?.hourlyRate);
  if (rate && type.includes("HOUR")) return `${currency} ${rate.toFixed(2)}/hr`;

  const amount = firstNumber(
    careCompensation.estimatedTotal,
    taskCompensation.estimatedTotal,
    taskCompensation.amount,
    task?.budgetAmount,
    task?.budget,
    task?.estimatedBudget,
    task?.estimatedTotal,
    task?.tokenAmount,
    charges.tokenAmount,
    charges.amount,
    charges.totalAmount,
    charges.chargeableAmount,
  );
  if (!amount) return "";
  return type.includes("LUMP") ? `${currency} ${amount.toFixed(2)} lump sum` : `${currency} ${amount.toFixed(2)}`;
};

const formatOfferMoney = (amount, currency = "USD", payType) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  const value = `${String(currency || "USD").toUpperCase()} ${numeric.toFixed(2)}`;
  return String(payType || "").toUpperCase().includes("HOUR") ? `${value}/hr` : value;
};

const offerAmountLabel = (payType, owner = "Your") =>
  String(payType || "").toUpperCase().includes("HOUR")
    ? `${owner} hourly rate`
    : `${owner} amount`;

const formatDurationLabel = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const raw = String(value).trim();
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*(minutes?|mins?)?$/i);
  if (!match) return raw;

  const totalMinutes = Math.round(Number(match[1]));
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return raw;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hourPart = hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const minutePart = minutes > 0 ? `${minutes} minute${minutes > 1 ? "s" : ""}` : "";

  if (hours && minutes) return `${hourPart} ${minutePart}`;
  return hourPart || minutePart || "0 minutes";
};

const parseDurationToMinutes = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? Math.round(value) : 0;
  const raw = String(value).toLowerCase();
  const hours = Number(raw.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)/)?.[1] || 0);
  const minutes = Number(raw.match(/(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes)/)?.[1] || 0);
  if (hours || minutes) return Math.round(hours * 60 + minutes);
  const numeric = Number(raw.replace(/[^0-9.]/g, ""));
  return Number.isFinite(numeric) ? Math.round(numeric) : 0;
};

const durationText = (task) => {
  const direct = firstText(task?.actualDuration, task?.requesterDuration, task?.duration);
  if (direct) return formatDurationLabel(direct);
  const care = careDetails(task);
  const schedule = care?.schedule || task?.schedule || {};
  const minutes = firstNumber(
    schedule.durationMinutes,
    task?.durationMinutes,
    task?.taskCreateRequest?.durationMinutes,
  );
  return minutes ? formatDurationLabel(minutes) : "";
};

const nearbyDistanceText = (task) => {
  const raw = firstNumber(
    task?.drivingInfo?.distanceMiles,
    task?.distanceMiles,
    task?.distanceInMiles,
    task?.distance,
  );
  if (!raw) return "";
  const miles = Number(raw);
  if (!Number.isFinite(miles) || miles <= 0) return "";
  return `${miles.toFixed(miles >= 10 ? 0 : 2)} miles away`;
};

const counterOfferConfig = (task) => {
  const care = careDetails(task);
  const careSchedule = care?.schedule || {};
  const careCompensation = care?.compensation || {};
  const payType = firstText(careCompensation.payType, task?.payType, task?.taskCharges?.payType);
  const isHourly = String(payType || "").toUpperCase().includes("HOUR");
  const isCare = isCareTask(task);
  const careImmediate =
    Boolean(careSchedule.immediate) || String(careSchedule.mode || "").toUpperCase() === "IMMEDIATE";
  const currentDuration = isCare
    ? formatDurationLabel(careSchedule.durationMinutes) || durationText(task)
    : durationText(task);
  const amount = isCare
    ? firstNumber(
        isHourly ? careCompensation.payRate : careCompensation.estimatedTotal,
        task?.tokenAmount,
        task?.taskCharges?.tokenAmount,
        task?.taskCharges?.amount,
      )
    : firstNumber(task?.tokenAmount, task?.taskCharges?.tokenAmount, task?.taskCharges?.amount);
  const currency = firstText(careCompensation.currency, task?.taskCharges?.currency, task?.currency, "USD").toUpperCase();

  return {
    allowDurationCounter: !isCare || careImmediate,
    amount,
    amountLabel: isCare && isHourly ? "Hourly rate" : "Amount",
    amountUnit: isCare && isHourly ? `${currency}/hr` : currency,
    counterPayRateFromAmount: isCare,
    counterPayType: isCare ? payType : "",
    currentDuration,
    currentDurationMinutes: parseDurationToMinutes(currentDuration),
    message: isCare
      ? careImmediate
        ? `Update the ${isHourly ? "hourly rate" : "amount"} and how long this instant Home Care & Assisted Living request will take.`
        : `Update the ${isHourly ? "hourly rate" : "amount"} for this scheduled Home Care & Assisted Living request. The scheduled time cannot be changed.`
      : `What amount feels fair for this ${isGeneralTask(task) ? "task" : "shift"}, and how long do you think it will take?`,
  };
};

const dateText = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const locationText = (task) => {
  const location = task?.location || {};
  if (typeof location === "string") return location;
  const cityZip = [location.city, location.zipCode || location.postalCode].filter(Boolean).join(" ");
  return firstText(
    cityZip,
    task?.address,
    location.formattedAddress,
    location.address,
    location.label,
    [location.city, location.state].filter(Boolean).join(", "),
  );
};

const fullLocationText = (task) => {
  const location = task?.location || {};
  if (typeof location === "string") return location;
  return firstText(
    task?.address,
    location.formattedAddress,
    location.address,
    location.label,
    [location.city, location.state, location.zipCode || location.postalCode].filter(Boolean).join(", "),
  );
};

const locationSource = (task) =>
  task?.location ||
  task?.taskLocation ||
  task?.careTask?.location ||
  task?.careTaskDetails?.location ||
  task?.careDetails?.location ||
  {};

const locationAreaText = (task) => {
  const source = locationSource(task);
  if (typeof source === "string") {
    const parts = source.split(",").map((part) => part.trim()).filter(Boolean);
    return parts.slice(1, 3).join(", ") || parts[0] || "";
  }
  const addressParts =
    typeof source.address === "string"
      ? source.address.split(",").map((part) => part.trim()).filter(Boolean)
      : [];
  const city = firstText(source.city, source.town, source.locality, addressParts[1]);
  const state = firstText(source.state, source.region, source.province, addressParts[2]);
  return [city, state].filter(Boolean).join(", ") || firstText(source.label, source.displayAddress);
};

const taskLocationRows = (task, { canViewFullLocation = false } = {}) => {
  const source = locationSource(task);
  const care = careDetails(task);
  const entity = care?.careEntity || care?.entity || {};
  const organizationLocationAddress = entity.organizationLocationAddress || {};
  const organizationLocationType = firstText(
    entity.organizationLocationType?.name,
    entity.organizationLocationTypeName,
    entity.organizationLocationType,
    organizationLocationAddress.type,
  );
  const displayLocation = canViewFullLocation
    ? firstText(
        fullLocationText(task),
        organizationLocationAddress.formattedAddress,
        organizationLocationAddress.address,
        locationText(task),
      )
    : firstText(locationAreaText(task), locationAreaText({ location: organizationLocationAddress }));

  return {
    title: isCareTask(task) ? "Care Location" : isGeneralTask(task) ? "Task Location" : "Shift Location",
    privacyText: canViewFullLocation ? "" : "Full address shared after acceptance.",
    rows: detailRows([
      isCareTask(task)
        ? {
            label: "Organization location",
            value: canViewFullLocation
              ? firstText(entity.organizationLocationName, source.organizationLocationName)
              : "Shared after acceptance",
          }
        : null,
      isCareTask(task) ? { label: "Location type", value: organizationLocationType } : null,
      { label: canViewFullLocation ? "Address" : "Area", value: displayLocation },
    ].filter(Boolean)),
  };
};

const personName = (person) =>
  firstText(
    person?.fullName,
    person?.name,
    [person?.firstName, person?.lastName].filter(Boolean).join(" "),
    person?.email,
    person?.id,
  );

const organizationName = (organization, workspace) =>
  firstText(
    organization?.name,
    organization?.organizationName,
    organization?.businessName,
    organization?.displayName,
    workspace?.organization?.name,
    workspace?.name,
  );

const organizationOnlyName = (organization, workspace) =>
  firstText(
    organization?.name,
    organization?.organizationName,
    organization?.businessName,
    organization?.displayName,
    workspace?.organization?.name,
    workspace?.organization?.organizationName,
    workspace?.organization?.businessName,
    workspace?.organization?.displayName,
  );

const isTravoTask = (task) => String(task?.taskType || task?.type || "").toUpperCase().includes("TRAVO");

const isCareTask = (task) => {
  const taskType = String(task?.taskType || task?.type || "").toUpperCase();
  return taskType.includes("CARE") || Boolean(task?.careTask || task?.careTaskDetails || task?.careDetails);
};

const isGeneralTask = (task) => {
  const taskType = String(task?.taskType || task?.type || task?.requestType || "").toUpperCase();
  return taskType.includes("GENERAL") || (!isCareTask(task) && !isTravoTask(task));
};

const taskMatchesKind = (task, taskKind) => {
  const kind = String(taskKind || "CARE").toUpperCase();
  if (kind === "GENERAL") return isGeneralTask(task);
  if (kind === "TRAVO") return isTravoTask(task);
  if (kind === "ALL") return true;
  return isCareTask(task);
};

const careDetails = (task) => task?.careTask || task?.careTaskDetails || task?.careDetails || {};

const travoDetails = (task) =>
  task?.travoTask ||
  task?.travoTaskDetails ||
  task?.airportTask ||
  task?.airportTaskDetails ||
  task?.taskCreateRequest ||
  {};

const detailRows = (rows) =>
  rows
    .filter(Boolean)
    .map((row) => ({ ...row, value: displayValue(row.value) }))
    .filter((row) => row.value);

const actorContactRows = (entity) => {
  const source = entity || {};
  return detailRows([
    { label: "Name", value: personName(source) || organizationName(source) },
    { label: "Email", value: firstText(source.email, source.personalEmail, source.workEmail, source.contactEmail) },
    { label: "Phone", value: firstText(source.phone, source.mobilePhone, source.workPhone, source.contactPhone) },
  ]);
};

const organizationRows = (entity) => {
  const source = entity || {};
  return detailRows([
    { label: "Name", value: organizationName(source) },
    { label: "Description", value: source.description },
    { label: "Type", value: titleCase(firstText(source.organizationLocationType, source.type)) },
    { label: "Status", value: firstText(source.status, "PENDING_REVIEW") },
  ]);
};

const organizationContactRows = (entity) => {
  const source = entity?.contactInformation || entity?.contact || entity || {};
  return detailRows([
    { label: "Primary email", value: firstText(source.primaryEmail, source.email, source.contactEmail, source.businessEmail) },
    { label: "Secondary email", value: source.secondaryEmail },
    { label: "Primary phone", value: firstText(source.primaryPhone, source.phone, source.contactPhone, source.businessPhone) },
    { label: "Secondary phone", value: source.secondaryPhone },
    { label: "Website", value: firstText(source.websiteUrl, source.website) },
    { label: "Contact person", value: source.contactPersonName },
    { label: "Contact title", value: source.contactPersonTitle },
  ]);
};

const organizationLocationRows = (entity) => {
  const source = entity?.locationInformation || entity?.location || entity?.address || {};
  return detailRows([
    { label: "Address", value: firstText(source.formattedAddress, source.address, entity?.address) },
    { label: "City", value: source.city },
    { label: "State", value: source.state },
    { label: "ZIP", value: firstText(source.zipCode, source.postalCode) },
  ]);
};

const organizationIdFrom = (organization, workspace) =>
  firstText(
    organization?.id,
    organization?.organizationId,
    workspace?.organizationId,
    workspace?.organization?.id,
    workspace?.organization?.organizationId,
  );

const actorProfileId = (entity) =>
  firstText(
    entity?.userProfileId,
    entity?.userProfile?.id,
    entity?.profile?.id,
    entity?.user?.id,
    entity?.id,
    entity?.userId,
    entity?.profileId,
    entity?.requesterId,
    entity?.helperId,
  );

const fetchActorProfileByType = async (actorId, actorType = "helper") => {
  if (!actorId) return null;
  const endpoints = actorType === "helper"
    ? [
        `/olitask-service/api/v1/helper-profiles/${actorId}`,
        `/olitask-service/api/v1/helpers/${actorId}`,
        `/identity-service/api/v1/users/${actorId}`,
      ]
    : [
        `/olitask-service/api/v1/requester-profiles/${actorId}`,
        `/olitask-service/api/v1/requesters/${actorId}`,
        `/identity-service/api/v1/users/${actorId}`,
      ];

  for (const endpoint of endpoints) {
    try {
      const response = await apiClient.get(endpoint);
      const payload = response?.data?.data || response?.data;
      if (payload) return payload;
    } catch {
      // Try the next endpoint.
    }
  }
  return null;
};

const actorProfileRows = (entity, roleLabel) => {
  const source = entity || {};
  return detailRows([
    { label: roleLabel === "Requester" ? "Previous Requests" : "Completed Shifts", value: firstText(source.completedTasks, source.completedTaskCount, source.taskCount, 0) },
    { label: "Account Status", value: source.isVerified || source.verified ? "Verified" : "Not Verified" },
    { label: "Location", value: firstText(source.location, source.city, source.state) },
    { label: "Sex", value: firstText(source.sex, source.gender, source.otherGender) },
  ]);
};

const actorBioRows = (entity) =>
  detailRows([{ label: "Bio", value: firstText(entity?.description, entity?.bio, "No bio available.") }]);

const withoutContactRows = (rows) =>
  (rows || []).filter((row) => !["Email", "Phone"].includes(row.label));

const directFileUrlValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("/storage-service")) return `${API_BASE_URL}${trimmed}`;
    if (trimmed.startsWith("/api/v1/files")) return `${STORAGE_BASE_URL}${trimmed}`;
    if (trimmed.startsWith("/")) return `${API_BASE_URL}${trimmed}`;
    return "";
  }
  if (typeof value !== "object") return "";
  return (
    value.signedUrl ||
    value.signedURL ||
    value.signed_url ||
    value.downloadUrl ||
    value.downloadURL ||
    value.download_url ||
    value.previewUrl ||
    value.previewURL ||
    value.preview_url ||
    value.publicUrl ||
    value.publicURL ||
    value.public_url ||
    value.fileUrl ||
    value.fileURL ||
    value.file_url ||
    value.mediaUrl ||
    value.mediaURL ||
    value.media_url ||
    value.url ||
    value.uri ||
    value.href ||
    directFileUrlValue(value.file) ||
    directFileUrlValue(value.data) ||
    ""
  );
};

const fileIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || /^(https?:|data:|blob:)/i.test(trimmed)) return "";
    return trimmed.replace(/^file-service:\/\//i, "");
  }
  if (typeof value !== "object") return "";
  return (
    value.fileId ||
    value.fileID ||
    value.file_id ||
    value.id ||
    value.uuid ||
    value.key ||
    value.file?.fileId ||
    value.file?.id ||
    value.file?.uuid ||
    value.data?.fileId ||
    value.data?.id ||
    ""
  );
};

const photoSourceValue = (...values) => {
  let fallbackFileId = "";
  for (const value of values) {
    const directUrl = directFileUrlValue(value);
    if (directUrl) return { url: directUrl, fileId: "" };
    const fileId = fileIdValue(value);
    if (!fallbackFileId && fileId) fallbackFileId = fileId;
  }
  return fallbackFileId
    ? {
        url: `${STORAGE_BASE_URL}/files/${encodeURIComponent(fallbackFileId)}`,
        fileId: fallbackFileId,
      }
    : { url: "", fileId: "" };
};

const responseUrlValue = (payload, visited = new WeakSet()) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload.trim();
  if (typeof payload !== "object") return "";
  if (visited.has(payload)) return "";
  visited.add(payload);
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const url = responseUrlValue(item, visited);
      if (url) return url;
    }
    return "";
  }
  const directUrl = directFileUrlValue(payload);
  if (directUrl) return directUrl;
  for (const value of Object.values(payload)) {
    const url = responseUrlValue(value, visited);
    if (url) return url;
  }
  return "";
};

const resolveSignedPhotoUrl = async (fileId) => {
  if (!fileId) return "";
  try {
    const response = await apiClient.get("/storage-service/api/v1/files/photo/signed-url", {
      params: { fid: fileId },
    });
    const url = responseUrlValue(response?.data);
    if (url) return url;
  } catch {
    // Fall back to the batch endpoint below.
  }

  try {
    const response = await apiClient.get("/storage-service/api/v1/files/photo/signed-urls", {
      params: { fids: fileId },
    });
    return responseUrlValue(response?.data);
  } catch {
    return "";
  }
};

const profilePhotoSource = (entity) =>
  photoSourceValue(
    entity?.profilePhotoUrl,
    entity?.profilePhoto,
    entity?.profilePhotoFileId,
    entity?.profilePhotoId,
    entity?.photoUrl,
    entity?.photo,
    entity?.photoFileId,
    entity?.photoId,
    entity?.avatarUrl,
    entity?.avatar,
    entity?.avatarFileId,
    entity?.avatarId,
    entity?.imageUrl,
    entity?.image,
    entity?.imageFileId,
    entity?.picture,
    entity?.pictureUrl,
    entity?.pictureFileId,
    entity?.profile?.profilePhoto,
    entity?.profile?.profilePhotoUrl,
    entity?.profile?.profilePhotoFileId,
    entity?.userProfile?.profilePhoto,
    entity?.userProfile?.profilePhotoUrl,
    entity?.userProfile?.profilePhotoFileId,
    entity?.user?.profilePhoto,
    entity?.user?.profilePhotoUrl,
    entity?.user?.profilePhotoFileId,
    entity?.identity?.profilePhoto,
    entity?.identity?.profilePhotoUrl,
    entity?.identity?.profilePhotoFileId,
  );

const securityPhotoSource = (entity) =>
  photoSourceValue(
    entity?.securityPhotoUrl,
    entity?.securityPhoto,
    entity?.identityPhoto,
    entity?.userIdentityPhoto,
    entity?.verificationPhoto,
    entity?.userIdentityVerification?.profilePhoto,
    entity?.userIdentityVerification?.photo,
    entity?.userIdentityVerification?.frontPhoto,
    entity?.userIdentityVerification?.frontPhotoUrl,
    entity?.userIdentityVerification?.profilePhotoUrl,
    entity?.userIdentityVerification?.securityPhoto,
    entity?.userIdentityVerification?.securityPhotoUrl,
  );

const profilePhoto = (entity) => profilePhotoSource(entity).url;

const securityPhoto = (entity) => securityPhotoSource(entity).url;

const joinedText = (entity) => {
  const value =
    entity?.signedUpAt ||
    entity?.createdAt ||
    entity?.joinedAt ||
    entity?.registeredAt ||
    entity?.createdOn ||
    entity?.createdDate;
  if (!value) return "N/A";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const isVerifiedProfile = (entity) =>
  Boolean(entity?.isVerified ?? entity?.verified ?? entity?.userIdentityVerification?.verified);

const ratingText = (entity) => {
  const rating = Number(entity?.averageRating ?? entity?.rating);
  return Number.isFinite(rating) && rating > 0 ? rating.toFixed(1) : "N/A";
};

const profileSkills = (entity) => {
  const skills = Array.isArray(entity?.helperSkills)
    ? entity.helperSkills
    : Array.isArray(entity?.skills)
      ? entity.skills
      : [];
  return skills
    .map((helperSkill) => {
      const skill = helperSkill?.skill || helperSkill || {};
      const name = firstText(skill?.name, helperSkill?.skillName, helperSkill?.name, skill?.title);
      return {
        id: firstText(helperSkill?.id, skill?.id, name),
        name,
        description: firstText(skill?.description, helperSkill?.description),
        synonyms: firstText(skill?.synonyms, helperSkill?.synonyms, skill?.relatedTerms, helperSkill?.relatedTerms),
        proficiencyLevel: firstText(helperSkill?.proficiencyLevel, helperSkill?.proficiency, helperSkill?.level, "N/A"),
        yearsOfExperience: firstText(helperSkill?.yearsOfExperience, helperSkill?.experienceYears, helperSkill?.years, 0),
      };
    })
    .filter((skill) => skill.name);
};

const shouldShowTaskChatLinks = (task, userId) => {
  const requesterId = actorProfileId(task?.requester);
  const helperId = actorProfileId(task?.helper);
  if (!task || !requesterId || !helperId || !userId) return false;
  if (![requesterId, helperId].includes(userId)) return false;

  const status = normalizeStatus(task.status);
  if (statusAllowsActiveTaskChat(status)) return true;

  if (status === "COMPLETED") {
    const completedAt =
      task?.helperCompletedAt ||
      task?.completedAt ||
      task?.completed_at ||
      task?.updatedAtUtc ||
      task?.updatedAt;
    return isWithinMs(completedAt);
  }

  if (status === "CANCELLED" && statusAllowsActiveTaskChat(task?.cancel?.cancelTaskStatus)) {
    return isWithinMs(task?.cancel?.cancelledAt || task?.updatedAtUtc || task?.updatedAt);
  }

  return false;
};

const taskChatPath = ({ task, recipientId, recipientName, workspaceId }) => {
  const params = new URLSearchParams();
  if (task?.conversationId) params.set("conversationId", task.conversationId);
  if (recipientId) {
    params.set("participantId", recipientId);
    params.set("recipientId", recipientId);
  }
  if (recipientName) params.set("recipientName", recipientName);
  if (task?.id || task?.taskId) params.set("taskId", firstText(task?.id, task?.taskId));
  if (workspaceId) params.set("workspaceId", workspaceId);
  const query = params.toString();
  return query ? `${paths.chatSpace}?${query}` : paths.chatSpace;
};

const actorGroups = (task, onActorOpen, { userId, onChatOpen, selectedWorkspaceId } = {}) => {
  const requesterOrganization = task?.requesterOrganization || task?.requesterWorkspace?.organization;
  const helperOrganization = task?.helperOrganization || task?.helperWorkspace?.organization;
  const requesterOrganizationName = organizationOnlyName(requesterOrganization, task?.requesterWorkspace);
  const helperOrganizationName = organizationOnlyName(helperOrganization, task?.helperWorkspace);
  const requesterId = actorProfileId(task?.requester);
  const helperId = actorProfileId(task?.helper);
  const requesterOrganizationId = organizationIdFrom(requesterOrganization, task?.requesterWorkspace);
  const helperOrganizationId = organizationIdFrom(helperOrganization, task?.helperWorkspace);
  const requesterWorkspaceId = firstText(task?.requesterWorkspaceId, workspaceIdOf(task?.requesterWorkspace));
  const helperWorkspaceId = firstText(task?.helperWorkspaceId, workspaceIdOf(task?.helperWorkspace));
  const canViewRequesterWorkspaceContact = Boolean(selectedWorkspaceId && requesterWorkspaceId && selectedWorkspaceId === requesterWorkspaceId);
  const canViewHelperWorkspaceContact = Boolean(selectedWorkspaceId && helperWorkspaceId && selectedWorkspaceId === helperWorkspaceId);
  const showChatLinks = shouldShowTaskChatLinks(task, userId);
  const showRequesterChat = showChatLinks && helperId && userId === helperId && requesterId;
  const showHelperChat = showChatLinks && requesterId && userId === requesterId && helperId;
  const linkRow = (label, value, payload) => ({
    label,
    value,
    action: onActorOpen && value ? () => onActorOpen(payload) : null,
    chatAction: payload?.chatPath && onChatOpen ? () => onChatOpen(payload.chatPath) : null,
    chatLabel: payload?.subtitle === "Care Giver" ? "Chat with Care Giver" : "Chat with requester",
  });
  return [
    {
      title: "Requester",
      hint: "Post owner",
      icon: <UserOutlined />,
      rows: detailRows([
        linkRow("Poster", personName(task?.requester), {
          title: personName(task?.requester) || "Requester",
          subtitle: "Requester",
          icon: <UserOutlined />,
          profilePath: requesterId ? paths.requesterProfileDetails(requesterId) : "",
          profileLabel: "View Requester Profile",
          profileRows: canViewRequesterWorkspaceContact
            ? actorProfileRows(task?.requester, "Requester")
            : withoutContactRows(actorProfileRows(task?.requester, "Requester")),
          bioRows: actorBioRows(task?.requester),
          chatPath: showRequesterChat
            ? taskChatPath({
                task,
                recipientId: requesterId,
                recipientName: personName(task?.requester),
                workspaceId: task?.helperWorkspaceId,
              })
            : "",
        }),
        linkRow("Organization", requesterOrganizationName, {
          title: requesterOrganizationName || "Organization",
          subtitle: "Requester organization",
          icon: <TeamOutlined />,
          organizationPath: requesterOrganizationId ? paths.publicOrganizationDetails(requesterOrganizationId) : "",
          organizationRows: organizationRows(requesterOrganization),
          contactRows: canViewRequesterWorkspaceContact ? organizationContactRows(requesterOrganization) : [],
          locationRows: canViewRequesterWorkspaceContact ? organizationLocationRows(requesterOrganization) : [],
        }),
      ]),
      chatPath: showRequesterChat
        ? taskChatPath({
            task,
            recipientId: requesterId,
            recipientName: personName(task?.requester),
            workspaceId: task?.helperWorkspaceId,
          })
        : "",
      chatLabel: "Chat with requester",
      onChatOpen,
    },
    {
      title: "Care Giver",
      hint: "Assigned provider",
      icon: <TeamOutlined />,
      rows: detailRows([
        linkRow("Person", personName(task?.helper), {
          title: personName(task?.helper) || "Care Giver",
          subtitle: "Care Giver",
          icon: <UserOutlined />,
          profilePath: helperId ? paths.helperProfileDetails(helperId) : "",
          profileLabel: "View Care Giver Profile",
          profileRows: canViewHelperWorkspaceContact
            ? actorProfileRows(task?.helper, "Care Giver")
            : withoutContactRows(actorProfileRows(task?.helper, "Care Giver")),
          bioRows: actorBioRows(task?.helper),
          chatPath: showHelperChat
            ? taskChatPath({
                task,
                recipientId: helperId,
                recipientName: personName(task?.helper),
                workspaceId: task?.requesterWorkspaceId,
              })
            : "",
        }),
        linkRow("Organization", helperOrganizationName, {
          title: helperOrganizationName || "Organization",
          subtitle: "Care Giver organization",
          icon: <TeamOutlined />,
          organizationPath: helperOrganizationId ? paths.publicOrganizationDetails(helperOrganizationId) : "",
          organizationRows: organizationRows(helperOrganization),
          contactRows: canViewHelperWorkspaceContact ? organizationContactRows(helperOrganization) : [],
          locationRows: canViewHelperWorkspaceContact ? organizationLocationRows(helperOrganization) : [],
        }),
      ]),
      chatPath: showHelperChat
        ? taskChatPath({
            task,
            recipientId: helperId,
            recipientName: personName(task?.helper),
            workspaceId: task?.requesterWorkspaceId,
          })
        : "",
      chatLabel: "Chat with Care Giver",
      onChatOpen,
    },
  ].filter((group) => group.rows.length);
};

const taskStatsMoney = (amount, currency = "USD") => {
  const numeric = Number(amount);
  return Number.isFinite(numeric) ? `${currency} ${numeric.toFixed(2)}` : "";
};

const taskChargeLineTotal = (charges, category) => {
  const normalizedCategory = normalizeStatus(category);
  const lines = Array.isArray(charges?.chargeLines) ? charges.chargeLines : [];
  return lines
    .filter((line) => normalizeStatus(line?.category) === normalizedCategory)
    .reduce((sum, line) => sum + Number(line?.amount || 0), 0);
};

const taskStatsPayment = (task, { isRequesterView = false } = {}) => {
  const care = careDetails(task);
  const paymentDetails = care?.taskDetails || care?.paymentDetails || task?.paymentDetails || {};
  const charges = task?.taskCharges || {};
  const payHelperInApp = paymentDetails?.payHelperInApp !== false;
  const currency = firstText(
    paymentDetails?.outOfAppPaymentFlatFeeCurrency,
    paymentDetails?.currency,
    task?.currency,
    charges.currency,
    "USD",
  ).toUpperCase();
  const creditsApplied = Number(task?.appliedCreditsTotal ?? charges.appliedCreditsTotal ?? 0);
  const tokenAmount = Number(task?.tokenAmount ?? charges.tokenAmount ?? charges.amount ?? 0);
  const chargeLineFees = taskChargeLineTotal(charges, "FEE");
  const chargeLineTaxes = taskChargeLineTotal(charges, "TAX");
  const taxesFromLegacyFields =
    charges.federalTax != null || charges.stateTax != null
      ? Number(charges.federalTax || 0) + Number(charges.stateTax || 0)
      : undefined;
  const feesEstimate = Number(paymentDetails?.feesEstimate ?? charges.serviceFee ?? chargeLineFees ?? 0);
  const taxesEstimate = Number(paymentDetails?.taxesEstimate ?? taxesFromLegacyFields ?? chargeLineTaxes ?? 0);
  const chargeLineFeeTaxTotal = chargeLineFees + chargeLineTaxes;
  const feeAndTaxTotal = Number(
    paymentDetails?.feesAndTaxesEstimate ?? charges.feeAndTaxTotal ?? (chargeLineFeeTaxTotal || feesEstimate + taxesEstimate),
  );
  const helperPayout = Number(charges.helperPayout ?? tokenAmount + creditsApplied);
  const outOfAppFlatFee = Number(
    paymentDetails?.outOfAppPaymentFlatFee ??
      (payHelperInApp ? 0 : charges.chargeableAmount ?? charges.totalCharge ?? charges.totalCost ?? 0),
  );
  const requesterPaysEstimate = Number(
    paymentDetails?.requesterPaysEstimate ??
      charges.chargeableAmount ??
      charges.totalCharge ??
      charges.totalCost ??
      helperPayout + feeAndTaxTotal,
  );
  const useCredits = Boolean(task?.useCredits ?? charges.useCredits);
  const outOfPocket = useCredits ? Math.max(0, requesterPaysEstimate - creditsApplied) : requesterPaysEstimate;

  return detailRows([
    { label: "Care Giver Payment", value: payHelperInApp ? "Paid through app" : "Paid outside app" },
    { label: "Net Payout Amount/Base Charge", value: taskStatsMoney(helperPayout, currency) },
    !payHelperInApp ? { label: "Platform fee", value: taskStatsMoney(outOfAppFlatFee, currency) } : null,
    { label: "Estimated Fees & Taxes", value: taskStatsMoney(feeAndTaxTotal, currency) },
    isRequesterView ? { label: "Requester pays", value: taskStatsMoney(requesterPaysEstimate, currency), emphasis: true } : null,
    isRequesterView ? { label: "Out of Pocket", value: taskStatsMoney(outOfPocket, currency), tone: "red" } : null,
    isRequesterView && useCredits ? { label: "Credits Applied", value: taskStatsMoney(creditsApplied, currency) } : null,
  ].filter(Boolean));
};

const taskStatsTipRows = (task) => {
  const charges = task?.tipChargeDetails || task?.tipPaymentChargeDetails || {};
  const tipAmount = firstNumber(task?.tipAmount, task?.taskCharges?.tipAmount, charges.tipAmount);
  if (!tipAmount) return [];
  const currency = firstText(charges.currency, task?.taskCharges?.currency, task?.currency, "USD").toUpperCase();
  const feeAndTaxTotal = firstNumber(charges.feeAndTaxTotal, charges.serviceFee, 0);
  const totalCharge = firstNumber(charges.totalCharge, tipAmount + feeAndTaxTotal);
  return detailRows([
    { label: "Tip Amount", value: taskStatsMoney(tipAmount, currency), tone: "green" },
    { label: "Tip Fees & Taxes", value: taskStatsMoney(feeAndTaxTotal, currency) },
    { label: "Total Tip Payment", value: taskStatsMoney(totalCharge, currency), emphasis: true },
  ]);
};

const travoRows = (task) => {
  const data = travoDetails(task);
  const airport = data.airport || task?.airport || {};
  const bagsTotal =
    Number(data.smallBags || 0) + Number(data.mediumBags || 0) + Number(data.largeBags || 0);
  const flags = [
    data.fragile ? "Fragile items" : "",
    data.heavy ? "Heavy bags" : "",
    data.wheelchair ? "Wheelchair support" : "",
    data.escortToGate ? "Escort to gate" : "",
  ].filter(Boolean);

  return {
    airport: detailRows([
      { label: "Airport", value: firstText(airport.name, data.airportName, data.airportCode, task?.airportName) },
      { label: "Airport code", value: firstText(airport.code, data.airportCode) },
      { label: "City / State", value: [airport.city, airport.state].filter(Boolean).join(", ") },
      { label: "Flight number", value: data.flightNumber },
      { label: "Terminal", value: data.terminal },
      { label: "Gate", value: data.gate },
    ]),
    help: detailRows([
      { label: "Service type", value: data.serviceType ? codeLabel(data.serviceType, TRAVO_SERVICE_LABELS) : "" },
      { label: "Meeting point", value: data.meetingPoint?.code ? codeLabel(data.meetingPoint.code, TRAVO_MEETING_LABELS) : data.meetingPoint },
    ]),
    meeting: detailRows([
      { label: "How to meet the requester", value: data.meetingDetail },
      { label: "Extra notes", value: data.meetingNotes },
      { label: "Other details", value: data.meetingOtherDetail },
    ]),
    handling: detailRows([
      { label: "Bag count (Small / Medium / Large)", value: bagsTotal ? `${bagsTotal} total (${data.smallBags || 0}/${data.mediumBags || 0}/${data.largeBags || 0})` : "" },
      { label: "Language required", value: data.languageRequired ? firstText(data.language, "Yes") : "" },
      { label: "Warnings", value: flags.join(", ") },
    ]),
  };
};

const careRows = (task) => {
  const data = careDetails(task);
  return detailRows([
    { label: "Care type", value: firstText(data.careType, data.serviceType, data.type) },
    { label: "Care recipient", value: firstText(data.recipientName, data.patientName) },
    { label: "Schedule", value: firstText(data.scheduleType, data.frequency) },
    { label: "Hourly rate", value: firstNumber(data.hourlyRate, data.payRate) ? `$${firstNumber(data.hourlyRate, data.payRate).toFixed(2)}/hr` : "" },
    { label: "Special needs", value: firstText(data.specialNeeds, data.notes) },
    { label: "Mobility support", value: data.mobilitySupport },
    { label: "Medication reminders", value: data.medicationReminder || data.medicationReminders },
  ]);
};

const statusClass = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === "NEW") return "new";
  if (["ACCEPTED", "ARRIVED", "NAVIGATING"].includes(normalized)) return "accepted";
  if (["IN_PROGRESS", "IN_REVIEW"].includes(normalized)) return "progress";
  if (normalized === "COMPLETED") return "completed";
  if (["CANCELLED", "EXPIRED", "DELETED"].includes(normalized)) return "closed";
  return "default";
};

const shouldShowForFilter = (task, taskFilter) => {
  const status = normalizeStatus(task?.status);
  if (taskFilter === "ALL") return true;
  if (taskFilter === "ACTIVE") return ACTIVE_REQUESTER_STATUSES.has(status);
  return status === taskFilter;
};

const dedupeTasks = (items = []) => {
  const seen = new Set();
  return items.filter((item, index) => {
    const id = taskKey(item, index);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const taskStagesFor = (task) => {
  const status = normalizeStatus(task?.status);
  const cancelStatus = normalizeStatus(task?.cancel?.cancelTaskStatus);
  if (status === "EXPIRED") return ["NEW", "EXPIRED", ...BASE_TASK_STAGES.slice(1)];
  if (status === "CANCELLED" && cancelStatus) {
    const index = BASE_TASK_STAGES.indexOf(cancelStatus);
    if (index >= 0) {
      return [
        ...BASE_TASK_STAGES.slice(0, index + 1),
        "CANCELLED",
        ...BASE_TASK_STAGES.slice(index + 1),
      ];
    }
    return [...BASE_TASK_STAGES, "CANCELLED"];
  }
  return BASE_TASK_STAGES;
};

const taskStageIndex = (task, stages) => {
  const status = normalizeStatus(task?.status);
  if (status === "CANCELLED") return stages.indexOf("CANCELLED");
  if (status === "EXPIRED") return stages.indexOf("EXPIRED");
  let index = stages.indexOf(status);
  if (["IN_PROGRESS", "COMPLETED"].includes(status) && stages.includes("ARRIVED")) {
    index = Math.max(index, stages.indexOf("IN_PROGRESS"));
  }
  return Math.max(0, index);
};

function TaskStatusProgress({ task, status, cancelStatus }) {
  const progressTask = task || { status, cancel: { cancelTaskStatus: cancelStatus } };
  const stages = taskStagesFor(progressTask);
  const currentIndex = taskStageIndex(progressTask, stages);

  return (
    <div
      className="my-requests-status-progress"
      style={{
        "--task-stage-count": stages.length,
        "--task-line-count": Math.max(1, stages.length - 1),
      }}
    >
      <div className="my-requests-status-progress__line">
        {stages.slice(0, -1).map((stage, index) => (
          <span
            key={`${stage}-${index}`}
            className={index < currentIndex ? "is-active" : ""}
          />
        ))}
      </div>
      <div className="my-requests-status-progress__steps">
        {stages.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isTerminal = stage === "CANCELLED" || stage === "EXPIRED";
          return (
            <div
              className={[
                "my-requests-status-progress__step",
                index > 0 && index <= currentIndex ? "is-line-before-active" : "",
                index < currentIndex ? "is-line-after-active" : "",
              ].filter(Boolean).join(" ")}
              key={`${stage}-${index}`}
            >
              <span
                className={[
                  "my-requests-status-progress__circle",
                  isCompleted ? "is-completed" : "",
                  isCurrent ? "is-current" : "",
                  isTerminal ? `is-${stage.toLowerCase()}` : "",
                ].filter(Boolean).join(" ")}
              >
                {isCompleted || isCurrent ? (isTerminal ? "x" : "✓") : ""}
              </span>
              <strong>{TASK_STAGE_LABELS[stage] || titleCase(stage)}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const actorMatchesUser = (actor, userId) => {
  if (!actor || !userId) return false;
  return [
    actor.id,
    actor.userId,
    actor.profileId,
    actor.requesterId,
    actor.helperId,
  ].some((value) => value && String(value) === String(userId));
};

const taskHasUserInterest = (task, userId) => {
  if (!task || !userId) return false;
  const interestLists = [
    task.interestedHelpers,
    task.helperInterests,
    task.interests,
    task.taskInterests,
    task.helpersInterested,
  ].filter(Array.isArray);
  return interestLists.some((list) =>
    list.some((item) => {
      const interestStatus = normalizeStatus(item?.status || item?.interestStatus || item?.interestedPartyStatus);
      const isInactive = ["WITHDRAWN", "CANCELLED", "CANCELED", "DECLINED", "REJECTED", "INACTIVE"].includes(interestStatus);
      return !isInactive && actorMatchesUser(item?.helper || item?.user || item?.profile || item, userId);
    }),
  );
};

const helperInterestStatus = (entry) =>
  normalizeStatus(
    entry?.status ||
      entry?.interestStatus ||
      entry?.interestedPartyStatus ||
      entry?.helperInterestStatus ||
      entry?.requestStatus,
  );

const isWithdrawnHelperInterest = (entry) =>
  ["WITHDRAWN", "CANCELLED", "CANCELED", "DECLINED", "REJECTED", "INACTIVE"].includes(helperInterestStatus(entry));

const isActiveOrAcceptedHelperInterest = (entry) => {
  const status = helperInterestStatus(entry);
  if (!status) return true;
  if (isWithdrawnHelperInterest(entry)) return false;
  return [
    "ACTIVE",
    "ACCEPTED",
    "INTERESTED",
    "PENDING",
    "SUBMITTED",
    "NEW",
    "STILL_INTERESTED",
    "AFFIRMED",
    "HELPER_STILL_INTERESTED",
  ].includes(status);
};

const taskHasUserCounterOffer = (task, userId) =>
  Boolean(
    task?.counterOffers?.some((offer) =>
      actorMatchesUser(offer?.helper || offer?.user || offer, userId),
    ),
  );

const workspaceIdOf = (workspace) => firstText(workspace?.id, workspace?.workspaceId);

const organizationIdOf = (entity) =>
  firstText(entity?.id, entity?.organizationId, entity?.organization?.id, entity?.entity?.id);

const helperWorkspaceIdFrom = (entry, offer) =>
  firstText(
    offer?.helperWorkspaceId,
    offer?.workspaceId,
    workspaceIdOf(offer?.helperWorkspace),
    entry?.helperWorkspaceId,
    entry?.workspaceId,
    workspaceIdOf(entry?.helperWorkspace),
  );

const helperOrganizationIdFrom = (entry, offer) =>
  firstText(
    offer?.helperOrganizationId,
    offer?.organizationId,
    organizationIdOf(offer?.helperOrganization),
    organizationIdOf(offer?.helperWorkspace?.organization),
    entry?.helperOrganizationId,
    entry?.organizationId,
    organizationIdOf(entry?.helperOrganization),
    organizationIdOf(entry?.helperWorkspace?.organization),
  );

const latestCounterOfferForHelper = (task, helperId, helperWorkspaceId) =>
  [...(Array.isArray(task?.counterOffers) ? task.counterOffers : [])]
    .filter((offer) => {
      if (!actorMatchesUser(offer?.helper || offer?.user || offer, helperId)) return false;
      if (!helperWorkspaceId) return true;
      return helperWorkspaceIdFrom({}, offer) === helperWorkspaceId;
    })
    .sort((a, b) => {
      const aTime = Date.parse(a?.submittedAt || a?.createdAt || "");
      const bTime = Date.parse(b?.submittedAt || b?.createdAt || "");
      return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
    })[0] || null;

const canAcceptHelperWithOfferState = (offer) => {
  if (!offer) return true;
  const status = normalizeStatus(offer.status);
  const responseStatus = normalizeStatus(offer.responseStatus);
  const helperAffirmedInterest =
    offer.isStillInterested === true ||
    ["STILL_INTERESTED", "AFFIRMED", "HELPER_STILL_INTERESTED"].includes(status) ||
    ["STILL_INTERESTED", "AFFIRMED", "HELPER_STILL_INTERESTED"].includes(responseStatus);
  return status === "ACCEPTED" || responseStatus === "ACCEPTED" || helperAffirmedInterest;
};

const counterOfferDisplayState = (offer) => {
  const status = normalizeStatus(offer?.status);
  const responseStatus = normalizeStatus(offer?.responseStatus);
  if (status === "DECLINED" || responseStatus === "DECLINED") {
    return { label: "Declined", tone: "declined", disabled: false };
  }
  if (status === "ACCEPTED" || responseStatus === "ACCEPTED") {
    return { label: "Accepted", tone: "accepted", disabled: true };
  }
  if (responseStatus === "COUNTERED") return { label: "Final", tone: "pending", disabled: false };
  return { label: "View Offer", tone: "pending", disabled: false };
};

const sharedProfessionProfileOf = (...sources) => {
  for (const source of sources) {
    if (!source) continue;
    const profile =
      source.helperProfessionProfile ||
      source.professionProfile ||
      source.sharedProfessionProfile ||
      source.helperProfileProfession ||
      null;
    const profileId = firstText(
      source.helperProfessionProfileId,
      source.professionProfileId,
      source.sharedProfessionProfileId,
      source.sharedHelperProfessionProfileId,
      profile?.id,
    );
    if (profileId || profile) return { profileId, profile };
  }
  return { profileId: "", profile: null };
};

const profileString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const titleCaseProfileValue = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const sharedProfileDisplay = (profile, profileId) => {
  const name = profileString(
    profile?.profileName,
    profile?.name,
    profile?.title,
    profile?.displayName,
    profile?.label,
    profile?.professionProfileName,
    profile?.professionalProfileName,
    profile?.profile?.profileName,
    profile?.profile?.name,
  );
  const sector = profileString(
    profile?.sectorName,
    profile?.professionSectorName,
    profile?.sector?.name,
    profile?.professionSector?.name,
    titleCaseProfileValue(profile?.sector || profile?.sectorCode || profile?.professionSectorCode),
  );
  const summary = profileString(profile?.profileSummary, profile?.summary, profile?.description);
  return {
    name: name || (profileId ? "Shared professional profile" : ""),
    sector,
    summary,
  };
};

const sharedBackgroundCheckOf = (...sources) => {
  for (const source of sources) {
    if (!source) continue;
    const backgroundCheck = source.helperBackgroundCheck || source.backgroundCheck || source.sharedBackgroundCheck || null;
    const backgroundCheckId = firstText(
      source.helperBackgroundCheckId,
      source.backgroundCheckId,
      source.sharedBackgroundCheckId,
      backgroundCheck?.id,
      backgroundCheck?.backgroundCheckId,
      backgroundCheck?.helperBackgroundCheckId,
    );
    if (backgroundCheckId || backgroundCheck) return { backgroundCheckId, backgroundCheck };
  }
  return { backgroundCheckId: "", backgroundCheck: null };
};

const sharedBackgroundCheckLabel = (backgroundCheck, backgroundCheckId) => {
  if (!backgroundCheck && !backgroundCheckId) return "";
  const label = profileString(
    backgroundCheck?.title,
    backgroundCheck?.label,
    backgroundCheck?.name,
    backgroundCheck?.type && `${titleCaseProfileValue(backgroundCheck.type)} Check`,
    backgroundCheck?.checkType && `${titleCaseProfileValue(backgroundCheck.checkType)} Check`,
  );
  const status = profileString(
    backgroundCheck?.status,
    backgroundCheck?.decision,
    backgroundCheck?.verificationStatus,
    backgroundCheck?.result,
  );
  return [label || "Background Check", status ? titleCaseProfileValue(status) : ""].filter(Boolean).join(" • ");
};

const helperInterestDisplayName = (entry, offer) => {
  const helper = entry?.helper || entry?.user || entry?.profile || entry;
  const organization = offer?.helperOrganization || entry?.helperOrganization || entry?.helperWorkspace?.organization;
  const organizationNameValue = organizationName(organization, offer?.helperWorkspace || entry?.helperWorkspace);
  const personNameValue = personName(helper);
  if (organizationNameValue) {
    return personNameValue ? `${organizationNameValue} • ${personNameValue}` : organizationNameValue;
  }
  return personNameValue;
};

const canResubmitTask = (task, isTaskRequester) => {
  const status = normalizeStatus(task?.status);
  if (!isTaskRequester || !["EXPIRED", "CANCELLED"].includes(status)) return false;
  const value = task?.updatedAtUtc || task?.createdAtUtc || task?.updatedAt || task?.createdAt;
  if (!value) return false;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return false;
  return (Date.now() - time) / (1000 * 60 * 60) <= 24;
};

function TaskActionSection({
  task,
  isHelperView,
  isTaskHelper,
  isTaskRequester,
  userId,
  selectedWorkspace,
  selectedWorkspaceId,
  onTaskUpdated,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [processingKey, setProcessingKey] = useState("");
  const [counterOfferOpen, setCounterOfferOpen] = useState(false);
  const [counterAmount, setCounterAmount] = useState("");
  const [counterDurationHours, setCounterDurationHours] = useState("");
  const [counterDurationMinutes, setCounterDurationMinutes] = useState("");
  const [counterReason, setCounterReason] = useState("");
  const [selectedCounterReasons, setSelectedCounterReasons] = useState([]);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [interestCounterVisible, setInterestCounterVisible] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const taskId = firstText(task?.id, task?.taskId);
  const status = normalizeStatus(task?.status);
  const taskHelperWorkspaceId = firstText(task?.helperWorkspaceId, workspaceIdOf(task?.helperWorkspace));
  const isTaskHelperWorkspace = Boolean(selectedWorkspaceId && taskHelperWorkspaceId && selectedWorkspaceId === taskHelperWorkspaceId);
  const helperAccepted = isTaskHelper || isTaskHelperWorkspace || actorMatchesUser(task?.helper, userId);
  const userAlreadyInterested = taskHasUserInterest(task, userId) || helperAccepted;
  const submittedCounterOffer = taskHasUserCounterOffer(task, userId);
  const noun = isGeneralTask(task) ? "task" : "shift";
  const titleNoun = isGeneralTask(task) ? "Task" : "Shift";
  const baseTaskPath = isGeneralTask(task) || location.pathname.startsWith("/hire-care-giver/general-tasks")
    ? "/hire-care-giver/general-tasks/posts"
    : "/hire-care-giver/shift-filler/posts";
  const taskPath = (suffix = "") => `${baseTaskPath}/${taskId}${suffix ? `/${suffix}` : ""}`;
  const canViewTaskProgress = (helperAccepted || isTaskRequester) && status === "IN_PROGRESS";
  const canStartNavigation = isHelperView && helperAccepted && status === "ACCEPTED";
  const canGoToNavigation = isHelperView && helperAccepted && status === "NAVIGATING";
  const canTrackHelperOnWay = isTaskRequester && status === "NAVIGATING";
  const canWithdrawInterest = isHelperView && userAlreadyInterested && !helperAccepted && status === "NEW";
  const canExpressInterest = isHelperView && !userAlreadyInterested && status === "NEW" && !isTaskRequester;
  const canCancelTask =
    (isTaskHelper || isTaskRequester) &&
    !["COMPLETED", "CANCELLED", "EXPIRED"].includes(status) &&
    (status !== "NEW" || isTaskRequester);
  const canShowResubmitButton = canResubmitTask(task, isTaskRequester);
  const canShowTipPrompt =
    isTaskRequester &&
    status === "COMPLETED" &&
    !task?.taskCharges?.tipAmount &&
    String(task?.giveTip || "").toUpperCase() === "UNDECIDED";
  const helperActionContext = selectedHelperContext({ selectedWorkspace, selectedWorkspaceId });
  const counterConfig = counterOfferConfig(task);

  const hasVisibleActions =
    canViewTaskProgress ||
    canStartNavigation ||
    canGoToNavigation ||
    canTrackHelperOnWay ||
    canWithdrawInterest ||
    canExpressInterest ||
    canShowResubmitButton ||
    canCancelTask ||
    canShowTipPrompt;

  if (!hasVisibleActions) return null;

  const runTaskAction = async (key, label, request) => {
    if (!taskId) return;
    try {
      setProcessingKey(key);
      await request();
      message.success(label);
      await onTaskUpdated?.();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Unable to complete this action.");
    } finally {
      setProcessingKey("");
    }
  };

  const openCounterOffer = () => {
    if (!selectedWorkspaceId) {
      message.warning("Please select a Care Giver workspace before sending a counter offer.");
      return;
    }
    if (submittedCounterOffer) {
      message.warning(`You can only submit one counter offer for this ${noun}.`);
      return;
    }
    const originalMinutes = counterConfig.currentDurationMinutes;
    setCounterAmount(counterConfig.amount > 0 ? String(counterConfig.amount) : "");
    setCounterDurationHours(String(Math.floor(originalMinutes / 60) || 0));
    setCounterDurationMinutes(String(originalMinutes % 60 || 0));
    setCounterReason("");
    setSelectedCounterReasons([]);
    if (interestDialogOpen) {
      setInterestCounterVisible(true);
      return;
    }
    setCounterOfferOpen(true);
  };

  const toggleCounterReason = (reason) => {
    setSelectedCounterReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason],
    );
  };

  const submitCounterOffer = () => {
    const originalAmount = Number(counterConfig.amount || 0);
    const parsedAmount = counterAmount.trim().length ? Number(counterAmount) : originalAmount;
    const originalMinutes = counterConfig.currentDurationMinutes;
    const hours = counterConfig.allowDurationCounter
      ? parseInt(counterDurationHours || "0", 10)
      : Math.floor(originalMinutes / 60);
    const minutes = counterConfig.allowDurationCounter
      ? parseInt(counterDurationMinutes || "0", 10)
      : originalMinutes % 60;
    const totalMinutes = hours * 60 + minutes;
    const typedReason = counterReason.trim();
    const quickReasons = selectedCounterReasons.filter((reason) => reason !== "Other");
    const reason = [...quickReasons, typedReason].filter(Boolean).join("; ");
    const amountChanged = Math.abs(parsedAmount - originalAmount) >= 0.005;
    const timeChanged = counterConfig.allowDurationCounter && totalMinutes !== originalMinutes;

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      message.warning("Please enter a valid counter offer amount.");
      return;
    }
    if (counterConfig.allowDurationCounter && (!Number.isFinite(totalMinutes) || totalMinutes <= 0)) {
      message.warning("Please enter the time needed for your counter offer.");
      return;
    }
    if (!amountChanged && !timeChanged) {
      message.warning(
        counterConfig.allowDurationCounter
          ? "Update the amount, the time, or both before sending a counter offer."
          : "Update the amount before sending a counter offer.",
      );
      return;
    }
    if (selectedCounterReasons.includes("Other") && !typedReason) {
      message.warning("Please enter a short reason for your counter offer.");
      return;
    }

    const payload = {
      amount: parsedAmount,
      ...(counterConfig.counterPayRateFromAmount ? { payRate: parsedAmount } : {}),
      ...(counterConfig.counterPayType ? { payType: counterConfig.counterPayType } : {}),
      duration: `${totalMinutes} minutes`,
      durationMinutes: totalMinutes,
      reason,
      ...(helperActionContext.body || {}),
    };

    runTaskAction("counter-offer", "Your counter offer has been submitted.", async () => {
      await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/counter-offer`, payload, {
        headers: taskActionHeaders(userId),
      });
      setCounterOfferOpen(false);
      setInterestDialogOpen(false);
      setInterestCounterVisible(false);
    });
  };

  const submitInterest = () => {
    setInterestDialogOpen(false);
    setInterestCounterVisible(false);
    runTaskAction("interest", `Your interest in helping on this ${noun} has been submitted.`, () =>
      apiClient.put(
        `/olitask-service/api/v1/tasks/${taskId}/express-interest${
          helperActionContext.query ? `?${helperActionContext.query}` : ""
        }`,
        helperActionContext.body,
        { headers: taskActionHeaders(userId) },
      ),
    );
  };

  const submitWithdrawInterest = () => {
    setWithdrawDialogOpen(false);
    runTaskAction("withdraw", "You have withdrawn your interest.", () =>
      withdrawInterestRequest(taskId, selectedWorkspaceId),
    );
  };

  return (
    <section className="my-requests-action-section">
      <div className="my-requests-action-section__head">
        <span><RocketOutlined /></span>
        <div>
          <strong>{titleNoun} Post Actions</strong>
          <small>Available actions for this {noun} stage</small>
        </div>
      </div>

      <div className="my-requests-action-grid">
        {canViewTaskProgress ? (
          <Button
            className="my-requests-action-button is-outline"
            icon={<EyeOutlined />}
            onClick={() => navigate(taskPath("progress"))}
          >
            View {titleNoun} Progress
          </Button>
        ) : null}

        {canStartNavigation ? (
          <Button
            className="my-requests-action-button is-primary"
            type="primary"
            icon={<CompassOutlined />}
            loading={processingKey === "navigate"}
            onClick={() =>
              runTaskAction("navigate", "Navigation started.", async () => {
                const location = await currentLocationPing(userId, taskCoordinates(task), { requirePermission: true });
                if (!location) throw new Error("Location permission is required to start navigation.");
                await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/navigate`, location, {
                  headers: {},
                });
                navigate(taskPath("heading"));
              })
            }
          >
            Start Navigation
          </Button>
        ) : null}

        {canGoToNavigation ? (
          <Button
            className="my-requests-action-button is-primary"
            type="primary"
            icon={<CompassOutlined />}
            onClick={() => navigate(taskPath("heading"))}
          >
            Go To Navigation
          </Button>
        ) : null}

        {canTrackHelperOnWay ? (
          <Button
            className="my-requests-action-button is-primary"
            type="primary"
            icon={<CompassOutlined />}
            onClick={() => navigate(taskPath("navigation"))}
          >
            Care Giver On The Way
          </Button>
        ) : null}

        {canWithdrawInterest ? (
          <Button
            className="my-requests-action-button is-danger"
            loading={processingKey === "withdraw"}
            onClick={() => setWithdrawDialogOpen(true)}
          >
            Withdraw Interest
          </Button>
        ) : null}

        {canExpressInterest ? (
          <>
            <Button
              className="my-requests-action-button is-primary"
              type="primary"
              icon={<UserOutlined />}
              loading={processingKey === "interest"}
              onClick={() => {
                setInterestCounterVisible(false);
                setInterestDialogOpen(true);
              }}
            >
              I'd Like to Help
            </Button>
            <Button
              className="my-requests-action-button is-counter"
              disabled={submittedCounterOffer}
              loading={processingKey === "counter-offer"}
              onClick={openCounterOffer}
            >
              {submittedCounterOffer ? "Counter Offer Sent" : "Counter Offer"}
            </Button>
          </>
        ) : null}

        {canShowResubmitButton ? (
          <Button
            className="my-requests-action-button is-outline"
            icon={<ReloadOutlined />}
            loading={processingKey === "resubmit"}
            onClick={() =>
              runTaskAction("resubmit", `${titleNoun} has been re-submitted and is now NEW.`, () =>
                apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/resubmit`, undefined, {
                  headers: taskActionHeaders(userId, { actor: false, xActor: true }),
                }),
              )
            }
          >
            Re-Submit {titleNoun}
          </Button>
        ) : null}

        {canCancelTask ? (
          <Button
            className="my-requests-action-button is-danger"
            onClick={() => navigate(taskPath("cancel"))}
          >
            Cancel {titleNoun} Post
          </Button>
        ) : null}
      </div>

      {canShowTipPrompt ? (
        <div className="my-requests-tip-prompt">
          <strong>Tip the Care Giver?</strong>
          <div>
            <button type="button" onClick={() => navigate(paths.taskCompleted(taskId))}>
              Yes
            </button>
            <button
              type="button"
              disabled={processingKey === "tip-decline"}
              onClick={() =>
                runTaskAction("tip-decline", "Tip skipped for now.", () =>
                  apiClient.post(`/olitask-service/api/v1/tasks/${taskId}/tip/decline`, undefined, {
                    headers: taskActionHeaders(userId, { actor: false, xActor: true }),
                  }),
                )
              }
            >
              Not Now
            </button>
          </div>
        </div>
      ) : null}

      <Modal
        title={interestCounterVisible ? "Counter Offer" : "Express Interest"}
        open={interestDialogOpen}
        onCancel={() => {
          if (!processingKey) {
            setInterestDialogOpen(false);
            setInterestCounterVisible(false);
          }
        }}
        footer={null}
        centered
        className="my-requests-counter-modal my-requests-confirm-modal my-requests-interest-modal"
        destroyOnClose
      >
        <div className="my-requests-interest-modal__sheen" aria-hidden="true" />
        {interestCounterVisible ? (
          <>
            <p className="my-requests-confirm-modal__message">
              {counterConfig.message}
            </p>

            {counterConfig.allowDurationCounter ? (
              <div className="my-requests-counter-field">
                <label>How long will it take?</label>
                <div className="my-requests-counter-duration">
                  <div>
                    <input
                      inputMode="numeric"
                      placeholder="0"
                      value={counterDurationHours}
                      onChange={(event) => setCounterDurationHours(event.target.value.replace(/[^0-9]/g, ""))}
                      disabled={Boolean(processingKey)}
                    />
                    <span>hr</span>
                  </div>
                  <div>
                    <input
                      inputMode="numeric"
                      placeholder="0"
                      value={counterDurationMinutes}
                      onChange={(event) => setCounterDurationMinutes(event.target.value.replace(/[^0-9]/g, ""))}
                      disabled={Boolean(processingKey)}
                    />
                    <span>min</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="my-requests-counter-locked">
                <strong>Scheduled time</strong>
                <span>This Home Care & Assisted Living request time cannot be counter offered.</span>
              </div>
            )}

            <div className="my-requests-counter-field">
              <label>{counterConfig.amountLabel}</label>
              <div className="my-requests-counter-amount">
                <input
                  inputMode="decimal"
                  placeholder="Enter Amount"
                  value={counterAmount}
                  onChange={(event) => setCounterAmount(event.target.value.replace(/[^0-9.]/g, ""))}
                  disabled={Boolean(processingKey)}
                />
                <span>{counterConfig.amountUnit}</span>
              </div>
            </div>

            <div className="my-requests-counter-field">
              <label>Reason</label>
              <div className="my-requests-counter-reasons">
                {COUNTER_OFFER_REASONS.map((reason) => (
                  <button
                    type="button"
                    key={reason}
                    className={selectedCounterReasons.includes(reason) ? "is-selected" : ""}
                    onClick={() => toggleCounterReason(reason)}
                    disabled={Boolean(processingKey)}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              {selectedCounterReasons.includes("Other") ? (
                <textarea
                  placeholder="Tell the requester why you are proposing this offer"
                  value={counterReason}
                  onChange={(event) => setCounterReason(event.target.value)}
                  disabled={Boolean(processingKey)}
                />
              ) : null}
            </div>

            <div className="my-requests-confirm-actions">
              <Button
                className="my-requests-confirm-button is-primary"
                loading={processingKey === "counter-offer"}
                disabled={Boolean(processingKey)}
                onClick={submitCounterOffer}
              >
                {processingKey === "counter-offer" ? "Submitting..." : "Submit Counter Offer"}
              </Button>
              <Button
                className="my-requests-confirm-button is-details"
                disabled={Boolean(processingKey)}
                onClick={() => setInterestCounterVisible(false)}
              >
                Back
              </Button>
              <Button
                className="my-requests-confirm-button is-cancel"
                disabled={Boolean(processingKey)}
                onClick={() => {
                  setInterestDialogOpen(false);
                  setInterestCounterVisible(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="my-requests-confirm-modal__message">
              Please review the {noun} details first to make sure you are comfortable with the request before expressing interest.
              Do you still want to continue?
            </p>
            <div className="my-requests-confirm-actions">
              <Button
                className="my-requests-confirm-button is-primary"
                loading={processingKey === "interest"}
                disabled={Boolean(processingKey)}
                onClick={submitInterest}
              >
                {processingKey === "interest" ? "Submitting..." : "Confirm Interest"}
              </Button>
              <Button
                className="my-requests-confirm-button is-counter"
                disabled={Boolean(processingKey) || submittedCounterOffer}
                onClick={openCounterOffer}
              >
                {submittedCounterOffer ? "Counter Offer Sent" : "Counter Offer"}
              </Button>
              <Button
                className="my-requests-confirm-button is-details"
                disabled={Boolean(processingKey)}
                onClick={() => {
                  setInterestDialogOpen(false);
                  setInterestCounterVisible(false);
                  navigate(taskPath());
                }}
              >
                Check {titleNoun} Details
              </Button>
              <Button
                className="my-requests-confirm-button is-cancel"
                disabled={Boolean(processingKey)}
                onClick={() => {
                  setInterestDialogOpen(false);
                  setInterestCounterVisible(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </Modal>

      <Modal
        title="Withdraw Interest"
        open={withdrawDialogOpen}
        onCancel={() => {
          if (!processingKey) setWithdrawDialogOpen(false);
        }}
        footer={null}
        centered
        className="my-requests-counter-modal my-requests-confirm-modal"
        destroyOnClose
      >
        <p className="my-requests-confirm-modal__message">Do you want to withdraw your interest from this {noun}?</p>
        <div className="my-requests-confirm-actions">
          <Button
            className="my-requests-confirm-button is-cancel"
            loading={processingKey === "withdraw"}
            disabled={Boolean(processingKey)}
            onClick={submitWithdrawInterest}
          >
            {processingKey === "withdraw" ? "Withdrawing..." : "Withdraw"}
          </Button>
          <Button
            className="my-requests-confirm-button is-details"
            disabled={Boolean(processingKey)}
            onClick={() => setWithdrawDialogOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal
        title="Counter Offer"
        open={counterOfferOpen}
        onCancel={() => {
          if (!processingKey) setCounterOfferOpen(false);
        }}
        footer={null}
        centered
        className="my-requests-counter-modal"
        destroyOnClose
      >
        <p className="my-requests-counter-modal__message">{counterConfig.message}</p>

        {counterConfig.allowDurationCounter ? (
          <div className="my-requests-counter-field">
            <label>How long will it take?</label>
            <div className="my-requests-counter-duration">
              <div>
                <input
                  inputMode="numeric"
                  value={counterDurationHours}
                  onChange={(event) => setCounterDurationHours(event.target.value.replace(/[^0-9]/g, ""))}
                  disabled={Boolean(processingKey)}
                />
                <span>hr</span>
              </div>
              <div>
                <input
                  inputMode="numeric"
                  value={counterDurationMinutes}
                  onChange={(event) => setCounterDurationMinutes(event.target.value.replace(/[^0-9]/g, ""))}
                  disabled={Boolean(processingKey)}
                />
                <span>min</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="my-requests-counter-locked">
            <strong>Scheduled time</strong>
            <span>This Home Care & Assisted Living request time cannot be counter offered.</span>
          </div>
        )}

        <div className="my-requests-counter-field">
          <label>{counterConfig.amountLabel}</label>
          <div className="my-requests-counter-amount">
            <input
              inputMode="decimal"
              placeholder="Enter Amount"
              value={counterAmount}
              onChange={(event) => setCounterAmount(event.target.value.replace(/[^0-9.]/g, ""))}
              disabled={Boolean(processingKey)}
            />
            <span>{counterConfig.amountUnit}</span>
          </div>
        </div>

        <div className="my-requests-counter-field">
          <label>Reason</label>
          <div className="my-requests-counter-reasons">
            {COUNTER_OFFER_REASONS.map((reason) => (
              <button
                type="button"
                key={reason}
                className={selectedCounterReasons.includes(reason) ? "is-selected" : ""}
                onClick={() => toggleCounterReason(reason)}
                disabled={Boolean(processingKey)}
              >
                {reason}
              </button>
            ))}
          </div>
          {selectedCounterReasons.includes("Other") ? (
            <textarea
              value={counterReason}
              onChange={(event) => setCounterReason(event.target.value)}
              placeholder="Tell the requester why you are proposing this offer"
              disabled={Boolean(processingKey)}
            />
          ) : null}
        </div>

        <div className="my-requests-counter-actions">
          <Button
            className="my-requests-action-button is-primary"
            type="primary"
            loading={processingKey === "counter-offer"}
            onClick={submitCounterOffer}
          >
            Submit Counter Offer
          </Button>
          <Button
            className="my-requests-action-button is-outline"
            disabled={Boolean(processingKey)}
            onClick={() => setCounterOfferOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </section>
  );
}

function RequestCard({ task, onDetails }) {
  const image = photoUrl(task);
  const status = normalizeStatus(task?.status);
  const offer = moneyText(task);
  const duration = durationText(task);
  const distance = nearbyDistanceText(task);
  const location = locationText(task);
  const posted = dateText(taskPostedAtValue(task));
  const updated = dateText(taskUpdatedAtValue(task));
  const meta = [
    posted ? `Posted At ${posted}` : "",
    updated ? `Last Updated ${updated}` : "",
    location,
  ].filter(Boolean).join(" • ");

  return (
    <article className="olitask-request-card">
      <div className="olitask-request-card__media">
        {image ? (
          <img src={image} alt="" />
        ) : (
          <div className="olitask-request-card__placeholder">
            <CheckSquareOutlined />
          </div>
        )}
        <span className={`olitask-request-card__status is-${statusClass(status)}`}>
          {titleCase(status)}
        </span>
      </div>
      <div className="olitask-request-card__body">
        <strong className="olitask-request-card__title">{taskTitle(task)}</strong>
        <p className="olitask-request-card__description">{taskDescription(task)}</p>
        <div className="olitask-request-card__offer">
          Offered: {offer || "Pending"}
          {duration ? <span>{`for ${duration}`}</span> : null}
        </div>
        <div className="olitask-request-card__meta">
          {meta || "Request activity will appear here"}
        </div>
        <button className="olitask-request-card__details" type="button" onClick={() => onDetails(task)}>
          <EyeOutlined /> See Details
        </button>
      </div>
    </article>
  );
}

function NearbyRequestCard({ task, onDetails, onExpressInterest, onWithdrawInterest, interested }) {
  const image = photoUrl(task);
  const status = normalizeStatus(task?.status);
  const offer = moneyText(task);
  const duration = durationText(task);
  const distance = nearbyDistanceText(task);
  const location = locationText(task);
  const posted = dateText(taskPostedAtValue(task));
  const updated = dateText(taskUpdatedAtValue(task));
  const activityLine = [
    posted ? `Posted At ${posted}` : "",
    updated ? `Last Updated ${updated}` : "",
    location,
  ].filter(Boolean).join(" • ");

  return (
    <article className="nearby-request-card">
      <div className="nearby-request-card__media">
        {image ? <img src={image} alt="" /> : <CheckSquareOutlined />}
        <span className={`olitask-request-card__status is-${statusClass(status)}`}>{titleCase(status)}</span>
      </div>
      <div className="nearby-request-card__body">
        <strong>{taskTitle(task)}</strong>
        <p>{taskDescription(task)}</p>
        {distance ? <div className="nearby-request-card__distance">{distance}</div> : null}
        <div className="nearby-request-card__offer">
          Offered: {offer || "Pending"}{duration ? ` for ${duration}` : ""}
        </div>
        {activityLine ? <div className="nearby-request-card__meta">{activityLine}</div> : null}
        <div className="nearby-request-card__actions">
          <Button className="my-requests-dock-button" onClick={() => onDetails(task)}>
            👀 See Details
          </Button>
          {status === "NEW" ? (
            interested ? (
              <Button className="my-requests-action-button is-danger" onClick={() => onWithdrawInterest(task)}>
                Withdraw Interest
              </Button>
            ) : (
              <Button className="my-requests-primary" type="primary" onClick={() => onExpressInterest(task)}>
                🙋 I'd Like to Help
              </Button>
            )
          ) : null}
        </div>
      </div>
    </article>
  );
}

function NearbyRequestsMap({ tasks, center, radius, onDetails, mapsReady, locationLabel }) {
  const mapContainerRef = useRef(null);
  const mapContainerNodeRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const mappableTasks = tasks
    .map((task) => ({ task, coords: taskCoordinates(task) }))
    .filter((item) => item.coords);

  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current || !window.google?.maps || !center) return;

    const mapCenter = { lat: center.latitude, lng: center.longitude };
    const mapContainer = mapContainerRef.current;
    const shouldCreateMap = !mapRef.current || mapContainerNodeRef.current !== mapContainer;

    if (shouldCreateMap) {
      mapContainerNodeRef.current = mapContainer;
      mapRef.current = new window.google.maps.Map(mapContainer, {
        center: mapCenter,
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
      });
    } else {
      mapRef.current.setCenter(mapCenter);
    }

    window.requestAnimationFrame(() => {
      if (mapRef.current && window.google?.maps?.event) {
        window.google.maps.event.trigger(mapRef.current, "resize");
        mapRef.current.setCenter(mapCenter);
      }
    });

    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(mapCenter);

    const searchMarker = new window.google.maps.Marker({
      map: mapRef.current,
      position: mapCenter,
      title: locationLabel || "Search location",
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#27ae30",
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 3,
      },
    });
    overlaysRef.current.push(searchMarker);

    const radiusCircle = new window.google.maps.Circle({
      map: mapRef.current,
      center: mapCenter,
      radius: Number(radius) * 1609.344,
      strokeColor: "#27ae30",
      strokeOpacity: 0.72,
      strokeWeight: 2,
      fillColor: "#27ae30",
      fillOpacity: 0.11,
      clickable: false,
    });
    overlaysRef.current.push(radiusCircle);
    const radiusBounds = radiusCircle.getBounds?.();
    if (radiusBounds) bounds.union(radiusBounds);

    mappableTasks.forEach(({ task, coords }, index) => {
      const position = { lat: coords.latitude, lng: coords.longitude };
      bounds.extend(position);
      const markerSvg = encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="46" height="54" viewBox="0 0 46 54">
          <path d="M23 52C18 44 5 34 5 20.5C5 10.8 13.1 3 23 3s18 7.8 18 17.5C41 34 28 44 23 52Z" fill="#27ae30" stroke="#ffffff" stroke-width="4"/>
          <rect x="14" y="14" width="18" height="20" rx="4" fill="#ffffff"/>
          <path d="M18 21.5l3 3 7-7" fill="none" stroke="#27ae30" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18 29h10" stroke="#0e5604" stroke-width="2.4" stroke-linecap="round"/>
        </svg>
      `);
      const marker = new window.google.maps.Marker({
        map: mapRef.current,
        position,
        title: firstText(task.title, task.name, "Help Request"),
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${markerSvg}`,
          scaledSize: new window.google.maps.Size(46, 54),
          anchor: new window.google.maps.Point(23, 52),
        },
      });
      marker.addListener("click", () => onDetails(task));
      overlaysRef.current.push(marker);
      if (index === 0) bounds.extend(position);
    });

    if (mappableTasks.length > 0 || radiusBounds) {
      mapRef.current.fitBounds(bounds, 56);
      const minZoom =
        Number(radius) <= 5 ? 12 :
          Number(radius) <= 10 ? 11 :
            Number(radius) <= 20 ? 10 :
              9;
      window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
        const currentZoom = mapRef.current?.getZoom?.();
        if (Number.isFinite(currentZoom) && currentZoom < minZoom) {
          mapRef.current.setZoom(minZoom);
        }
      });
    }
  }, [center, locationLabel, mappableTasks, mapsReady, onDetails, radius]);

  return (
    <div className="nearby-map">
      <div ref={mapContainerRef} className="nearby-map-canvas" />
      {!mapsReady || !mappableTasks.length ? (
        <div className="nearby-map__empty">
          <strong>Nearby Help Requests</strong>
          <span>
            {!mapsReady
              ? "Map is loading. Location and requests will appear shortly."
              : "No mappable requests yet. The map still shows your search radius."}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function DetailSection({ title, icon, tone = "green", children }) {
  return (
    <section className={`my-requests-detail-section is-${tone}`}>
      <div className="my-requests-detail-section__header">
        <span>{icon}</span>
        <strong>{title}</strong>
      </div>
      {children}
    </section>
  );
}

function DetailRows({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="my-requests-detail-rows">
      {rows.map((row) => (
        <div
          className={[
            "my-requests-detail-row",
            row.emphasis ? "is-emphasis" : "",
            row.tone ? `is-${row.tone}` : "",
          ].filter(Boolean).join(" ")}
          key={row.label}
        >
          <span>{row.label}</span>
          <div className="my-requests-detail-row__value">
            {row.action ? (
              <button type="button" className="my-requests-detail-link" onClick={row.action}>
                {row.value}
              </button>
            ) : (
              <strong>{row.value}</strong>
            )}
            {row.chatAction ? (
              <button
                type="button"
                className="my-requests-actor-chat-icon"
                onClick={row.chatAction}
                aria-label={row.chatLabel || "Open chat"}
                title={row.chatLabel || "Open chat"}
              >
                <MessageOutlined />
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailPhotoStrip({ task, onPhotoPress }) {
  const photos = taskPhotos(task);
  if (!photos.length) return null;
  return (
    <DetailSection title="Photo Gallery" icon={<PictureOutlined />} tone="blue">
      <div className="my-requests-detail-photos">
        {photos.map((url, index) => (
          <button type="button" key={url} onClick={() => onPhotoPress(photos, index)}>
            <img src={url} alt="" />
          </button>
        ))}
      </div>
    </DetailSection>
  );
}

function CoreTasksSection({ task }) {
  const categories = taskCategories(task);
  if (!categories.length) return null;
  const noun = isGeneralTask(task) ? "Task" : "Shift";
  return (
    <DetailSection title={`Core ${noun} Needs`} icon={<CheckSquareOutlined />} tone="green">
      <div className="my-requests-core-chips">
        {categories.map((name) => (
          <span key={name}>
            <CheckSquareOutlined /> {name}
          </span>
        ))}
      </div>
    </DetailSection>
  );
}

const counterOfferStatus = (offer) => {
  const status = normalizeStatus(offer?.status);
  const responseStatus = normalizeStatus(offer?.responseStatus);
  const finalDeclined = responseStatus === "DECLINED";
  const offerDeclined = status === "DECLINED";
  const accepted = status === "ACCEPTED" || responseStatus === "ACCEPTED";
  const hasFinalOffer =
    responseStatus === "COUNTERED" ||
    offer?.responseAmount != null ||
    offer?.responseDurationMinutes != null ||
    offer?.responseReason;
  const stillInterested = Boolean(
    offer?.isStillInterested ||
      offer?.stillInterested ||
      String(offer?.helperStillInterested || "").toUpperCase() === "YES",
  );

  const badge = finalDeclined
    ? "OFFER DECLINED"
    : offerDeclined
      ? "DECLINED"
      : stillInterested
        ? "STILL INTERESTED"
        : accepted
          ? "ACCEPTED"
          : hasFinalOffer
            ? "FINAL OFFER"
            : firstText(offer?.responseStatus, offer?.status, "PENDING");

  const subtitle = finalDeclined
    ? "Care Giver declined the requester final offer."
    : offerDeclined
      ? "Requester declined this offer change request."
      : hasFinalOffer
        ? "Requester sent a final offer."
        : accepted
          ? "Requester accepted this offer change request."
          : "Waiting for requester response.";

  return { accepted, badge, declined: finalDeclined || offerDeclined, hasFinalOffer, stillInterested, subtitle };
};

const counterOffersForView = (task, { isHelperView, userId }) => {
  const offers = Array.isArray(task?.counterOffers) ? task.counterOffers : [];
  const visibleOffers = isHelperView && userId
    ? offers.filter((offer) => actorMatchesUser(offer?.helper || offer?.user || offer, userId))
    : offers;

  return [...visibleOffers].sort((a, b) => {
    const aTime = Date.parse(a?.submittedAt || a?.createdAt || a?.updatedAt || "");
    const bTime = Date.parse(b?.submittedAt || b?.createdAt || b?.updatedAt || "");
    return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
  });
};

function CounterOfferDetails({ task, isHelperView, userId, onTaskUpdated }) {
  const [processingOfferKey, setProcessingOfferKey] = useState("");
  const offers = counterOffersForView(task, { isHelperView, userId });
  if (!offers.length) return null;

  const currency = firstText(task?.taskCharges?.currency, task?.currency, "USD");
  const taskId = firstText(task?.id, task?.taskId);
  const runOfferAction = async (offer, action, label) => {
    if (!taskId || !offer?.id) return;
    try {
      setProcessingOfferKey(`${offer.id}:${action}`);
      await apiClient.put(
        `/olitask-service/api/v1/tasks/${taskId}/counter-offer/${offer.id}/${action}`,
        undefined,
        { headers: taskActionHeaders(userId) },
      );
      message.success(label);
      await onTaskUpdated?.();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Unable to update offer change request.");
    } finally {
      setProcessingOfferKey("");
    }
  };

  const confirmFinalOfferDecision = (offer, accept) => {
    const noun = isGeneralTask(task) ? "task" : "shift";
    Modal.confirm({
      title: accept ? "Accept final offer?" : "Decline final offer?",
      content: accept
        ? `This will accept the requester final offer for this ${noun}.`
        : "This will decline the requester final offer.",
      okText: accept ? "Accept" : "Decline",
      okButtonProps: { danger: !accept },
      onOk: () =>
        runOfferAction(
          offer,
          `response/${accept ? "accept" : "decline"}`,
          accept ? "Final offer accepted." : "Final offer declined.",
        ),
    });
  };

  return (
    <DetailSection title="Offer Change Requests" icon={<SwapOutlined />} tone="gold">
      <div className="my-requests-counter-offers">
        {offers.map((offer, index) => {
          const status = counterOfferStatus(offer);
          const payType = offer?.payType;
          const finalPayType = offer?.responsePayType || offer?.payType;
          const helperName = personName(offer?.helper || offer?.user || offer) || `Care Giver ${index + 1}`;
          const helperRows = detailRows([
            {
              label: offerAmountLabel(payType, isHelperView ? "Your" : "Care Giver"),
              value: formatOfferMoney(offer?.payRate ?? offer?.amount, currency, payType),
            },
            { label: isHelperView ? "Your time" : "Care Giver time", value: formatDurationLabel(offer?.durationMinutes) },
            { label: "Reason", value: offer?.reason },
          ]);
          const finalRows = detailRows([
            {
              label: offerAmountLabel(finalPayType, "Requester"),
              value: formatOfferMoney(offer?.responsePayRate ?? offer?.responseAmount, currency, finalPayType),
            },
            { label: "Time", value: formatDurationLabel(offer?.responseDurationMinutes) },
            { label: "Note", value: offer?.responseReason },
          ]);
          const canRespondToFinalOffer =
            isHelperView && status.hasFinalOffer && !status.declined && !status.accepted;
          const canAffirmStillInterested =
            isHelperView && status.declined && !status.stillInterested;
          const acceptKey = `${offer?.id}:response/accept`;
          const declineKey = `${offer?.id}:response/decline`;
          const stillInterestedKey = `${offer?.id}:still-interested`;

          return (
            <article
              id={offer?.id ? `counter-offer-${offer.id}` : undefined}
              className={[
                "my-requests-counter-offer-card",
                status.declined ? "is-declined" : "",
                status.accepted ? "is-accepted" : "",
              ].filter(Boolean).join(" ")}
              key={offer?.id || `${helperName}-${index}`}
            >
              <div className="my-requests-counter-offer-card__head">
                <div>
                  <strong>{isHelperView ? "Your Offer Change Request" : "Care Giver Offer Change Request"}</strong>
                  <span>{isHelperView ? status.subtitle.replace("Care Giver", "You") : status.subtitle}</span>
                  {!isHelperView ? <small>{helperName}</small> : null}
                </div>
                <b>{status.badge}</b>
              </div>

              {helperRows.length ? (
                <div className="my-requests-counter-offer-card__rows">
                  <DetailRows rows={helperRows} />
                </div>
              ) : null}

              {finalRows.length ? (
                <div className="my-requests-final-offer-box">
                  <strong>Requester final offer</strong>
                  <DetailRows rows={finalRows} />
                  {canRespondToFinalOffer ? (
                    <div className="my-requests-counter-offer-actions">
                      <Button
                        className="my-requests-counter-offer-action is-decline"
                        loading={processingOfferKey === declineKey}
                        onClick={() => confirmFinalOfferDecision(offer, false)}
                      >
                        Decline Final Offer
                      </Button>
                      <Button
                        className="my-requests-counter-offer-action is-accept"
                        type="primary"
                        loading={processingOfferKey === acceptKey}
                        onClick={() => confirmFinalOfferDecision(offer, true)}
                      >
                        Accept Final Offer
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {canAffirmStillInterested ? (
                <div className="my-requests-still-interested-box">
                  <strong>Still interested in helping?</strong>
                  <span>If you are still available despite the declined offer change, let the requester know.</span>
                  <Button
                    className="my-requests-counter-offer-action is-accept"
                    type="primary"
                    loading={processingOfferKey === stillInterestedKey}
                    onClick={() => runOfferAction(offer, "still-interested", "Requester notified that you are still interested.")}
                  >
                    Yes, I am still interested
                  </Button>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </DetailSection>
  );
}

function InterestedHelpersDetails({
  task,
  isRequesterView,
  isHelperView,
  selectedWorkspace,
  selectedWorkspaceId,
  userId,
  onTaskUpdated,
}) {
  const navigate = useNavigate();
  const [processingHelperKey, setProcessingHelperKey] = useState("");
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [counterOfferChargesLoading, setCounterOfferChargesLoading] = useState(false);
  const [counterOfferSubmitting, setCounterOfferSubmitting] = useState(false);
  const [dialogChargePayload, setDialogChargePayload] = useState(null);
  const [dialogChargesError, setDialogChargesError] = useState("");
  const [chargePreview, setChargePreview] = useState(null);
  const [finalAmount, setFinalAmount] = useState("");
  const [finalDurationHours, setFinalDurationHours] = useState("");
  const [finalDurationMinutes, setFinalDurationMinutes] = useState("");
  const [finalReason, setFinalReason] = useState("");
  const status = normalizeStatus(task?.status);
  const helpers = Array.isArray(task?.interestedHelpers)
    ? task.interestedHelpers
    : Array.isArray(task?.helperInterests)
      ? task.helperInterests
      : Array.isArray(task?.interests)
        ? task.interests
        : [];
  const displayedHelpers = useMemo(() => {
    const byWorkspace = new Map();
    for (const entry of helpers) {
      const helper = entry?.helper || entry?.user || entry?.profile || entry;
      const key =
        entry?.helperWorkspaceId ||
        entry?.workspaceId ||
        workspaceIdOf(entry?.helperWorkspace) ||
        firstText(helper?.id, helper?.userId, entry?.helperId, entry?.userId);
      if (!key) continue;
      const existing = byWorkspace.get(key);
      if (!existing) {
        byWorkspace.set(key, entry);
        continue;
      }
      const existingTime = Date.parse(existing?.interestedAt || existing?.updatedAt || existing?.createdAt || "");
      const nextTime = Date.parse(entry?.interestedAt || entry?.updatedAt || entry?.createdAt || "");
      if ((Number.isFinite(nextTime) ? nextTime : 0) > (Number.isFinite(existingTime) ? existingTime : 0)) {
        byWorkspace.set(key, entry);
      }
    }
    return Array.from(byWorkspace.values()).filter(isActiveOrAcceptedHelperInterest);
  }, [helpers]);

  useEffect(() => {
    setDialogChargePayload(null);
    setDialogChargesError("");
    setChargePreview(null);
    setFinalAmount("");
    setFinalDurationHours("");
    setFinalDurationMinutes("");
    setFinalReason("");
  }, [selectedOffer]);

  const requesterWorkspaceId = firstText(task?.requesterWorkspaceId, task?.workspaceId, selectedWorkspaceId);
  const requesterOrganizationId = firstText(
    task?.requesterOrganizationId,
    task?.organizationId,
    selectedWorkspace?.organizationId,
    selectedWorkspace?.organization?.id,
  );
  const acceptHelper = async (entry, offer) => {
    const helper = entry?.helper || entry?.user || entry?.profile || entry;
    const helperId = firstText(helper?.id, helper?.userId, entry?.helperId, entry?.userId);
    const helperWorkspaceId = helperWorkspaceIdFrom(entry, offer);
    if (!requesterWorkspaceId) {
      message.warning("Select a requester workspace before accepting Care Giver offers.");
      return;
    }
    if (!helperId || !helperWorkspaceId) {
      message.warning("This Care Giver offer is missing a workspace. Ask the Care Giver to submit the offer again.");
      return;
    }

    const coords = taskCoordinates(task);
    const clientId = localStorage.getItem("clientId") || null;
    const params = new URLSearchParams({
      workspaceId: requesterWorkspaceId,
      requesterWorkspaceId,
      helperWorkspaceId,
    });
    const payload = {
      helperId,
      workspaceId: requesterWorkspaceId,
      requesterWorkspaceId,
      helperWorkspaceId,
      acceptedPingLocation: coords
        ? {
            latitude: coords.latitude,
            longitude: coords.longitude,
            source: O_CLIENT_APP,
            clientId,
            actorId: userId || null,
            pingAt: null,
          }
        : null,
    };

    try {
      setProcessingHelperKey(`${helperId}:accept`);
      await apiClient.put(
        `/olitask-service/api/v1/tasks/${firstText(task?.id, task?.taskId)}/accept/helper/${helperId}?${params.toString()}`,
        payload,
        { headers: acceptHelperHeaders() },
      );
      message.success("Care Giver accepted successfully.");
      await onTaskUpdated?.();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Unable to accept Care Giver.");
    } finally {
      setProcessingHelperKey("");
    }
  };

  const confirmAcceptHelper = (entry, offer) => {
    const helper = entry?.helper || entry?.user || entry?.profile || entry;
    const helperName = helperInterestDisplayName(entry, offer) || personName(helper) || "this Care Giver";
    const noun = isGeneralTask(task) ? "task" : "shift";
    Modal.confirm({
      title: "Accept Care Giver?",
      content: `This will accept ${helperName} for this ${noun} and move the ${noun} forward.`,
      okText: "Confirm & Accept",
      cancelText: "Cancel",
      okButtonProps: { className: "my-requests-confirm-ok-button" },
      onOk: () => acceptHelper(entry, offer),
    });
  };

  const drawerStatus = counterOfferStatus(selectedOffer);
  const drawerPayType = selectedOffer?.payType;
  const drawerFinalPayType = selectedOffer?.responsePayType || selectedOffer?.payType;
  const drawerCurrency = firstText(task?.taskCharges?.currency, task?.currency, "USD");
  const selectedOfferId = firstText(selectedOffer?.id, selectedOffer?.counterOfferId);
  const selectedOfferHasFinalOffer = Boolean(
    selectedOffer?.responseAmount != null ||
      selectedOffer?.responseDurationMinutes != null ||
      selectedOffer?.responseReason ||
      normalizeStatus(selectedOffer?.responseStatus) === "COUNTERED",
  );
  const drawerHelperName = selectedOffer
    ? personName(selectedOffer?.helper || selectedOffer?.user || selectedOffer) || "The Care Giver"
    : "";
  const drawerRows = selectedOffer
    ? detailRows([
        {
          label: offerAmountLabel(drawerPayType, "Care Giver"),
          value: formatOfferMoney(selectedOffer?.payRate ?? selectedOffer?.amount, drawerCurrency, drawerPayType),
        },
        { label: "Time", value: formatDurationLabel(selectedOffer?.durationMinutes) },
        { label: "Reason", value: selectedOffer?.reason || "No reason provided." },
      ])
    : [];
  const drawerFinalRows = selectedOffer
    ? detailRows([
        {
          label: offerAmountLabel(drawerFinalPayType, "Requester"),
          value: formatOfferMoney(
            selectedOffer?.responsePayRate ?? selectedOffer?.responseAmount,
            drawerCurrency,
            drawerFinalPayType,
          ),
        },
        { label: "Time", value: formatDurationLabel(selectedOffer?.responseDurationMinutes) },
        { label: "Note", value: selectedOffer?.responseReason },
      ])
    : [];
  const dialogCharges = dialogChargePayload?.taskCharges;
  const dialogCurrency = firstText(dialogCharges?.currency, drawerCurrency, "USD").toUpperCase();
  const dialogFeeTotal = (dialogCharges?.chargeLines || [])
    .filter((line) => normalizeStatus(line?.category) === "FEE")
    .reduce((sum, line) => sum + Number(line?.amount || 0), 0);
  const dialogTaxTotal = (dialogCharges?.chargeLines || [])
    .filter((line) => normalizeStatus(line?.category) === "TAX")
    .reduce((sum, line) => sum + Number(line?.amount || 0), 0);
  const dialogYouPay = firstNumber(dialogChargePayload?.chargeableAmount, dialogCharges?.totalCharge, dialogChargePayload?.amount);
  const previewCharges = chargePreview?.payload?.taskCharges;
  const previewCurrency = firstText(previewCharges?.currency, dialogCurrency, drawerCurrency, "USD").toUpperCase();
  const previewFeeTotal = (previewCharges?.chargeLines || [])
    .filter((line) => normalizeStatus(line?.category) === "FEE")
    .reduce((sum, line) => sum + Number(line?.amount || 0), 0);
  const previewTaxTotal = (previewCharges?.chargeLines || [])
    .filter((line) => normalizeStatus(line?.category) === "TAX")
    .reduce((sum, line) => sum + Number(line?.amount || 0), 0);
  const previewChargeableAmount = firstNumber(
    chargePreview?.payload?.chargeableAmount,
    previewCharges?.totalCharge,
    chargePreview?.payload?.amount,
  );

  const buildCounterOfferActionPayload = async ({
    amount,
    payRate,
    payType,
    duration,
    durationMinutes,
    reason,
  }) => {
    const numericAmount = Number(amount || 0);
    const numericMinutes = Number(durationMinutes || 0);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      throw new Error("Charges are unavailable because the offer is missing amount.");
    }
    if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) {
      throw new Error("Charges are unavailable because the offer is missing time.");
    }

    const currentCharges = task?.taskCharges || {};
    const location = locationSource(task);
    const country = firstText(currentCharges.country, location.country, DEFAULT_CHARGE_COUNTRY);
    const state = firstText(currentCharges.state, currentCharges.stateCode, location.state, location.region);
    const county = firstText(currentCharges.county, location.county);
    const city = firstText(currentCharges.city, location.city, location.locality, location.town);
    const currency = firstText(currentCharges.currency, task?.currency, drawerCurrency, "USD").toUpperCase();
    const charges = await computeOfferCharges(numericAmount, { currency, country, state, county, city });
    const nextTaskCharges = {
      ...charges,
      currency: charges.currency || currency,
      country,
      state,
      county,
      city,
      tokenAmount: moneyNumber(numericAmount),
      tipAmount: 0,
      useCredits: false,
      appliedCreditsTotal: 0,
      cashPortion: moneyNumber(numericAmount),
      creditDeductions: [],
      location: location || null,
    };
    const helperWorkspaceId = helperWorkspaceIdFrom({}, selectedOffer);
    const helperOrganizationId = helperOrganizationIdFrom({}, selectedOffer);

    return compactPayload({
      amount: moneyNumber(numericAmount),
      ...(payRate != null ? { payRate: moneyNumber(payRate) } : {}),
      ...(payType ? { payType } : {}),
      duration,
      durationMinutes: numericMinutes,
      reason,
      taskCharges: nextTaskCharges,
      tokenAmount: moneyNumber(numericAmount),
      useCredits: false,
      appliedCredits: [],
      appliedCreditsTotal: 0,
      chargeableAmount: moneyNumber(charges?.totalCharge ?? numericAmount),
      receivableAmount: moneyNumber(numericAmount),
      ...(requesterWorkspaceId ? { workspaceId: requesterWorkspaceId, requesterWorkspaceId } : {}),
      ...(requesterOrganizationId ? { requesterOrganizationId } : {}),
      ...(helperWorkspaceId ? { helperWorkspaceId } : {}),
      ...(helperOrganizationId ? { helperOrganizationId } : {}),
    });
  };

  const previewCounterOfferAction = async (mode) => {
    if (!selectedOfferId) return;
    if (mode === "respond" && selectedOfferHasFinalOffer) {
      message.warning("A final offer has already been sent to this Care Giver.");
      return;
    }
    const parsedAmount = mode === "accept" ? Number(selectedOffer?.amount || 0) : Number(finalAmount || 0);
    const durationMinutes = mode === "accept"
      ? Number(selectedOffer?.durationMinutes || 0)
      : Number(finalDurationHours || 0) * 60 + Number(finalDurationMinutes || 0);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      message.warning("Enter a valid offer amount.");
      return;
    }
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      message.warning(`Enter how long the ${isGeneralTask(task) ? "task" : "shift"} should take.`);
      return;
    }

    try {
      setCounterOfferChargesLoading(true);
      setDialogChargesError("");
      const payload = await buildCounterOfferActionPayload({
        amount: parsedAmount,
        payRate:
          mode === "accept"
            ? selectedOffer?.payRate ?? (selectedOffer?.payType ? parsedAmount : undefined)
            : selectedOffer?.payType
              ? parsedAmount
              : undefined,
        payType: selectedOffer?.payType,
        duration: `${durationMinutes} minutes`,
        durationMinutes,
        reason: mode === "accept" ? selectedOffer?.reason || "" : finalReason.trim(),
      });
      setChargePreview({ mode, counterOfferId: selectedOfferId, payload });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to calculate charges for this offer.";
      setDialogChargesError(errorMessage);
      message.error(errorMessage);
    } finally {
      setCounterOfferChargesLoading(false);
    }
  };

  const submitCounterOfferAction = async () => {
    if (!chargePreview?.counterOfferId || !chargePreview?.payload) return;
    const params = new URLSearchParams();
    if (requesterWorkspaceId) params.set("workspaceId", requesterWorkspaceId);
    if (chargePreview.payload.requesterWorkspaceId) params.set("requesterWorkspaceId", chargePreview.payload.requesterWorkspaceId);
    if (chargePreview.payload.helperWorkspaceId) params.set("helperWorkspaceId", chargePreview.payload.helperWorkspaceId);
    const action = chargePreview.mode === "accept" ? "accept" : "respond";
    const query = params.toString() ? `?${params.toString()}` : "";

    try {
      setCounterOfferSubmitting(true);
      await apiClient.put(
        `/olitask-service/api/v1/tasks/${firstText(task?.id, task?.taskId)}/counter-offer/${chargePreview.counterOfferId}/${action}${query}`,
        chargePreview.payload,
        { headers: taskActionHeaders(userId) },
      );
      message.success(chargePreview.mode === "accept" ? "Care Giver offer change request accepted." : "Final offer sent.");
      setChargePreview(null);
      setSelectedOffer(null);
      await onTaskUpdated?.();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to submit offer.");
    } finally {
      setCounterOfferSubmitting(false);
    }
  };

  const declineCounterOffer = async () => {
    if (!selectedOfferId) return;
    const helperWorkspaceId = helperWorkspaceIdFrom({}, selectedOffer);
    const params = new URLSearchParams();
    if (requesterWorkspaceId) params.set("workspaceId", requesterWorkspaceId);
    if (requesterWorkspaceId) params.set("requesterWorkspaceId", requesterWorkspaceId);
    if (helperWorkspaceId) params.set("helperWorkspaceId", helperWorkspaceId);
    const query = params.toString() ? `?${params.toString()}` : "";
    try {
      setCounterOfferSubmitting(true);
      await apiClient.put(
        `/olitask-service/api/v1/tasks/${firstText(task?.id, task?.taskId)}/counter-offer/${selectedOfferId}/decline${query}`,
        {},
        { headers: taskActionHeaders(userId) },
      );
      message.success("Care Giver offer change request declined.");
      setSelectedOffer(null);
      await onTaskUpdated?.();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to decline offer.");
    } finally {
      setCounterOfferSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!selectedOffer) return undefined;
    const amount = Number(selectedOffer?.amount || 0);
    const durationMinutes = Number(selectedOffer?.durationMinutes || 0);
    if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      setDialogChargePayload(null);
      setDialogChargesError("Charges are unavailable because the Care Giver offer change request is missing amount or time.");
      return undefined;
    }
    (async () => {
      try {
        setCounterOfferChargesLoading(true);
        setDialogChargesError("");
        const payload = await buildCounterOfferActionPayload({
          amount,
          payRate: selectedOffer?.payRate ?? (selectedOffer?.payType ? amount : undefined),
          payType: selectedOffer?.payType,
          duration: `${durationMinutes} minutes`,
          durationMinutes,
          reason: selectedOffer?.reason || "",
        });
        if (!cancelled) setDialogChargePayload(payload);
      } catch (error) {
        if (!cancelled) {
          setDialogChargePayload(null);
          setDialogChargesError(error?.response?.data?.message || error?.message || "Failed to calculate charges.");
        }
      } finally {
        if (!cancelled) setCounterOfferChargesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedOffer]);

  if (!isRequesterView || isHelperView || status !== "NEW") return null;

  return (
    <DetailSection title="Interested Care Givers" icon={<TeamOutlined />} tone="green">
      {!displayedHelpers.length ? (
        <p className="my-requests-no-helpers">No Care Giver has expressed interest yet.</p>
      ) : null}
      <div className="my-requests-interested-helpers">
        {displayedHelpers.map((entry, index) => {
          const helper = entry?.helper || entry?.user || entry?.profile || entry;
          const helperId = firstText(helper?.id, helper?.userId, entry?.helperId, entry?.userId);
          const helperWorkspaceId = helperWorkspaceIdFrom(entry);
          const counterOffer = latestCounterOfferForHelper(task, helperId, helperWorkspaceId);
          const offerState = counterOfferDisplayState(counterOffer);
          const canAcceptHelper = canAcceptHelperWithOfferState(counterOffer);
          const withdrawnInterest = isWithdrawnHelperInterest(entry);
          const helperOrganizationId = helperOrganizationIdFrom(entry, counterOffer);
          const helperName = helperInterestDisplayName(entry, counterOffer) || `Care Giver ${index + 1}`;
          const sharedProfile = sharedProfessionProfileOf(entry, counterOffer);
          const sharedProfileId = sharedProfile.profileId;
          const sharedProfileInfo = sharedProfileDisplay(sharedProfile.profile, sharedProfileId);
          const sharedBackgroundCheck = sharedBackgroundCheckOf(entry, counterOffer);
          const sharedBackgroundCheckText = sharedBackgroundCheckLabel(
            sharedBackgroundCheck.backgroundCheck,
            sharedBackgroundCheck.backgroundCheckId,
          );
          const helperWorkspace = firstText(
            counterOffer?.helperWorkspace?.name,
            entry?.helperWorkspace?.name,
            entry?.workspace?.name,
            entry?.workspaceName,
            entry?.helperWorkspaceName,
          );

          return (
            <article className="my-requests-interested-helper-card" key={entry?.id || helper?.id || helperName}>
              <div className="my-requests-interested-helper-card__head">
                <span>{helperName.trim().charAt(0).toUpperCase() || "?"}</span>
                <div>
                  <strong>{helperName}</strong>
                  {helperWorkspace ? <small>{helperWorkspace}</small> : null}
                </div>
              </div>
              {sharedProfileId || sharedProfile.profile ? (
                <div className="my-requests-shared-profile-card">
                  <FileTextOutlined />
                  <div>
                    <span>Professional Profile</span>
                    <strong>{sharedProfileInfo.name}</strong>
                    {sharedProfileInfo.sector ? <small>{sharedProfileInfo.sector}</small> : null}
                    {sharedProfileInfo.summary ? <p>{sharedProfileInfo.summary}</p> : null}
                    {sharedBackgroundCheckText ? (
                      <b>
                        <SafetyCertificateOutlined />
                        {sharedBackgroundCheckText}
                      </b>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {counterOffer ? (
                <div
                  className={[
                    "my-requests-helper-offer-preview",
                    offerState.tone === "accepted" ? "is-accepted" : "",
                    offerState.tone === "declined" ? "is-declined" : "",
                  ].filter(Boolean).join(" ")}
                >
                  <div>
                    <span>Care Giver offer change request</span>
                    <strong>
                      {formatOfferMoney(
                        counterOffer?.payRate ?? counterOffer?.amount,
                        firstText(task?.taskCharges?.currency, task?.currency, "USD"),
                        counterOffer?.payType,
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Time</span>
                    <strong>{formatDurationLabel(counterOffer?.durationMinutes)}</strong>
                  </div>
                  {counterOffer?.reason ? <p>{counterOffer.reason}</p> : null}
                </div>
              ) : null}
              <div className="my-requests-interested-helper-actions">
                {!withdrawnInterest ? (
                  <Button
                    className={`my-requests-helper-pill is-accept ${!canAcceptHelper ? "is-pending" : ""}`}
                    type="primary"
                    disabled={Boolean(processingHelperKey) || !canAcceptHelper || !helperId}
                    loading={processingHelperKey === `${helperId}:accept`}
                    onClick={() => confirmAcceptHelper(entry, counterOffer)}
                  >
                    {processingHelperKey === `${helperId}:accept`
                      ? "Processing..."
                      : canAcceptHelper
                        ? "Accept"
                        : "Pending"}
                  </Button>
                ) : null}
                {helperOrganizationId ? (
                  <Button
                    className="my-requests-helper-pill is-details"
                    onClick={() => navigate(paths.publicOrganizationDetails(helperOrganizationId))}
                  >
                    Business details
                  </Button>
                ) : null}
                {sharedProfileId ? (
                  <Button
                    className="my-requests-helper-pill is-profile"
                    onClick={() => navigate(paths.professionalProfileDetails(sharedProfileId))}
                  >
                    Profile
                  </Button>
                ) : null}
                {counterOffer ? (
                  <Button
                    className={[
                      "my-requests-helper-pill",
                      "is-offer",
                      offerState.tone === "accepted" ? "is-accepted" : "",
                      offerState.tone === "declined" ? "is-declined" : "",
                    ].filter(Boolean).join(" ")}
                    disabled={offerState.disabled}
                    onClick={() => setSelectedOffer(counterOffer)}
                  >
                    {offerState.label}
                  </Button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
      <Drawer
        title="Care Giver Offer Change Request"
        placement="right"
        width={520}
        open={Boolean(selectedOffer)}
        onClose={() => setSelectedOffer(null)}
        className="my-requests-offer-drawer"
      >
        {selectedOffer ? (
          <div className="my-requests-offer-drawer__content">
            <div className="my-requests-offer-drawer__intro">
              <strong>Review Care Giver offer change request</strong>
              <span>{`${drawerHelperName} proposed the terms below.`}</span>
            </div>
            <div className="my-requests-counter-offer-card">
              <div className="my-requests-counter-offer-card__head">
                <div>
                  <strong>Care Giver offer change request</strong>
                  <span>{drawerStatus.subtitle}</span>
                </div>
                <b>{drawerStatus.badge}</b>
              </div>
              <DetailRows rows={drawerRows} />
            </div>

            <div className="my-requests-final-offer-box">
              <strong>Costs</strong>
              {counterOfferChargesLoading && !dialogChargePayload ? (
                <p className="my-requests-counter-offer-note">Calculating charges...</p>
              ) : dialogChargesError ? (
                <div className="my-requests-charge-error">
                  <strong>Charges Error</strong>
                  <span>{dialogChargesError}</span>
                </div>
              ) : dialogChargePayload ? (
                <DetailRows
                  rows={detailRows([
                    {
                      label: offerAmountLabel(dialogChargePayload.payType),
                      value: formatOfferMoney(
                        dialogChargePayload.payRate ?? dialogChargePayload.amount,
                        dialogCurrency,
                        dialogChargePayload.payType,
                      ),
                    },
                    { label: "Estimated total", value: formatOfferMoney(dialogCharges?.totalCharge, dialogCurrency) },
                    { label: "Taxes & Fees", value: formatOfferMoney(dialogFeeTotal + dialogTaxTotal, dialogCurrency) },
                    { label: "You pay", value: formatOfferMoney(dialogYouPay, dialogCurrency) },
                  ])}
                />
              ) : null}
            </div>

            {drawerFinalRows.length ? (
              <div className="my-requests-final-offer-box">
                <strong>Requester final offer</strong>
                <DetailRows rows={drawerFinalRows} />
              </div>
            ) : null}

            <div className="my-requests-counter-offer-actions">
              <Button
                className="my-requests-counter-offer-action is-decline"
                loading={counterOfferSubmitting}
                disabled={counterOfferChargesLoading || drawerStatus.accepted || drawerStatus.declined}
                onClick={declineCounterOffer}
              >
                Decline Request
              </Button>
              <Button
                className="my-requests-counter-offer-action is-accept"
                type="primary"
                loading={counterOfferChargesLoading}
                disabled={counterOfferSubmitting || !selectedOfferId || drawerStatus.accepted || drawerStatus.declined}
                onClick={() => previewCounterOfferAction("accept")}
              >
                {counterOfferChargesLoading ? "Calculating..." : counterOfferSubmitting ? "Accepting..." : "Grant Request"}
              </Button>
            </div>

            <div className="my-requests-final-offer-form">
              <strong>Send final offer</strong>
              <label>
                <span>{offerAmountLabel(selectedOffer?.payType)}</span>
                <div className="my-requests-amount-input-row">
                  <input
                    value={finalAmount}
                    onChange={(event) => setFinalAmount(event.target.value.replace(/[^0-9.]/g, ""))}
                    placeholder={selectedOffer?.amount == null ? "Enter Amount" : String(selectedOffer.amount)}
                    disabled={counterOfferSubmitting || counterOfferChargesLoading || selectedOfferHasFinalOffer}
                  />
                  <b>{String(selectedOffer?.payType || "").toUpperCase().includes("HOUR") ? `${drawerCurrency}/hr` : drawerCurrency}</b>
                </div>
              </label>
              <label>
                <span>Time</span>
                <div className="my-requests-duration-inputs">
                  <div className="my-requests-amount-input-row">
                    <input
                      value={finalDurationHours}
                      onChange={(event) => setFinalDurationHours(event.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="0"
                      disabled={counterOfferSubmitting || counterOfferChargesLoading || selectedOfferHasFinalOffer}
                    />
                    <b>hr</b>
                  </div>
                  <div className="my-requests-amount-input-row">
                    <input
                      value={finalDurationMinutes}
                      onChange={(event) => setFinalDurationMinutes(event.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="0"
                      disabled={counterOfferSubmitting || counterOfferChargesLoading || selectedOfferHasFinalOffer}
                    />
                    <b>min</b>
                  </div>
                </div>
              </label>
              <label>
                <span>Reason</span>
                <textarea
                  value={finalReason}
                  onChange={(event) => setFinalReason(event.target.value)}
                  placeholder="Optional note for the Care Giver"
                  disabled={counterOfferSubmitting || counterOfferChargesLoading || selectedOfferHasFinalOffer}
                />
              </label>
              <Button
                className="my-requests-counter-offer-action is-final"
                disabled={selectedOfferHasFinalOffer || counterOfferSubmitting || counterOfferChargesLoading}
                loading={counterOfferChargesLoading}
                onClick={() => previewCounterOfferAction("respond")}
              >
                {selectedOfferHasFinalOffer
                  ? "Final Offer Sent"
                  : counterOfferChargesLoading
                    ? "Calculating..."
                    : counterOfferSubmitting
                      ? "Sending..."
                      : "Send Final Offer"}
              </Button>
            </div>
          </div>
        ) : null}
      </Drawer>
      <Modal
        title={chargePreview?.mode === "accept" ? "Preview before accepting offer" : "Preview before sending final offer"}
        open={Boolean(chargePreview)}
        onCancel={() => {
          if (!counterOfferSubmitting) setChargePreview(null);
        }}
        footer={null}
        className="my-requests-counter-offer-preview-modal"
        destroyOnClose
      >
        {chargePreview ? (
          <div className="my-requests-offer-drawer__content">
            <p className="my-requests-counter-offer-note">Review the offer, fees, taxes, and final charge before submitting.</p>
            <div className="my-requests-final-offer-box">
              <strong>Costs</strong>
              <DetailRows
                rows={detailRows([
                  {
                    label: offerAmountLabel(chargePreview.payload.payType),
                    value: formatOfferMoney(
                      chargePreview.payload.payRate ?? chargePreview.payload.amount,
                      previewCurrency,
                      chargePreview.payload.payType,
                    ),
                  },
                  { label: `${isGeneralTask(task) ? "Task" : "Shift"} time`, value: formatDurationLabel(chargePreview.payload.durationMinutes) },
                  { label: "Reason", value: chargePreview.payload.reason },
                ])}
              />
            </div>
            <div className="my-requests-final-offer-box">
              <strong>Charge summary</strong>
              <DetailRows
                rows={detailRows([
                  { label: "Estimated total", value: formatOfferMoney(previewCharges?.totalCharge, previewCurrency) },
                  { label: "Taxes & Fees", value: formatOfferMoney(previewFeeTotal + previewTaxTotal, previewCurrency) },
                  { label: "Credits applied", value: formatOfferMoney(0, previewCurrency) },
                  { label: "You will be charged", value: formatOfferMoney(previewChargeableAmount, previewCurrency) },
                ])}
              />
            </div>
            <div className="my-requests-counter-offer-actions">
              <Button
                className="my-requests-counter-offer-action is-decline"
                disabled={counterOfferSubmitting}
                onClick={() => setChargePreview(null)}
              >
                Back
              </Button>
              <Button
                className="my-requests-counter-offer-action is-accept"
                type="primary"
                loading={counterOfferSubmitting}
                disabled={counterOfferSubmitting}
                onClick={submitCounterOfferAction}
              >
                {counterOfferSubmitting ? "Submitting..." : "Confirm & Submit"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </DetailSection>
  );
}

function TaskTypeDetails({ task }) {
  if (isTravoTask(task)) {
    const rows = travoRows(task);
    return (
      <>
        <DetailSection title="Request Airport Luggage Help • Airport" icon={<RocketOutlined />} tone="gold">
          <div className="my-requests-travo-subtitle">Care Giver details for travel assistance</div>
        </DetailSection>
        {rows.airport.length ? (
          <DetailSection title="Airport Information" icon={<RocketOutlined />} tone="blue">
            <DetailRows rows={rows.airport} />
          </DetailSection>
        ) : null}
        {rows.help.length ? (
          <DetailSection title="Requested Help" icon={<TagsOutlined />} tone="green">
            <DetailRows rows={rows.help} />
          </DetailSection>
        ) : null}
        {rows.meeting.length ? (
          <DetailSection title="Meeting Details" icon={<IdcardOutlined />} tone="gold">
            <DetailRows rows={rows.meeting} />
          </DetailSection>
        ) : null}
        {rows.handling.length ? (
          <DetailSection title="Special Handling" icon={<IdcardOutlined />} tone="red">
            <DetailRows rows={rows.handling} />
          </DetailSection>
        ) : null}
      </>
    );
  }

  if (isCareTask(task)) {
    const rows = careRows(task);
    if (!rows.length) return null;
    return (
      <DetailSection title="Care Details" icon={<HomeOutlined />} tone="gold">
        <DetailRows rows={rows} />
      </DetailSection>
    );
  }

  return null;
}

function TaskStatsDetails({ task, isRequesterView, onActorOpen, userId, selectedWorkspaceId }) {
  const navigate = useNavigate();
  const status = normalizeStatus(task?.status);
  const durationRows = detailRows([
    { label: "Duration Estimate", value: durationText(task) },
    ["COMPLETED", "CANCELLED", "CANCELED"].includes(status)
      ? { label: "Actual Duration", value: firstText(task?.actualDuration) || "N/A" }
      : null,
  ].filter(Boolean));
  const paymentRows = taskStatsPayment(task, { isRequesterView });
  const tipRows = taskStatsTipRows(task);
  const groups = actorGroups(task, onActorOpen, {
    userId,
    selectedWorkspaceId,
    onChatOpen: (path) => navigate(path),
  });
  const dates = detailRows([
    { label: "Posted At", value: dateText(taskPostedAtValue(task)) || "N/A" },
    { label: "Last Updated", value: dateText(taskUpdatedAtValue(task)) || "N/A" },
  ]);

  return (
    <div className="my-requests-task-stats">
      {durationRows.length ? (
        <div className="my-requests-stat-card">
          <DetailRows rows={durationRows} />
        </div>
      ) : null}

      {paymentRows.length ? (
        <div className="my-requests-stat-card">
          <div className="my-requests-stat-card__title">Payment Details</div>
          <DetailRows rows={paymentRows} />
          {isRequesterView ? (
            <div className="my-requests-stat-pills">
              <span>Requester View</span>
              <span>{task?.useCredits || task?.taskCharges?.useCredits ? "Credits ON" : "No Credits"}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {tipRows.length ? (
        <div className="my-requests-stat-card">
          <div className="my-requests-stat-card__title">Tip Payment</div>
          <DetailRows rows={tipRows} />
        </div>
      ) : null}

      {groups.length ? (
        <div className="my-requests-stat-card">
          <div className="my-requests-stat-card__title">Actors</div>
          <div className="my-requests-stat-actors">
            {groups.map((group) => (
              <div className="my-requests-stat-actor" key={group.title}>
                <div className="my-requests-stat-actor__head">
                  <span>{group.icon}</span>
                  <div>
                    <strong>{group.title}</strong>
                    <small>{group.hint}</small>
                  </div>
                </div>
                <DetailRows rows={group.rows} />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {dates.length ? (
        <div className="my-requests-stat-card">
          <div className="my-requests-stat-card__title">Dates</div>
          <DetailRows rows={dates} />
        </div>
      ) : null}
    </div>
  );
}

function TaskReviewsSection({ taskId, visible }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadReviews = async () => {
      if (!visible || !taskId) {
        setReviews([]);
        return;
      }
      try {
        setLoading(true);
        const response = await apiClient.get(`/olitask-service/api/v1/task-reviews/task/${taskId}`);
        const payload = response?.data?.data || response?.data || [];
        if (!cancelled) setReviews(Array.isArray(payload) ? payload : []);
      } catch {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [taskId, visible]);

  if (!visible) return null;

  return (
    <DetailSection title="Reviews" icon={<StarOutlined />} tone="gold">
      {loading ? <Skeleton active paragraph={{ rows: 2 }} /> : null}
      {!loading && reviews.length ? (
        <div className="my-task-reviews-list">
          {reviews.map((review, index) => {
            const ratingValue = Math.max(0, Math.min(5, Math.round(firstNumber(review?.rating) || 0)));
            return (
              <div className="my-task-review-card" key={firstText(review?.id, `${review?.source || "review"}-${index}`)}>
                <div className="my-task-review-card__head">
                  <strong>{titleCase(firstText(review?.source, "Review"))}</strong>
                  <span>
                    {Array.from({ length: 5 }).map((_, starIndex) => (
                      <StarOutlined key={starIndex} className={starIndex < ratingValue ? "is-filled" : ""} />
                    ))}
                  </span>
                </div>
                <p>{firstText(review?.description, review?.comment, review?.review, "No review text.")}</p>
              </div>
            );
          })}
        </div>
      ) : null}
      {!loading && !reviews.length ? (
        <Paragraph className="my-requests-task-description">No reviews have been submitted yet.</Paragraph>
      ) : null}
    </DetailSection>
  );
}

function ActorReviewsDrawer({ open, onClose, actorId, actorType, actorName, averageRating }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const loadReviews = async () => {
      if (!open || !actorId) {
        setReviews([]);
        setError("");
        return;
      }
      const rolePath = actorType === "helper" ? "helpers" : "requesters";
      try {
        setLoading(true);
        setError("");
        const response = await apiClient.get(`/olitask-service/api/v1/task-reviews/${rolePath}/${actorId}/reviews`, {
          params: { page: 0, size: 20 },
        });
        const payload = response?.data?.data || response?.data || {};
        const content = Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload)
            ? payload
            : [];
        if (!cancelled) setReviews(content);
      } catch (err) {
        if (!cancelled) {
          setReviews([]);
          setError(err?.response?.data?.message || err?.message || "Unable to load reviews.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [actorId, actorType, open]);

  const ratingNumber = Number(averageRating);
  const averageText = Number.isFinite(ratingNumber) && ratingNumber > 0 ? ratingNumber.toFixed(1) : "N/A";
  const title = actorName ? `${actorName} - Reviews` : "Reviews";

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      width="min(520px, 100vw)"
      className="my-actor-reviews-drawer"
    >
      <Space direction="vertical" size={14} className="my-requests-drawer__content">
        <div className="my-actor-reviews-summary">
          <span><StarOutlined /></span>
          <div>
            <strong>Average Rating</strong>
            <div className="my-actor-profile-stars" aria-label={`${averageText} average rating`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarOutlined
                  key={star}
                  className={Number.isFinite(ratingNumber) && star <= Math.round(ratingNumber) ? "is-filled" : ""}
                />
              ))}
              <em>{averageText}/5</em>
            </div>
          </div>
        </div>

        {loading ? <Skeleton active paragraph={{ rows: 5 }} /> : null}

        {!loading && error ? (
          <Alert showIcon type="error" message="Unable to load reviews" description={error} />
        ) : null}

        {!loading && !error && reviews.length ? (
          <div className="my-actor-reviews-list">
            {reviews.map((review, index) => {
              const reviewRating = Math.max(0, Math.min(5, Math.round(firstNumber(review?.rating) || 0)));
              const photos = Array.isArray(review?.photos) ? review.photos : [];
              return (
                <article className="my-actor-review-card" key={firstText(review?.id, `review-${index}`)}>
                  <div className="my-actor-review-card__head">
                    <strong>
                      {firstText(review?.reviewer?.nickName, review?.reviewer?.firstName, "User")}
                      {" - "}
                      {normalizeStatus(review?.source) === "HELPER" ? "Care Giver" : "Requester"}
                    </strong>
                    <span>
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <StarOutlined key={starIndex} className={starIndex < reviewRating ? "is-filled" : ""} />
                      ))}
                    </span>
                  </div>

                  {review?.task?.title ? <p className="my-actor-review-card__task">Request: {review.task.title}</p> : null}
                  <p>Review: {firstText(review?.description, review?.comment, review?.review, "No review text.")}</p>

                  {photos.length ? (
                    <div className="my-actor-review-card__photos">
                      {photos.slice(0, 4).map((photo, photoIndex) => (
                        <img key={`${photo}-${photoIndex}`} src={photo} alt="" />
                      ))}
                    </div>
                  ) : null}

                  <time>{dateText(review?.createdAt || review?.updatedAt)}</time>
                </article>
              );
            })}
          </div>
        ) : null}

        {!loading && !error && !reviews.length ? (
          <Empty description="No reviews available." />
        ) : null}
      </Space>
    </Drawer>
  );
}

function ActorDetailsDrawer({ actor, onClose }) {
  const navigate = useNavigate();
  const openPath = (path) => {
    if (!path) return;
    onClose();
    navigate(path);
  };

  return (
    <Drawer
      title={actor?.title || "Actor Details"}
      open={!!actor}
      onClose={onClose}
      width="min(420px, 100vw)"
      className="my-requests-actor-drawer"
    >
      {actor ? (
        <Space direction="vertical" size={14} className="my-requests-drawer__content">
          <div className="my-requests-actor-drawer__hero">
            <span>{actor.icon || <UserOutlined />}</span>
            <div>
              <strong>{actor.title}</strong>
              {actor.subtitle ? <small>{actor.subtitle}</small> : null}
            </div>
          </div>
          {actor.profilePath ? (
            <Button
              className="my-requests-profile-link-button"
              type="primary"
              icon={<IdcardOutlined />}
              onClick={() => openPath(actor.profilePath)}
              block
            >
              {actor.profileLabel || "View Profile"}
            </Button>
          ) : null}
          {actor.organizationPath ? (
            <Button
              className="my-requests-profile-link-button"
              type="primary"
              icon={<TeamOutlined />}
              onClick={() => openPath(actor.organizationPath)}
              block
            >
              View Organization Details
            </Button>
          ) : null}
          {actor.profileRows?.length ? (
            <DetailSection title="Quick Info" icon={<IdcardOutlined />} tone="blue">
              <DetailRows rows={actor.profileRows} />
            </DetailSection>
          ) : null}
          {actor.bioRows?.length ? (
            <DetailSection title="Bio" icon={<FileTextOutlined />} tone="gold">
              <DetailRows rows={actor.bioRows} />
            </DetailSection>
          ) : null}
          {actor.organizationRows?.length ? (
            <DetailSection title="Organization details" icon={<TeamOutlined />} tone="green">
              <DetailRows rows={actor.organizationRows} />
            </DetailSection>
          ) : null}
          {actor.contactRows?.length ? (
            <DetailSection title="Contact information" icon={<IdcardOutlined />} tone="blue">
              <DetailRows rows={actor.contactRows} />
            </DetailSection>
          ) : null}
          {actor.locationRows?.length ? (
            <DetailSection title="Location information" icon={<EnvironmentOutlined />} tone="gold">
              <DetailRows rows={actor.locationRows} />
            </DetailSection>
          ) : null}
        </Space>
      ) : null}
    </Drawer>
  );
}

function TaskDetailsDrawerContent({
  task,
  loading,
  isHelperWorkspace,
  userId,
  onPhotoPress,
  onActorOpen,
  onTaskUpdated,
  onManageTask,
  selectedWorkspace,
  selectedWorkspaceId,
  showHero = true,
  showActions = false,
  showCounterOfferDetails = false,
}) {
  const requesterId = firstText(task?.requester?.id, task?.requester?.userId);
  const helperId = firstText(task?.helper?.id, task?.helper?.userId);
  const helperWorkspaceId = firstText(task?.helperWorkspaceId, workspaceIdOf(task?.helperWorkspace));
  const isTaskHelperWorkspace = Boolean(selectedWorkspaceId && helperWorkspaceId && selectedWorkspaceId === helperWorkspaceId);
  const isTaskRequester = Boolean(userId && requesterId && userId === requesterId);
  const isTaskHelper = Boolean((userId && helperId && userId === helperId) || isTaskHelperWorkspace);
  const isHelperView = isHelperWorkspace || isTaskHelper;
  const isRequesterView = isTaskRequester || !isHelperView;
  const noun = isGeneralTask(task) ? "Task" : "Shift";
  const status = normalizeStatus(task?.status);
  const canViewFullLocation =
    isTaskRequester ||
    (isTaskHelper && ["ACCEPTED", "NAVIGATING", "ARRIVED", "IN_PROGRESS", "COMPLETED"].includes(status));
  const locationDetails = taskLocationRows(task, { canViewFullLocation });

  return (
    <Space direction="vertical" size={14} className="my-requests-drawer__content">
      {loading ? (
        <div className="my-requests-drawer__loading">
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : null}

      <TaskStatusProgress task={task} />

      {showActions ? (
        <TaskActionSection
          task={task}
          isHelperView={isHelperView}
          isTaskHelper={isTaskHelper}
          isTaskRequester={isTaskRequester}
          userId={userId}
          selectedWorkspace={selectedWorkspace}
          selectedWorkspaceId={selectedWorkspaceId}
          onTaskUpdated={onTaskUpdated}
        />
      ) : null}

      {isHelperView || showCounterOfferDetails ? (
        <CounterOfferDetails task={task} isHelperView={isHelperView} userId={userId} onTaskUpdated={onTaskUpdated} />
      ) : null}
      <InterestedHelpersDetails
        task={task}
        isRequesterView={isRequesterView}
        isHelperView={isHelperView}
        selectedWorkspace={selectedWorkspace}
        selectedWorkspaceId={selectedWorkspaceId}
        userId={userId}
        onTaskUpdated={onTaskUpdated}
      />

      {showHero ? (
        <div className="my-requests-drawer__hero">
          <div className="my-requests-drawer__hero-top">
            <span className={`olitask-request-card__status is-${statusClass(task.status)}`}>
              {titleCase(task.status || "New")}
            </span>
            <span className="my-requests-drawer__type">
              {isTravoTask(task) ? "Request Airport Luggage Help" : isCareTask(task) ? "Home Care" : "General Task"}
            </span>
          </div>
          <Title level={4}>{taskTitle(task)}</Title>
          <Paragraph>{taskDescription(task)}</Paragraph>
        </div>
      ) : null}

      <CoreTasksSection task={task} />

      <DetailSection title={`${noun} Description`} icon={<FileTextOutlined />} tone="gold">
        <Paragraph className="my-requests-task-description">{taskDescription(task)}</Paragraph>
      </DetailSection>

      {locationDetails.rows.length ? (
        <DetailSection title={locationDetails.title} icon={<EnvironmentOutlined />} tone="blue">
          <DetailRows rows={locationDetails.rows} />
          {locationDetails.privacyText ? (
            <Paragraph className="my-requests-location-privacy">{locationDetails.privacyText}</Paragraph>
          ) : null}
        </DetailSection>
      ) : null}

      <TaskStatsDetails
        task={task}
        isRequesterView={isRequesterView}
        onActorOpen={onActorOpen}
        userId={userId}
        selectedWorkspaceId={selectedWorkspaceId}
      />

      <TaskTypeDetails task={task} />
      <DetailPhotoStrip task={task} onPhotoPress={onPhotoPress} />

      <TaskReviewsSection
        taskId={firstText(task?.id, task?.taskId)}
        visible={normalizeStatus(task?.status) === "COMPLETED"}
      />

      {task?.cancel ? (
        <DetailSection title="Cancellation" icon={<IdcardOutlined />} tone="red">
          <DetailRows
            rows={detailRows([
              { label: "Reason", value: task.cancel.reason || task.cancel.cancelReason },
              { label: "Notes", value: task.cancel.notes || task.cancel.comment },
              { label: "Cancelled at", value: dateText(task.cancel.cancelledAt || task.cancel.createdAt) },
            ])}
          />
        </DetailSection>
      ) : null}

      {onManageTask ? (
        <div className="my-requests-drawer__bottom-action">
          <Button
            type="primary"
            className="my-requests-manage-button"
            icon={<FileTextOutlined />}
            onClick={onManageTask}
            title={`Manage ${noun.toLowerCase()} post`}
            aria-label={`Manage ${noun.toLowerCase()} post`}
          >
            Manage {noun} Post
          </Button>
        </div>
      ) : null}

    </Space>
  );
}

function PhotoGalleryModal({ gallery, onClose, onShift, onSelect }) {
  return (
    <Modal
      open={gallery.photos.length > 0}
      footer={null}
      onCancel={onClose}
      width="min(860px, 96vw)"
      centered
      className="my-requests-photo-modal"
      title={
        gallery.photos.length
          ? `Photo ${gallery.index + 1} of ${gallery.photos.length}`
          : "Photo Gallery"
      }
    >
      {gallery.photos.length ? (
        <div className="my-requests-photo-viewer">
          {gallery.photos.length > 1 ? (
            <button
              type="button"
              className="my-requests-photo-viewer__nav is-prev"
              onClick={() => onShift(-1)}
              aria-label="Previous photo"
            >
              <LeftOutlined />
            </button>
          ) : null}
          <img src={gallery.photos[gallery.index]} alt="" />
          {gallery.photos.length > 1 ? (
            <button
              type="button"
              className="my-requests-photo-viewer__nav is-next"
              onClick={() => onShift(1)}
              aria-label="Next photo"
            >
              <RightOutlined />
            </button>
          ) : null}
          {gallery.photos.length > 1 ? (
            <div className="my-requests-photo-viewer__thumbs">
              {gallery.photos.map((url, index) => (
                <button
                  type="button"
                  key={url}
                  className={index === gallery.index ? "is-active" : ""}
                  onClick={() => onSelect(index)}
                  aria-label={`Open photo ${index + 1}`}
                >
                  <img src={url} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

export default function MyTasks({
  basePath = "/hire-care-giver/shift-filler/posts",
  createPath = paths.taskCreateCare,
  heading: headingOverride = "",
  taskKind = "CARE",
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { tasks, loading, error, totalPages, totalElements, fetchTasks } = useTasks();
  const [page, setPage] = useState(1);
  const [taskFilter, setTaskFilter] = useState("ACTIVE");
  const [nearbyView, setNearbyView] = useState("LIST");
  const [nearbyRadius, setNearbyRadius] = useState(10);
  const [nearbyLocation, setNearbyLocation] = useState(null);
  const [nearbyLocationText, setNearbyLocationText] = useState("");
  const [nearbySuggestions, setNearbySuggestions] = useState([]);
  const [nearbySearchFocused, setNearbySearchFocused] = useState(false);
  const [nearbyTasks, setNearbyTasks] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState("");
  const [nearbyTotalElements, setNearbyTotalElements] = useState(0);
  const [nearbyTotalPages, setNearbyTotalPages] = useState(1);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      process.env.VITE_GOOGLE_MAPS_API_KEY ||
      "",
  );
  const [locationApiReady, setLocationApiReady] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState("");
  const [selectedActor, setSelectedActor] = useState(null);
  const [photoGallery, setPhotoGallery] = useState({ photos: [], index: 0 });
  const [nearbyInterestTask, setNearbyInterestTask] = useState(null);
  const [nearbyInterestCounterOpen, setNearbyInterestCounterOpen] = useState(false);
  const [nearbyInterestProcessing, setNearbyInterestProcessing] = useState("");
  const [nearbyCounterAmount, setNearbyCounterAmount] = useState("");
  const [nearbyCounterDurationHours, setNearbyCounterDurationHours] = useState("");
  const [nearbyCounterDurationMinutes, setNearbyCounterDurationMinutes] = useState("");
  const [nearbyCounterReason, setNearbyCounterReason] = useState("");
  const [nearbySelectedCounterReasons, setNearbySelectedCounterReasons] = useState([]);

  const isHelperWorkspace = false;
  const taskMode = String(taskKind || "CARE").toUpperCase();
  const taskNoun = taskMode === "GENERAL" ? "task" : "shift";
  const taskNounPlural = taskMode === "GENERAL" ? "tasks" : "shifts";
  const createButtonLabel = taskMode === "GENERAL" ? "Post Task" : "Request Help";
  const createEmptyLabel = taskMode === "GENERAL" ? "Create Task" : "Create Request";
  const detailPath = useCallback(
    (taskId) => `${String(basePath || "/hire-care-giver/shift-filler/posts").replace(/\/+$/g, "")}/${taskId}`,
    [basePath],
  );
  const userId = userIdOf(user);

  useEffect(() => {
    if (!isHelperWorkspace) return;
    const params = new URLSearchParams(location.search);
    const nearbyMode = String(params.get("nearby") || "").toLowerCase();
    const requestedFilter = String(params.get("filter") || "").toUpperCase();
    if (nearbyMode === "active" || requestedFilter === "ACTIVE") {
      setTaskFilter("ACTIVE");
      setNearbyView("LIST");
      setPage(1);
    }
  }, [isHelperWorkspace, location.search]);

  const taskContextFilters = useMemo(
    () =>
      isHelperWorkspace
        ? {
            workspaceId: selectedWorkspaceId,
            helperWorkspaceId: selectedWorkspaceId,
            helperOrganizationId: selectedWorkspace?.organizationId,
            contextRole: "HELPER",
          }
        : {
            workspaceId: selectedWorkspaceId,
            requesterWorkspaceId: selectedWorkspaceId,
            contextRole: "REQUESTER",
          },
    [isHelperWorkspace, selectedWorkspace?.organizationId, selectedWorkspaceId],
  );

  const loadNearbyTasks = useCallback(async (nextPage = page - 1) => {
    if (!isHelperWorkspace) return;
    const location = nearbyLocation || DEFAULT_NEARBY_LOCATION;
    const effectiveFilter = nearbyView === "MAP" ? "ACTIVE" : taskFilter;
    const pageSize = nearbyView === "MAP" ? NEARBY_MAP_PAGE_SIZE : NEARBY_LIST_PAGE_SIZE;

    try {
      setNearbyLoading(true);
      setNearbyError("");
      const params = new URLSearchParams({
        page: String(nextPage),
        size: String(pageSize),
        radius: String(nearbyRadius),
        lat: String(location.latitude),
        lon: String(location.longitude),
      });
      if (effectiveFilter !== "ALL") params.set("status", effectiveFilter);

      const endpoint = isHelperOwnedFilter(effectiveFilter)
        ? effectiveFilter === "ALL" || effectiveFilter === "ACTIVE"
          ? "/olitask-service/api/v1/tasks/helper-tasks"
          : "/olitask-service/api/v1/tasks/helper-tasks/status"
        : effectiveFilter === "ACTIVE"
          ? "/olitask-service/api/v1/tasks/near-helper-tasks/active"
          : "/olitask-service/api/v1/tasks/near-helper-tasks";

      if (selectedWorkspaceId) {
        params.set("workspaceId", selectedWorkspaceId);
        params.set("helperWorkspaceId", selectedWorkspaceId);
      }
      if (selectedWorkspace?.organizationId) {
        params.set("helperOrganizationId", selectedWorkspace.organizationId);
      }

      const response = await apiClient.get(`${endpoint}?${params.toString()}`);
      const payload = response?.data?.data || response?.data || {};
      const content = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.content)
          ? payload.content
          : [];
      setNearbyTasks(content.filter(isCareTask));
      setNearbyTotalPages(payload.totalPages || 1);
      setNearbyTotalElements(payload.totalElements || content.length);
    } catch (err) {
      setNearbyError(err?.response?.data?.message || err?.message || "Unable to load nearby requests.");
      setNearbyTasks([]);
      setNearbyTotalPages(1);
      setNearbyTotalElements(0);
    } finally {
      setNearbyLoading(false);
    }
  }, [
    isHelperWorkspace,
    nearbyLocation,
    nearbyRadius,
    nearbyView,
    page,
    selectedWorkspace?.organizationId,
    selectedWorkspaceId,
    taskFilter,
  ]);

  useEffect(() => {
    if (isHelperWorkspace) {
      loadNearbyTasks(page - 1);
      return;
    }
    fetchTasks(page - 1, PAGE_SIZE, "", taskContextFilters);
  }, [fetchTasks, isHelperWorkspace, loadNearbyTasks, page, taskContextFilters]);

  useEffect(() => {
    if (!isHelperWorkspace || nearbyLocation) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setNearbyLocation(DEFAULT_NEARBY_LOCATION);
      setNearbyLocationText("Current Location");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNearbyLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setNearbyLocationText("Current Location");
      },
      () => {
        setNearbyLocation(DEFAULT_NEARBY_LOCATION);
        setNearbyLocationText("Current Location");
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, [isHelperWorkspace, nearbyLocation]);

  useEffect(() => {
    if (!isHelperWorkspace || googleMapsApiKey) return;
    let cancelled = false;
    const loadAppConfig = async () => {
      try {
        const response = await apiClient.get("/olitask-service/api/v1/app-config", {
          headers: { "O-SKIP-WORKSPACE-CONTEXT": "1" },
        });
        if (cancelled) return;
        setGoogleMapsApiKey(googleMapsKeyFromConfigResponse(response));
      } catch {
        if (!cancelled) setGoogleMapsApiKey("");
      }
    };
    loadAppConfig();
    return () => {
      cancelled = true;
    };
  }, [googleMapsApiKey, isHelperWorkspace]);

  useEffect(() => {
    if (!isHelperWorkspace) return;
    let alive = true;
    setLocationApiReady(false);
    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (!alive) return;
        setLocationApiReady(Boolean(ok && window.google?.maps));
      })
      .catch(() => {
        if (alive) setLocationApiReady(false);
      });
    return () => {
      alive = false;
    };
  }, [googleMapsApiKey, isHelperWorkspace]);

  useEffect(() => {
    setPage(1);
  }, [selectedWorkspaceId, taskFilter, nearbyView, nearbyRadius]);

  useEffect(() => {
    if (!isHelperWorkspace) return undefined;
    const id = window.setTimeout(() => {
      searchNearbyAddresses(nearbyLocationText);
    }, 250);
    return () => window.clearTimeout(id);
  }, [isHelperWorkspace, nearbyLocationText]);

  const heading = headingOverride || (isHelperWorkspace
    ? taskFilter === "ACTIVE"
      ? "Active Nearby Requests"
      : "Nearby Requests"
    : "My Requests");
  const kindFilteredTasks = useMemo(
    () => (Array.isArray(tasks) ? tasks.filter((task) => taskMatchesKind(task, taskMode)) : []),
    [taskMode, tasks],
  );
  const uniqueTasks = useMemo(
    () => dedupeTasks(isHelperWorkspace ? nearbyTasks.filter(isCareTask) : kindFilteredTasks),
    [isHelperWorkspace, kindFilteredTasks, nearbyTasks],
  );
  const visibleTasks = useMemo(
    () => uniqueTasks.filter((task) => shouldShowForFilter(task, taskFilter)),
    [taskFilter, uniqueTasks],
  );
  const activeFilterLabel =
    FILTER_BUTTONS.find((button) => button.value === taskFilter)?.label || "Active";
  const emptyStateTitle = taskFilter === "ACTIVE" ? `No active ${taskNounPlural}` : `No ${activeFilterLabel.toLowerCase()} ${taskNounPlural}`;
  const emptyStateText =
    taskFilter === "ACTIVE"
      ? `You do not have any active ${taskNounPlural} right now.`
      : `No ${taskNounPlural} found for the selected ${activeFilterLabel.toLowerCase()} filter.`;
  const displayLoading = isHelperWorkspace ? nearbyLoading : loading;
  const displayError = isHelperWorkspace ? nearbyError : error;
  const displayTotalElements = isHelperWorkspace ? nearbyTotalElements : totalElements;
  const displayTotalPages = isHelperWorkspace ? nearbyTotalPages : totalPages;

  const refreshTasks = () => {
    if (isHelperWorkspace) return loadNearbyTasks(page - 1);
    return fetchTasks(page - 1, PAGE_SIZE, "", taskContextFilters);
  };

  const resetNearbyInterestDialog = () => {
    setNearbyInterestTask(null);
    setNearbyInterestCounterOpen(false);
    setNearbyInterestProcessing("");
    setNearbyCounterAmount("");
    setNearbyCounterDurationHours("");
    setNearbyCounterDurationMinutes("");
    setNearbyCounterReason("");
    setNearbySelectedCounterReasons([]);
  };

  const openNearbyInterestDialog = (task) => {
    const config = counterOfferConfig(task);
    const originalMinutes = config.currentDurationMinutes;
    setNearbyInterestTask(task);
    setNearbyInterestCounterOpen(false);
    setNearbyInterestProcessing("");
    setNearbyCounterAmount(config.amount > 0 ? String(config.amount) : "");
    setNearbyCounterDurationHours(String(Math.floor(originalMinutes / 60) || 0));
    setNearbyCounterDurationMinutes(String(originalMinutes % 60 || 0));
    setNearbyCounterReason("");
    setNearbySelectedCounterReasons([]);
  };

  const openNearbyCounterOffer = () => {
    if (!selectedWorkspaceId) {
      message.warning("Please select a Care Giver workspace before sending a counter offer.");
      return;
    }
    if (taskHasUserCounterOffer(nearbyInterestTask, userId)) {
      message.warning(`You can only submit one counter offer for this ${isGeneralTask(nearbyInterestTask) ? "task" : "shift"}.`);
      return;
    }
    setNearbyInterestCounterOpen(true);
  };

  const toggleNearbyCounterReason = (reason) => {
    setNearbySelectedCounterReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason],
    );
  };

  const useCurrentNearbyLocation = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNearbyLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setNearbyLocationText("Current Location");
        setNearbySuggestions([]);
        setNearbySearchFocused(false);
        setPage(1);
      },
      () => message.warning("Location access is unavailable. Enter a location manually."),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  };

  const searchNearbyAddresses = async (query) => {
    const input = String(query || "").trim();
    if (input.length < 3 || !window.google?.maps?.places) {
      setNearbySuggestions([]);
      return;
    }
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input }, (predictions = [], status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        setNearbySuggestions([]);
        return;
      }
      setNearbySuggestions(
        predictions.map((prediction) => ({
          description: prediction.description,
          placeId: prediction.place_id,
        })),
      );
    });
  };

  const selectNearbySuggestion = (suggestion) => {
    if (!window.google?.maps?.places) return;
    const node = document.createElement("div");
    const service = new window.google.maps.places.PlacesService(node);
    service.getDetails({ placeId: suggestion.placeId, fields: ["geometry", "formatted_address"] }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
      const loc = place?.geometry?.location;
      const latitude = typeof loc?.lat === "function" ? loc.lat() : null;
      const longitude = typeof loc?.lng === "function" ? loc.lng() : null;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      setNearbyLocation({ latitude, longitude });
      setNearbyLocationText(place?.formatted_address || suggestion.description);
      setNearbySuggestions([]);
      setNearbySearchFocused(false);
      setPage(1);
    });
  };

  const nearbyTaskAction = async (task, action) => {
    const id = task?.id || task?.taskId;
    if (!id) return false;
    const helperActionContext = selectedHelperContext({ selectedWorkspace, selectedWorkspaceId });
    const interestQuery = helperActionContext.query;
    const withdrawQuery = withdrawInterestQuery(selectedWorkspaceId);
    const endpoint = action === "withdraw"
      ? `/olitask-service/api/v1/tasks/${id}/withdraw-interest${withdrawQuery ? `?${withdrawQuery}` : ""}`
      : `/olitask-service/api/v1/tasks/${id}/express-interest${interestQuery ? `?${interestQuery}` : ""}`;
    try {
      if (action === "withdraw") {
        await withdrawInterestRequest(id, selectedWorkspaceId);
      } else {
        await apiClient.put(endpoint, helperActionContext.body, {
          headers: taskActionHeaders(userId),
        });
      }
      message.success(action === "withdraw" ? "You have withdrawn your interest." : "Your interest has been submitted.");
      await loadNearbyTasks(page - 1);
      return true;
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to complete this action.");
      return false;
    }
  };

  const submitNearbyInterest = async () => {
    if (!nearbyInterestTask) return;
    setNearbyInterestProcessing("interest");
    const ok = await nearbyTaskAction(nearbyInterestTask, "interest");
    setNearbyInterestProcessing("");
    if (ok) resetNearbyInterestDialog();
  };

  const submitNearbyCounterOffer = async () => {
    const task = nearbyInterestTask;
    const id = task?.id || task?.taskId;
    if (!id) return;
    const counterConfig = counterOfferConfig(task);
    const originalAmount = Number(counterConfig.amount || 0);
    const parsedAmount = nearbyCounterAmount.trim().length ? Number(nearbyCounterAmount) : originalAmount;
    const originalMinutes = counterConfig.currentDurationMinutes;
    const hours = counterConfig.allowDurationCounter
      ? parseInt(nearbyCounterDurationHours || "0", 10)
      : Math.floor(originalMinutes / 60);
    const minutes = counterConfig.allowDurationCounter
      ? parseInt(nearbyCounterDurationMinutes || "0", 10)
      : originalMinutes % 60;
    const totalMinutes = hours * 60 + minutes;
    const typedReason = nearbyCounterReason.trim();
    const quickReasons = nearbySelectedCounterReasons.filter((reason) => reason !== "Other");
    const reason = [...quickReasons, typedReason].filter(Boolean).join("; ");
    const amountChanged = Math.abs(parsedAmount - originalAmount) >= 0.005;
    const timeChanged = counterConfig.allowDurationCounter && totalMinutes !== originalMinutes;

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      message.warning("Please enter a valid counter offer amount.");
      return;
    }
    if (counterConfig.allowDurationCounter && (!Number.isFinite(totalMinutes) || totalMinutes <= 0)) {
      message.warning("Please enter the time needed for your counter offer.");
      return;
    }
    if (!amountChanged && !timeChanged) {
      message.warning(
        counterConfig.allowDurationCounter
          ? "Update the amount, the time, or both before sending a counter offer."
          : "Update the amount before sending a counter offer.",
      );
      return;
    }
    if (nearbySelectedCounterReasons.includes("Other") && !typedReason) {
      message.warning("Please enter a short reason for your counter offer.");
      return;
    }

    const helperActionContext = selectedHelperContext({ selectedWorkspace, selectedWorkspaceId });
    const payload = {
      amount: parsedAmount,
      ...(counterConfig.counterPayRateFromAmount ? { payRate: parsedAmount } : {}),
      ...(counterConfig.counterPayType ? { payType: counterConfig.counterPayType } : {}),
      duration: `${totalMinutes} minutes`,
      durationMinutes: totalMinutes,
      reason,
      ...(helperActionContext.body || {}),
    };

    try {
      setNearbyInterestProcessing("counter-offer");
      await apiClient.put(`/olitask-service/api/v1/tasks/${id}/counter-offer`, payload, {
        headers: taskActionHeaders(userId),
      });
      message.success("Your counter offer has been submitted.");
      await loadNearbyTasks(page - 1);
      resetNearbyInterestDialog();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to submit counter offer.");
    } finally {
      setNearbyInterestProcessing("");
    }
  };

  const loadDrawerTask = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    try {
      if (!silent) setDrawerLoading(true);
      const response = await apiClient.get(`/olitask-service/api/v1/tasks/${id}`);
      const details = response?.data?.data || response?.data || {};
      setSelectedTask((current) => ({
        ...(current || {}),
        ...details,
        id: details.id || current?.id || id,
      }));
    } catch (err) {
      setDrawerError(
        err?.response?.data?.message ||
          err?.message ||
          "Showing the request details already loaded.",
      );
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const selectedTaskId = firstText(selectedTask?.id, selectedTask?.taskId);
  const refreshSelectedTask = useCallback(
    ({ silent = false } = {}) => loadDrawerTask(selectedTaskId, { silent }),
    [loadDrawerTask, selectedTaskId],
  );

  useTaskStatusPolling({
    taskId: selectedTaskId,
    task: selectedTask,
    refreshTask: refreshSelectedTask,
    enabled: Boolean(selectedTaskId),
  });

  const openTaskDetails = async (task) => {
    setSelectedTask(task);
    setDrawerError("");
    const id = task?.id || task?.taskId;
    if (!id) return;

    await loadDrawerTask(id);
  };

  const closeTaskDetails = () => {
    setSelectedTask(null);
    setDrawerError("");
    setSelectedActor(null);
    setDrawerLoading(false);
  };

  const openPhotoGallery = (photos, index = 0) => {
    setPhotoGallery({ photos, index });
  };

  const closePhotoGallery = () => {
    setPhotoGallery({ photos: [], index: 0 });
  };

  const shiftPhotoGallery = (direction) => {
    setPhotoGallery((current) => {
      if (!current.photos.length) return current;
      const nextIndex =
        (current.index + direction + current.photos.length) % current.photos.length;
      return { ...current, index: nextIndex };
    });
  };

  const nearbyCounterConfig = nearbyInterestTask ? counterOfferConfig(nearbyInterestTask) : null;
  const submittedNearbyCounterOffer = taskHasUserCounterOffer(nearbyInterestTask, userId);

  return (
    <div className="my-requests-screen">
      <section className={`my-requests-hero ${isHelperWorkspace ? "nearby-page-header" : ""}`}>
        <div className={isHelperWorkspace ? "my-requests-hero__main nearby-mobile-header" : "my-requests-hero__main"}>
          {!isHelperWorkspace ? (
            <span className="my-requests-hero__icon">
              <AppstoreOutlined />
            </span>
          ) : null}
          <div>
            {!isHelperWorkspace ? (
              <span className="my-requests-hero__eyebrow">Requester Workspace</span>
            ) : null}
            <Title level={2}>{isHelperWorkspace ? "Shift Requests Near You" : heading}</Title>
            {!isHelperWorkspace ? (
              <Paragraph>Create, filter, and follow the help requests connected to this Olitask workspace.</Paragraph>
            ) : null}
            {isHelperWorkspace ? (
              <div className="nearby-hero-meta">
                <span>Hey {user?.preferredFirstName || user?.firstName || "there"}</span>
                <b>{uniqueTasks.length} {taskFilter} {uniqueTasks.length === 1 ? taskNoun : taskNounPlural} found</b>
                <span>Radius {nearbyRadius}mi</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="my-requests-hero__actions">
          <Button className="my-requests-refresh" icon={<ReloadOutlined />} onClick={refreshTasks}>
            Refresh
          </Button>
          {!isHelperWorkspace ? (
            <Button
              className="my-requests-primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(createPath)}
              type="primary"
            >
              {createButtonLabel}
            </Button>
          ) : null}
        </div>
      </section>

      {isHelperWorkspace ? (
        <section className="nearby-control-panel">
          <div className="nearby-radius-row">
            <label>
              <span>Radius</span>
              <strong>{nearbyRadius} mi</strong>
            </label>
            <input
              type="range"
              min="1"
              max="30"
              value={nearbyRadius}
              onChange={(event) => setNearbyRadius(Number(event.target.value))}
            />
          </div>
          <div className="nearby-view-switch" aria-label="Nearby request view">
            <span className={nearbyView === "LIST" ? "is-active" : ""}>SHOW LIST</span>
            <button
              type="button"
              className={nearbyView === "MAP" ? "is-map" : "is-list"}
              onClick={() => setNearbyView((value) => (value === "MAP" ? "LIST" : "MAP"))}
              aria-label="Toggle map and list view"
            >
              <i />
            </button>
            <span className={nearbyView === "MAP" ? "is-active" : ""}>SHOW MAP</span>
          </div>
          <div className="nearby-location-box">
            <Text>Search a location to see nearby help requests.</Text>
            <div className="nearby-location-input">
              <input
                value={nearbyLocationText}
                onFocus={() => setNearbySearchFocused(true)}
                onBlur={() => window.setTimeout(() => setNearbySearchFocused(false), 160)}
                onChange={(event) => {
                  setNearbyLocationText(event.target.value);
                  setNearbySearchFocused(true);
                }}
                placeholder="City, ZIP, or address"
              />
              {nearbyLocationText ? (
                <button type="button" onClick={() => setNearbyLocationText("")} aria-label="Clear location">
                  x
                </button>
              ) : null}
            </div>
            {nearbySearchFocused ? (
              <div className="nearby-location-suggestions">
                <button type="button" onMouseDown={useCurrentNearbyLocation}>
                  Use Current Location
                </button>
                {nearbySuggestions.map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion.placeId}
                    onMouseDown={() => selectNearbySuggestion(suggestion)}
                  >
                    {suggestion.description}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {!isHelperWorkspace ? (
        <section className="my-requests-shortcuts" aria-label="Create request type">
          {REQUEST_SHORTCUTS.filter((shortcut) => shortcut.tone === (taskMode === "GENERAL" ? "general" : "care")).map((shortcut) => (
            <button
              className={`my-requests-shortcut is-${shortcut.tone}`}
              key={shortcut.title}
              type="button"
              onClick={() => navigate(shortcut.path)}
            >
              <span>{shortcut.icon}</span>
              <strong>{shortcut.title}</strong>
              <small>{shortcut.text}</small>
            </button>
          ))}
        </section>
      ) : null}

      {nearbyView === "LIST" || !isHelperWorkspace ? (
        <section className="my-requests-filter-panel">
          <div className="my-requests-filter-panel__header">
            <Text>Filter {taskMode === "GENERAL" ? "Tasks" : "Shifts"}</Text>
            <strong>{activeFilterLabel}</strong>
          </div>
          <div className="my-requests-filter-panel__chips">
            {FILTER_BUTTONS.map((filter) => {
              const isActive = taskFilter === filter.value;
              return (
                <button
                  key={filter.value}
                  type="button"
                  className={isActive ? "is-active" : ""}
                  onClick={() => setTaskFilter(filter.value)}
                  style={{
                    borderColor: isActive ? filter.activeBackgroundColor : filter.borderColor,
                    background: isActive ? filter.activeBackgroundColor : filter.backgroundColor,
                    color: isActive ? filter.activeTextColor : filter.textColor,
                  }}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {displayError ? (
        <Alert
          showIcon
          type="error"
          message="Unable to load requests"
          description={displayError}
          className="my-requests-alert"
        />
      ) : null}

      <section className="my-requests-list-panel">
        {isHelperWorkspace && nearbyView === "MAP" ? (
          <NearbyRequestsMap
            tasks={uniqueTasks}
            center={nearbyLocation || DEFAULT_NEARBY_LOCATION}
            radius={nearbyRadius}
            onDetails={openTaskDetails}
            mapsReady={locationApiReady}
            locationLabel={nearbyLocationText || "Current Location"}
          />
        ) : displayLoading ? (
          <Skeleton active paragraph={{ rows: 7 }} />
        ) : visibleTasks.length ? (
          <div className="my-requests-list">
            {visibleTasks.map((task, index) =>
              isHelperWorkspace ? (
                <NearbyRequestCard
                  key={taskKey(task, index)}
                  task={task}
                  onDetails={openTaskDetails}
                  interested={taskHasUserInterest(task, userId)}
                  onExpressInterest={openNearbyInterestDialog}
                  onWithdrawInterest={(nextTask) => nearbyTaskAction(nextTask, "withdraw")}
                />
              ) : (
                <RequestCard key={taskKey(task, index)} task={task} onDetails={openTaskDetails} />
              ),
            )}
          </div>
        ) : (
          <div className="my-requests-empty">
            <Empty description={emptyStateTitle}>
              <Paragraph>{emptyStateText}</Paragraph>
              {!isHelperWorkspace ? (
                <Button
                  className="my-requests-primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(createPath)}
                  type="primary"
                >
                  {createEmptyLabel}
                </Button>
              ) : null}
            </Empty>
          </div>
        )}

        {displayTotalElements > (isHelperWorkspace ? NEARBY_LIST_PAGE_SIZE : PAGE_SIZE) && nearbyView === "LIST" ? (
          <div className="my-requests-pagination">
            <Pagination
              current={page}
              onChange={setPage}
              pageSize={isHelperWorkspace ? NEARBY_LIST_PAGE_SIZE : PAGE_SIZE}
              total={Math.max(
                displayTotalElements,
                displayTotalPages * (isHelperWorkspace ? NEARBY_LIST_PAGE_SIZE : PAGE_SIZE),
              )}
              showSizeChanger={false}
            />
          </div>
        ) : null}
      </section>

      <Modal
        title={nearbyInterestCounterOpen ? "Counter Offer" : "Express Interest"}
        open={Boolean(nearbyInterestTask)}
        onCancel={() => {
          if (!nearbyInterestProcessing) resetNearbyInterestDialog();
        }}
        footer={null}
        centered
        className="my-requests-counter-modal my-requests-confirm-modal my-requests-interest-modal"
        destroyOnClose
      >
        <div className="my-requests-interest-modal__sheen" aria-hidden="true" />
        {nearbyInterestTask && nearbyCounterConfig ? (
          nearbyInterestCounterOpen ? (
            <>
              <p className="my-requests-confirm-modal__message">
                {nearbyCounterConfig.message}
              </p>

              {nearbyCounterConfig.allowDurationCounter ? (
                <div className="my-requests-counter-field">
                  <label>How long will it take?</label>
                  <div className="my-requests-counter-duration">
                    <div>
                      <input
                        inputMode="numeric"
                        value={nearbyCounterDurationHours}
                        onChange={(event) => setNearbyCounterDurationHours(event.target.value.replace(/[^0-9]/g, ""))}
                        disabled={Boolean(nearbyInterestProcessing)}
                      />
                      <span>hr</span>
                    </div>
                    <div>
                      <input
                        inputMode="numeric"
                        value={nearbyCounterDurationMinutes}
                        onChange={(event) => setNearbyCounterDurationMinutes(event.target.value.replace(/[^0-9]/g, ""))}
                        disabled={Boolean(nearbyInterestProcessing)}
                      />
                      <span>min</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="my-requests-counter-locked">
                  <strong>Scheduled time</strong>
                  <span>This Home Care & Assisted Living request time cannot be counter offered.</span>
                </div>
              )}

              <div className="my-requests-counter-field">
                <label>{nearbyCounterConfig.amountLabel}</label>
                <div className="my-requests-counter-amount">
                  <input
                    inputMode="decimal"
                    placeholder="Enter Amount"
                    value={nearbyCounterAmount}
                    onChange={(event) => setNearbyCounterAmount(event.target.value.replace(/[^0-9.]/g, ""))}
                    disabled={Boolean(nearbyInterestProcessing)}
                  />
                  <span>{nearbyCounterConfig.amountUnit}</span>
                </div>
              </div>

              <div className="my-requests-counter-field">
                <label>Reason</label>
                <div className="my-requests-counter-reasons">
                  {COUNTER_OFFER_REASONS.map((reason) => (
                    <button
                      type="button"
                      key={reason}
                      className={nearbySelectedCounterReasons.includes(reason) ? "is-selected" : ""}
                      onClick={() => toggleNearbyCounterReason(reason)}
                      disabled={Boolean(nearbyInterestProcessing)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                {nearbySelectedCounterReasons.includes("Other") ? (
                  <textarea
                    value={nearbyCounterReason}
                    onChange={(event) => setNearbyCounterReason(event.target.value)}
                    placeholder="Tell the requester why you are proposing this offer"
                    disabled={Boolean(nearbyInterestProcessing)}
                  />
                ) : null}
              </div>

              <div className="my-requests-confirm-actions">
                <Button
                  className="my-requests-confirm-button is-primary"
                  loading={nearbyInterestProcessing === "counter-offer"}
                  disabled={Boolean(nearbyInterestProcessing)}
                  onClick={submitNearbyCounterOffer}
                >
                  {nearbyInterestProcessing === "counter-offer" ? "Submitting..." : "Submit Counter Offer"}
                </Button>
                <Button
                  className="my-requests-confirm-button is-details"
                  disabled={Boolean(nearbyInterestProcessing)}
                  onClick={() => setNearbyInterestCounterOpen(false)}
                >
                  Back
                </Button>
                <Button
                  className="my-requests-confirm-button is-cancel"
                  disabled={Boolean(nearbyInterestProcessing)}
                  onClick={resetNearbyInterestDialog}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="my-requests-confirm-modal__message">
                Please review the {taskNoun} details first to make sure you are comfortable with the request before expressing interest.
                Do you still want to continue?
              </p>
              <div className="my-requests-confirm-actions">
                <Button
                  className="my-requests-confirm-button is-primary"
                  loading={nearbyInterestProcessing === "interest"}
                  disabled={Boolean(nearbyInterestProcessing)}
                  onClick={submitNearbyInterest}
                >
                  {nearbyInterestProcessing === "interest" ? "Submitting..." : "Confirm Interest"}
                </Button>
                <Button
                  className="my-requests-confirm-button is-counter"
                  disabled={Boolean(nearbyInterestProcessing) || submittedNearbyCounterOffer}
                  onClick={openNearbyCounterOffer}
                >
                  {submittedNearbyCounterOffer ? "Counter Offer Sent" : "Counter Offer"}
                </Button>
                <Button
                  className="my-requests-confirm-button is-details"
                  disabled={Boolean(nearbyInterestProcessing)}
                  onClick={async () => {
                    const task = nearbyInterestTask;
                    resetNearbyInterestDialog();
                    if (task) await openTaskDetails(task);
                  }}
                >
                  Check {taskMode === "GENERAL" ? "Task" : "Shift"} Details
                </Button>
                <Button
                  className="my-requests-confirm-button is-cancel"
                  disabled={Boolean(nearbyInterestProcessing)}
                  onClick={resetNearbyInterestDialog}
                >
                  Cancel
                </Button>
              </div>
            </>
          )
        ) : null}
      </Modal>

      <Drawer
        title={selectedTask ? taskTitle(selectedTask) : "Request details"}
        open={!!selectedTask}
        onClose={closeTaskDetails}
        width="min(780px, 100vw)"
        className="my-requests-drawer"
        extra={
          selectedTask?.id || selectedTask?.taskId ? (
            <Button
              type="primary"
              className="my-requests-manage-button"
              icon={<FileTextOutlined />}
              onClick={() => navigate(detailPath(selectedTask.id || selectedTask.taskId))}
              title={taskMode === "GENERAL" ? "Manage task post" : "Manage shift post"}
              aria-label={taskMode === "GENERAL" ? "Manage task post" : "Manage shift post"}
            >
              {taskMode === "GENERAL" ? "Manage Task Post" : "Manage Shift Post"}
            </Button>
          ) : null
        }
      >
        {selectedTask ? (
          <>
            {drawerError ? (
              <Alert
                showIcon
                type="warning"
                message="Unable to refresh full details"
                description={drawerError}
                className="my-requests-alert"
              />
            ) : null}
            <TaskDetailsDrawerContent
              task={selectedTask}
              loading={drawerLoading}
              isHelperWorkspace={isHelperWorkspace}
              userId={userId}
              onPhotoPress={openPhotoGallery}
              onActorOpen={setSelectedActor}
              onTaskUpdated={refreshSelectedTask}
              selectedWorkspace={selectedWorkspace}
              selectedWorkspaceId={selectedWorkspaceId}
              showActions
              showCounterOfferDetails
              onManageTask={() => navigate(detailPath(selectedTask.id || selectedTask.taskId))}
            />
          </>
        ) : null}
      </Drawer>

      <PhotoGalleryModal
        gallery={photoGallery}
        onClose={closePhotoGallery}
        onShift={shiftPhotoGallery}
        onSelect={(index) => setPhotoGallery((current) => ({ ...current, index }))}
      />
      <ActorDetailsDrawer actor={selectedActor} onClose={() => setSelectedActor(null)} />
    </div>
  );
}

export function TaskDetailsPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, selectedWorkspaceUse } = useWorkspace();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [photoGallery, setPhotoGallery] = useState({ photos: [], index: 0 });
  const [selectedActor, setSelectedActor] = useState(null);

  const isHelperWorkspace = isHelperUse(selectedWorkspaceUse);
  const isGeneralTaskRoute = location.pathname.startsWith("/hire-care-giver/general-tasks");
  const noun = isGeneralTaskRoute ? "Task" : "Shift";
  const userId = userIdOf(user);
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(paths.myTasks);
  };

  const loadTask = useCallback(async ({ silent = false } = {}) => {
    if (!taskId) {
      setError(`${noun} ID is missing.`);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError("");
      const response = await apiClient.get(`/olitask-service/api/v1/tasks/${taskId}`);
      const details = response?.data?.data || response?.data || {};
      setTask(details);
    } catch (err) {
      setError(
          err?.response?.data?.message ||
          err?.message ||
          `Unable to load ${noun.toLowerCase()} details.`,
      );
    } finally {
      setLoading(false);
    }
  }, [noun, taskId]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  useTaskStatusPolling({
    taskId,
    task,
    refreshTask: loadTask,
    enabled: Boolean(task),
  });

  const openPhotoGallery = (photos, index = 0) => {
    setPhotoGallery({ photos, index });
  };

  const closePhotoGallery = () => {
    setPhotoGallery({ photos: [], index: 0 });
  };

  const shiftPhotoGallery = (direction) => {
    setPhotoGallery((current) => {
      if (!current.photos.length) return current;
      const nextIndex =
        (current.index + direction + current.photos.length) % current.photos.length;
      return { ...current, index: nextIndex };
    });
  };

  return (
    <div className="my-requests-screen my-task-details-page">
      <section className="my-task-details-page__bar">
        <button
          type="button"
          className="my-task-details-page__back"
          onClick={goBack}
          aria-label="Go back"
        >
          <LeftOutlined />
        </button>
        <div>
          <Title level={2}>{task ? taskTitle(task) : `${noun} Details`}</Title>
        </div>
      </section>

      {error ? (
        <Alert
          showIcon
          type="error"
          message={`Unable to load ${noun.toLowerCase()} details`}
          description={error}
          className="my-requests-alert"
        />
      ) : null}

      {loading && !task ? (
        <div className="my-task-details-page__loading">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : null}

      {task ? (
        <>
          <TaskDetailsDrawerContent
            task={task}
            loading={loading}
            isHelperWorkspace={isHelperWorkspace}
            userId={userId}
            onPhotoPress={openPhotoGallery}
            onActorOpen={setSelectedActor}
            onTaskUpdated={loadTask}
            selectedWorkspace={selectedWorkspace}
            selectedWorkspaceId={selectedWorkspaceId}
            showHero={false}
            showActions
          />
          <ActorDetailsDrawer actor={selectedActor} onClose={() => setSelectedActor(null)} />
        </>
      ) : null}

      <PhotoGalleryModal
        gallery={photoGallery}
        onClose={closePhotoGallery}
        onShift={shiftPhotoGallery}
        onSelect={(index) => setPhotoGallery((current) => ({ ...current, index }))}
      />
    </div>
  );
}

const STAGE_PAGE_CONTENT = {
  heading: {
    title: "Heading To Requester",
    eyebrow: "Navigation",
    text: "Use this screen while traveling to the shift location. Confirm arrival when you reach the requester.",
    tone: "blue",
  },
  navigation: {
    title: "Care Giver On The Way",
    eyebrow: "Live Progress",
    text: "Track the accepted Care Giver while they are on the way. The full shift address is visible for active shift actors.",
    tone: "blue",
  },
  progress: {
    title: "Shift In Progress",
    eyebrow: "Active Shift",
    text: "Use this screen while the work is happening. Care Givers can complete the shift from here.",
    tone: "green",
  },
  completed: {
    title: "Shift Completed",
    eyebrow: "Wrap Up",
    text: "This shift has been marked as completed. Leave a review and add a tip when applicable.",
    tone: "gold",
  },
  cancel: {
    title: "Cancel Shift Post",
    eyebrow: "Cancellation",
    text: "Explain why this shift is being cancelled before submitting.",
    tone: "red",
  },
};

const stageRouteKey = (stage) => {
  const normalized = String(stage || "").toLowerCase();
  if (["heading", "on-the-way", "go-to-navigation"].includes(normalized)) return "heading";
  if (["navigation", "track", "helper-on-the-way"].includes(normalized)) return "navigation";
  if (["progress", "in-progress", "task-in-progress"].includes(normalized)) return "progress";
  if (["completed", "complete", "task-completed"].includes(normalized)) return "completed";
  if (["cancel", "cancel-task"].includes(normalized)) return "cancel";
  return "progress";
};

function TaskElapsedTimer({ startedAt, stoppedAt }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (stoppedAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [stoppedAt]);

  const start = Date.parse(startedAt || "");
  const stop = Date.parse(stoppedAt || "");
  const end = Number.isFinite(stop) ? stop : now;
  const elapsed = Number.isFinite(start) ? Math.max(0, Math.floor((end - start) / 1000)) : 0;
  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  return (
    <div className="my-task-stage-timer">
      <span>{String(hours).padStart(2, "0")}</span>
      <b>:</b>
      <span>{String(minutes).padStart(2, "0")}</span>
      <b>:</b>
      <span>{String(seconds).padStart(2, "0")}</span>
    </div>
  );
}

const routeLatLng = (coords) =>
  coords && Number.isFinite(Number(coords.latitude)) && Number.isFinite(Number(coords.longitude))
    ? { lat: Number(coords.latitude), lng: Number(coords.longitude) }
    : null;

const routePointKey = (point) =>
  point ? `${Number(point.lat).toFixed(6)},${Number(point.lng).toFixed(6)}` : "";

const coordinateFrom = (...sources) => {
  for (const source of sources) {
    const latitude = numberValue(source?.latitude, source?.lat, source?.coords?.latitude, source?.coordinate?.latitude);
    const longitude = numberValue(
      source?.longitude,
      source?.lng,
      source?.lon,
      source?.coords?.longitude,
      source?.coordinate?.longitude,
    );
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) return { latitude, longitude };
  }
  return null;
};

const pointsAreClose = (left, right) => {
  if (!left || !right) return false;
  return Math.abs(Number(left.lat) - Number(right.lat)) < 0.00001 && Math.abs(Number(left.lng) - Number(right.lng)) < 0.00001;
};

const approximateRouteDestination = (destination, showExactDestination) => {
  if (!destination || showExactDestination) return destination;
  const seed = Math.abs(Math.sin(destination.latitude * 999 + destination.longitude * 777));
  const angle = (seed % 1) * Math.PI * 2;
  const distance = 40 + (seed % 1) * 60;
  const dLat = (distance / 111320) * Math.cos(angle);
  const dLng = (distance / (111320 * Math.cos((destination.latitude * Math.PI) / 180))) * Math.sin(angle);
  return {
    latitude: destination.latitude + dLat,
    longitude: destination.longitude + dLng,
  };
};

const buildWebNavigationMapSnapShot = ({ helper, destination, distance, duration, address }) => {
  if (!helper || !destination) return null;
  const helperPoint = {
    latitude: Number(helper.latitude),
    longitude: Number(helper.longitude),
  };
  const destinationPoint = {
    latitude: Number(destination.latitude),
    longitude: Number(destination.longitude),
  };
  if (
    !Number.isFinite(helperPoint.latitude) ||
    !Number.isFinite(helperPoint.longitude) ||
    !Number.isFinite(destinationPoint.latitude) ||
    !Number.isFinite(destinationPoint.longitude)
  ) {
    return null;
  }
  const latitudeDelta = Math.max(Math.abs(helperPoint.latitude - destinationPoint.latitude) * 2, 0.01);
  const longitudeDelta = Math.max(Math.abs(helperPoint.longitude - destinationPoint.longitude) * 2, 0.01);
  return {
    version: 1,
    capturedAt: new Date().toISOString(),
    map: {
      provider: "google",
      showsCompass: true,
      initialRegion: {
        latitude: (helperPoint.latitude + destinationPoint.latitude) / 2,
        longitude: (helperPoint.longitude + destinationPoint.longitude) / 2,
        latitudeDelta,
        longitudeDelta,
      },
    },
    markers: {
      helper: helperPoint,
      requester: destinationPoint,
    },
    paths: {
      traveledPath: [],
      remainingPath: [],
    },
    extras: {
      ead: distance || "—",
      eat: duration || "—",
      address: address || "—",
    },
    route: {
      navViewMode: "NORTH_UP",
      camera: null,
    },
  };
};

function TaskRouteMap({
  apiKey,
  origin,
  destination,
  title,
  addressLabel,
  showExactDestination,
  onRouteInfo,
  taskId,
  liveUserId,
  liveUserIds,
  onLiveAddress,
  onLiveStatus,
}) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const routeOverlaysRef = useRef([]);
  const helperMarkerRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const liveOriginRef = useRef(origin);
  const routeRequestKeyRef = useRef("");
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [routeInfo, setRouteInfo] = useState({ distance: "", duration: "" });
  const [liveOrigin, setLiveOrigin] = useState(origin);
  const liveUserIdCandidates = useMemo(
    () => uniqueTextValues(liveUserIds || [], liveUserId),
    [liveUserId, liveUserIds],
  );
  const routedDestination = useMemo(
    () => approximateRouteDestination(destination, showExactDestination),
    [destination, showExactDestination],
  );
  const destinationKey = routePointKey(routeLatLng(destination));
  const routedDestinationKey = routePointKey(routeLatLng(routedDestination));

  useEffect(() => {
    setLiveOrigin(origin);
    liveOriginRef.current = origin;
  }, [origin]);

  useEffect(() => {
    setReady(false);
    if (!apiKey) return undefined;
    let cancelled = false;
    ensureGoogleMapsPlacesLoaded(apiKey)
      .then((loaded) => {
        if (!cancelled) setReady(Boolean(loaded));
      })
      .catch(() => {
        if (!cancelled) setReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  const clearRouteMap = useCallback(() => {
    overlaysRef.current.forEach((overlay) => overlay?.setMap?.(null));
    overlaysRef.current = [];
    routeOverlaysRef.current.forEach((overlay) => overlay?.setMap?.(null));
    routeOverlaysRef.current = [];
    helperMarkerRef.current?.setMap?.(null);
    helperMarkerRef.current = null;
    directionsRendererRef.current?.setMap?.(null);
    directionsRendererRef.current = null;
  }, []);

  const clearRouteOverlays = useCallback(() => {
    routeOverlaysRef.current.forEach((overlay) => overlay?.setMap?.(null));
    routeOverlaysRef.current = [];
    directionsRendererRef.current?.setMap?.(null);
    directionsRendererRef.current = null;
  }, []);

  useEffect(() => {
    if (!taskId || !liveUserIdCandidates.length) return undefined;
    let cancelled = false;
    let timeoutId = null;
    let inFlight = false;

    const poll = async () => {
      if (cancelled) return;
      if (inFlight) {
        timeoutId = window.setTimeout(poll, 2000);
        return;
      }

      inFlight = true;
      try {
        let data = null;
        for (const candidateId of liveUserIdCandidates) {
          try {
            const response = await apiClient.get(
              `/olitask-service/api/v1/location/task/${taskId}/user/${candidateId}/navigation/latest`,
              { headers: { "O-SKIP-WORKSPACE-CONTEXT": "1" } },
            );
            const payload = response?.data?.data || response?.data || {};
            const snap = payload?.mapSnapShot || payload?.mapSnapshot || {};
            const helperCoords = coordinateFrom(
              payload,
              payload?.location,
              payload?.currentLocation,
              payload?.helperLocation,
              payload?.pingLocation,
              payload?.latestLocation,
              snap?.markers?.helper,
            );
            if (
              helperCoords ||
              firstText(payload?.ead, snap?.extras?.ead, payload?.eat, snap?.extras?.eat, payload?.address, snap?.extras?.address)
            ) {
              data = payload;
              break;
            }
          } catch {
            // Try the next helper id shape. Mobile uses task.helper.id, but older payloads vary.
          }
        }
        if (cancelled) return;
        if (!data) return;
        const snap = data?.mapSnapShot || data?.mapSnapshot || {};
        const helperCoords = coordinateFrom(
          data,
          data?.location,
          data?.currentLocation,
          data?.helperLocation,
          data?.pingLocation,
          data?.latestLocation,
          snap?.markers?.helper,
        );
        if (helperCoords) {
          liveOriginRef.current = helperCoords;
          setLiveOrigin((current) => {
            const currentPoint = routeLatLng(current);
            const nextPoint = routeLatLng(helperCoords);
            return pointsAreClose(currentPoint, nextPoint) ? current : helperCoords;
          });
        }

        const distance = firstText(data?.ead, snap?.extras?.ead);
        const duration = firstText(data?.eat, snap?.extras?.eat);
        if (distance || duration) {
          const nextInfo = { distance, duration };
          setRouteInfo(nextInfo);
          onRouteInfo?.(nextInfo);
        }

        const address = firstText(data?.address, snap?.extras?.address);
        if (address) onLiveAddress?.(address);
        if (data?.taskStatus) onLiveStatus?.(normalizeStatus(data.taskStatus));
      } catch {
        // Keep polling; transient missing pings should not break the map.
      } finally {
        inFlight = false;
        if (!cancelled) timeoutId = window.setTimeout(poll, 2000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [liveUserIdCandidates, onLiveAddress, onLiveStatus, onRouteInfo, taskId]);

  useEffect(() => {
    const initialOrigin = liveOriginRef.current || origin;
    if (!ready || !mapNodeRef.current || !initialOrigin || !routedDestination || !window.google?.maps) return undefined;

    const google = window.google;
    const originPoint = routeLatLng(initialOrigin);
    const destinationPoint = routeLatLng(routedDestination);
    const exactDestinationPoint = routeLatLng(destination);
    if (!originPoint || !destinationPoint) return undefined;

    const center = {
      lat: (originPoint.lat + destinationPoint.lat) / 2,
      lng: (originPoint.lng + destinationPoint.lng) / 2,
    };

    mapRef.current = new google.maps.Map(mapNodeRef.current, {
      center,
      zoom: 14,
      heading: 0,
      tilt: 0,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    });

    helperMarkerRef.current = new google.maps.Marker({
      map: mapRef.current,
      position: originPoint,
      title: "Care Giver (live)",
      zIndex: 20,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: OLIVEEB_COLORS.oliveebGreen,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 4,
      },
    });

    if (showExactDestination && exactDestinationPoint) {
      overlaysRef.current.push(new google.maps.Marker({
        map: mapRef.current,
        position: exactDestinationPoint,
        title: title || "Shift Location",
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: "#16a34a",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
      }));
    } else {
      overlaysRef.current.push(new google.maps.Circle({
        map: mapRef.current,
        center: destinationPoint,
        radius: 100,
        strokeColor: "rgba(17,149,12,0.35)",
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: "rgba(17,149,12,0.12)",
        fillOpacity: 1,
      }));
    }

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(originPoint);
    bounds.extend(destinationPoint);
    mapRef.current.fitBounds(bounds, 72);

    return () => {
      clearRouteMap();
      mapRef.current = null;
      routeRequestKeyRef.current = "";
    };
  }, [clearRouteMap, destinationKey, ready, routedDestinationKey, showExactDestination, title]);

  useEffect(() => {
    const point = routeLatLng(liveOrigin);
    if (!point || !helperMarkerRef.current) return;
    helperMarkerRef.current.setPosition(point);
  }, [liveOrigin]);

  useEffect(() => {
    const currentOrigin = origin || liveOrigin;
    if (!ready || !mapRef.current || !currentOrigin || !routedDestination || !window.google?.maps) return undefined;

    const google = window.google;
    const originPoint = routeLatLng(currentOrigin);
    const destinationPoint = routeLatLng(routedDestination);
    if (!originPoint || !destinationPoint) return undefined;
    if (pointsAreClose(originPoint, destinationPoint)) return undefined;

    const nextRouteRequestKey = [
      originPoint.lat.toFixed(6),
      originPoint.lng.toFixed(6),
      destinationPoint.lat.toFixed(6),
      destinationPoint.lng.toFixed(6),
    ].join("|");
    if (routeRequestKeyRef.current) return undefined;
    routeRequestKeyRef.current = nextRouteRequestKey;

    clearRouteOverlays();
    setLoading(true);

    const renderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      preserveViewport: true,
      polylineOptions: {
        strokeColor: "#3dd102",
        strokeOpacity: 0.95,
        strokeWeight: 7,
      },
    });
    renderer.setMap(mapRef.current);
    directionsRendererRef.current = renderer;

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: originPoint,
        destination: destinationPoint,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        setLoading(false);
        if (status !== "OK" || !result?.routes?.[0]) {
          routeRequestKeyRef.current = "";
          return;
        }
        renderer.setDirections(result);
        const leg = result.routes[0]?.legs?.[0] || {};
        const nextInfo = {
          distance: leg.distance?.text || "",
          duration: leg.duration?.text || "",
        };
        setRouteInfo(nextInfo);
        onRouteInfo?.(nextInfo);
      },
    );

    return undefined;
  }, [clearRouteOverlays, liveOrigin, onRouteInfo, origin, ready, routedDestinationKey]);

  const zoomBy = (factor) => {
    const map = mapRef.current;
    if (!map) return;
    const currentZoom = map.getZoom() || 14;
    map.setZoom(Math.min(Math.max(currentZoom + factor, 3), 21));
  };

  const fitRoute = () => {
    const directions = directionsRendererRef.current?.getDirections?.();
    const bounds = directions?.routes?.[0]?.bounds;
    if (bounds) mapRef.current?.fitBounds(bounds, 72);
  };

  const recenterHelper = () => {
    const point = routeLatLng(liveOriginRef.current);
    if (!point || !mapRef.current) return;
    mapRef.current.panTo(point);
    mapRef.current.setZoom(Math.max(mapRef.current.getZoom() || 15, 17));
  };

  const pointNorth = () => {
    const map = mapRef.current;
    if (!map) return;
    map.setHeading?.(0);
    map.setTilt?.(0);
  };

  return (
    <div className="my-task-route-map-shell">
      <div ref={mapNodeRef} className="my-task-route-map-canvas" />
      {!apiKey ? (
        <div className="my-task-route-map-message">Google Maps is not ready yet.</div>
      ) : null}
      {loading ? (
        <div className="my-task-route-map-loader">
          <Skeleton.Button active size="small" />
        </div>
      ) : null}
      {(routeInfo.distance || routeInfo.duration) ? (
        <div className="my-task-route-eta-card">
          <div>
            <CompassOutlined />
            <strong>Trip Estimate</strong>
          </div>
          <span>{routeInfo.distance}</span>
          <span>{routeInfo.duration}</span>
        </div>
      ) : null}
      <div className="my-task-route-map-controls">
        <button type="button" onClick={fitRoute}>
          <CompassOutlined />
          <span>Fit</span>
        </button>
        <button type="button" onClick={pointNorth}>
          <CompassOutlined />
          <span>North</span>
        </button>
        <button type="button" onClick={recenterHelper}>
          <EnvironmentOutlined />
          <span>Care Giver</span>
        </button>
        <button type="button" onClick={() => zoomBy(1)}>
          <PlusOutlined />
          <span>Zoom</span>
        </button>
        <button type="button" onClick={() => zoomBy(-1)}>
          <span aria-hidden="true">-</span>
          <span>Out</span>
        </button>
      </div>
    </div>
  );
}

const milesFromDistanceText = (value) => {
  const text = String(value || "").toLowerCase();
  const amount = Number.parseFloat(text.replace(/,/g, ""));
  if (!Number.isFinite(amount)) return null;
  if (text.includes(" ft")) return amount / 5280;
  if (text.includes(" km")) return amount * 0.621371;
  if (text.includes(" m") && !text.includes("mi")) return amount * 0.000621371;
  return amount;
};

function TaskHeadingInfoCard({
  actor,
  actorType = "helper",
  taskId,
  eta,
  distance,
  currentStreet,
  taskStatus,
  canConfirmArrived,
  canStartTask,
  canCancel,
  processingKey,
  onConfirmArrived,
  onStartTask,
  onCancelTask,
  onDetails,
  onActorOpen,
  cancelLabel = "Cancel Shift Post",
}) {
  const { user: authUser } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [resolvedPhoto, setResolvedPhoto] = useState("");
  const [loadedActor, setLoadedActor] = useState(null);
  const effectiveActor = loadedActor || actor || {};
  const effectiveActorId = actorProfileId(actor);
  const currentUserId = userIdOf(authUser);
  const actorIds = [
    actorProfileId(actor),
    actorProfileId(effectiveActor),
    actor?.id,
    actor?.userId,
    actor?.userProfileId,
    effectiveActor?.id,
    effectiveActor?.userId,
    effectiveActor?.userProfileId,
  ].filter(Boolean).map(String);
  const isCurrentUserActor = Boolean(currentUserId && actorIds.includes(String(currentUserId)));

  useEffect(() => {
    let cancelled = false;
    const loadActor = async () => {
      setLoadedActor(null);
      if (!effectiveActorId) return;
      const loaded = await fetchActorProfileByType(effectiveActorId, actorType);
      if (!cancelled) setLoadedActor(loaded || null);
    };
    loadActor();
    return () => {
      cancelled = true;
    };
  }, [actorType, effectiveActorId]);

  const actorOrganization =
    actorType === "requester"
      ? effectiveActor?.requesterOrganization || effectiveActor?.organization
      : effectiveActor?.helperOrganization || effectiveActor?.organization;
  const actorOrganizationId =
    actorType === "requester"
      ? effectiveActor?.requesterOrganizationId || actorOrganization?.id || actorOrganization?.organizationId
      : effectiveActor?.helperOrganizationId || actorOrganization?.id || actorOrganization?.organizationId;
  const orgName = organizationName(actorOrganization);
  const personDisplayName = personName(effectiveActor);
  const actorDisplayName = actorOrganizationId
    ? [orgName || (actorType === "requester" ? "Business requester" : "Business Care Giver"), personDisplayName ? `(${personDisplayName})` : ""]
        .filter(Boolean)
        .join(" ")
    : personDisplayName || (actorType === "requester" ? "Requester" : "Care Giver");
  const photoSource = useMemo(() => {
    const primary = profilePhotoSource(effectiveActor);
    if (primary.url || primary.fileId) return primary;
    return isCurrentUserActor ? profilePhotoSource(authUser) : primary;
  }, [authUser, effectiveActor, isCurrentUserActor]);

  useEffect(() => {
    let cancelled = false;
    const loadPhoto = async () => {
      if (!photoSource.fileId) {
        setResolvedPhoto(photoSource.url || "");
        return;
      }
      const signedUrl = await resolveSignedPhotoUrl(photoSource.fileId);
      if (!cancelled) setResolvedPhoto(signedUrl || photoSource.url || "");
    };
    loadPhoto();
    return () => {
      cancelled = true;
    };
  }, [photoSource.fileId, photoSource.url]);

  return (
    <div className={`my-task-heading-info-card${collapsed ? " is-collapsed" : ""}`}>
      <button
        type="button"
        className="my-task-heading-info-card__handle"
        onClick={() => setCollapsed((value) => !value)}
        aria-label={collapsed ? "Expand route details" : "Collapse route details"}
      >
        {collapsed ? "▲" : "▼"}
      </button>

      <div className="my-task-heading-info-card__banner">
        Live location tracking
      </div>

      {!collapsed ? (
        <>
          <button
            type="button"
            className="my-task-heading-info-card__actor"
            onClick={() => onActorOpen?.(effectiveActor)}
          >
            <span className="my-task-heading-info-card__avatar">
              {resolvedPhoto ? <img src={resolvedPhoto} alt="" /> : <UserOutlined />}
            </span>
            <span className="my-task-heading-info-card__name">{actorDisplayName}</span>
            {actorOrganizationId ? (
              <span className="my-task-heading-info-card__hint">
                {actorType === "requester" ? "Business requester" : "Business Care Giver"}
              </span>
            ) : null}
          </button>

          <div className="my-task-heading-info-card__info">
            {eta ? <span>🕒 {eta} away</span> : null}
            {distance ? <span>📏 {distance} away</span> : null}
            {currentStreet ? <span>📍 {currentStreet}</span> : null}
            {taskStatus ? <span>✅ {TASK_STAGE_LABELS[taskStatus] || titleCase(taskStatus)}</span> : null}
          </div>

          <div className="my-task-heading-info-card__buttons">
            <Button className="my-task-heading-info-card__button is-details" onClick={onDetails}>
              Details
            </Button>
            {canConfirmArrived ? (
              <Button
                type="primary"
                className="my-task-heading-info-card__button is-primary"
                loading={processingKey === "arrived"}
                onClick={onConfirmArrived}
              >
                Arrived
              </Button>
            ) : null}
            {canStartTask ? (
              <Button
                type="primary"
                className="my-task-heading-info-card__button is-primary"
                loading={processingKey === "start"}
                onClick={onStartTask}
              >
                Start
              </Button>
            ) : null}
            {canCancel ? (
              <Button className="my-task-heading-info-card__button is-cancel" onClick={onCancelTask}>
                {cancelLabel}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function TaskStagePage() {
  const { taskId, stage } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, selectedWorkspaceUse } = useWorkspace();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingKey, setProcessingKey] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [tipAmount, setTipAmount] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(4);
  const [existingReviews, setExistingReviews] = useState([]);
  const [submittedReview, setSubmittedReview] = useState(false);
  const [submittedTip, setSubmittedTip] = useState(false);
  const [photoGallery, setPhotoGallery] = useState({ photos: [], index: 0 });
  const [selectedActor, setSelectedActor] = useState(null);
  const [routeInfo, setRouteInfo] = useState({ distance: "", duration: "" });
  const [routeLiveAddress, setRouteLiveAddress] = useState("");
  const [routeOrigin, setRouteOrigin] = useState(null);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      process.env.VITE_GOOGLE_MAPS_API_KEY ||
      "",
  );
  const routeInfoRef = useRef(routeInfo);
  const routeAddressRef = useRef(routeLiveAddress);

  const stageKey = stageRouteKey(stage);
  const stageContent = STAGE_PAGE_CONTENT[stageKey] || STAGE_PAGE_CONTENT.progress;
  const isGeneralTaskRoute = location.pathname.startsWith("/hire-care-giver/general-tasks");
  const noun = isGeneralTaskRoute ? "task" : "shift";
  const titleNoun = isGeneralTaskRoute ? "Task" : "Shift";
  const stageDisplay = isGeneralTaskRoute
    ? {
        ...stageContent,
        title: stageContent.title.replace(/Shift Post/g, "Task Post").replace(/Shift/g, "Task"),
        eyebrow: stageContent.eyebrow.replace(/Shift/g, "Task"),
        text: stageContent.text.replace(/shift/g, "task").replace(/Shift/g, "Task"),
      }
    : stageContent;
  const baseTaskPath = isGeneralTaskRoute ? "/hire-care-giver/general-tasks/posts" : "/hire-care-giver/shift-filler/posts";
  const taskPath = (suffix = "") => `${baseTaskPath}/${taskId}${suffix ? `/${suffix}` : ""}`;
  const userId = userIdOf(user);
  const isHelperWorkspace = isHelperUse(selectedWorkspaceUse);
  const status = normalizeStatus(task?.status);
  const taskHelperWorkspaceId = firstText(task?.helperWorkspaceId, workspaceIdOf(task?.helperWorkspace));
  const isTaskHelperWorkspace = Boolean(selectedWorkspaceId && taskHelperWorkspaceId && selectedWorkspaceId === taskHelperWorkspaceId);
  const isTaskHelper = actorMatchesUser(task?.helper, userId) || isTaskHelperWorkspace;
  const isTaskRequester = actorMatchesUser(task?.requester, userId);
  const acceptedHelper = isTaskHelper || actorMatchesUser(task?.helper, userId) || isTaskHelperWorkspace;
  const canActAsAcceptedHelper = acceptedHelper && (isHelperWorkspace || isTaskHelperWorkspace);
  const canHelperNavigate = canActAsAcceptedHelper && ["ACCEPTED", "NAVIGATING", "ARRIVED"].includes(status);
  const canHelperArrive = canActAsAcceptedHelper && status === "NAVIGATING";
  const canHelperStart = canActAsAcceptedHelper && status === "ARRIVED";
  const canHelperComplete = canActAsAcceptedHelper && status === "IN_PROGRESS";
  const canCancel =
    (isTaskHelper || isTaskRequester) &&
    !["COMPLETED", "CANCELLED", "EXPIRED"].includes(status) &&
    (status !== "NEW" || isTaskRequester);
  const hasHelperReview = existingReviews.some((review) => normalizeStatus(review?.source) === "HELPER");
  const hasRequesterReview = existingReviews.some((review) => normalizeStatus(review?.source) === "REQUESTER");
  const reviewSource = isHelperWorkspace ? "HELPER" : "REQUESTER";
  const routeScreenTitle =
    stageKey === "heading"
      ? "Heading To Requester"
        : stageKey === "navigation"
          ? "Care Giver On The Way"
        : stageDisplay.title;
  const routePrimaryName =
    stageKey === "heading"
      ? personName(task?.requester) || organizationName(task?.requesterWorkspace?.organization, task?.requesterWorkspace) || "Requester"
      : personName(task?.helper) || organizationName(task?.helperWorkspace?.organization, task?.helperWorkspace) || "Care Giver";
  const routeSecondaryName =
    stageKey === "heading"
      ? personName(task?.helper) || organizationName(task?.helperWorkspace?.organization, task?.helperWorkspace) || "Care Giver"
      : personName(task?.requester) || organizationName(task?.requesterWorkspace?.organization, task?.requesterWorkspace) || "Requester";
  const canTip =
    !submittedTip &&
    isTaskRequester &&
    status === "COMPLETED" &&
    !task?.taskCharges?.tipAmount;
  const canReview = Boolean(
    !submittedReview &&
    task &&
    status === "COMPLETED" &&
    (isTaskHelper || isTaskRequester) &&
    (reviewSource === "HELPER" ? !hasHelperReview : !hasRequesterReview)
  );
  const destinationCoords = useMemo(() => taskCoordinates(task), [task]);
  const showFullRouteAddress = ["ACCEPTED", "NAVIGATING", "ARRIVED", "IN_PROGRESS", "COMPLETED"].includes(status);
  const routeAddressLabel = showFullRouteAddress
    ? fullLocationText(task) || locationText(task) || `${titleNoun} Location`
    : locationAreaText(task) || `${titleNoun} Location`;
  const routeDistanceMiles = milesFromDistanceText(routeInfo.distance);
  const routeActor = stageKey === "navigation" ? task?.helper : task?.requester;
  const routeActorType = stageKey === "navigation" ? "helper" : "requester";
  const routeLiveUserIds = useMemo(
    () =>
      uniqueTextValues(
        task?.helper?.id,
        task?.helper?.userId,
        task?.helper?.userProfileId,
        task?.helper?.profileId,
        task?.helper?.helperId,
        task?.helper?.user?.id,
        task?.helper?.user?.userId,
        task?.helper?.profile?.id,
        task?.helper?.userProfile?.id,
        actorProfileId(task?.helper),
      ),
    [task?.helper],
  );
  const routeLiveUserId = routeLiveUserIds[0] || "";
  const canRouteConfirmArrived = canHelperArrive && (routeDistanceMiles == null || routeDistanceMiles <= 0.1);
  const canRouteStartTask = canHelperStart && (routeDistanceMiles == null || routeDistanceMiles <= 0.1);

  useEffect(() => {
    routeInfoRef.current = routeInfo;
  }, [routeInfo]);

  useEffect(() => {
    routeAddressRef.current = routeLiveAddress || routeAddressLabel;
  }, [routeAddressLabel, routeLiveAddress]);

  const loadTask = useCallback(async ({ silent = false } = {}) => {
    if (!taskId) {
      setError(`${titleNoun} ID is missing.`);
      setLoading(false);
      return;
    }
    try {
      if (!silent) setLoading(true);
      setError("");
      const response = await apiClient.get(`/olitask-service/api/v1/tasks/${taskId}`);
      const details = response?.data?.data || response?.data || {};
      setTask(details);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Unable to load ${noun} details.`);
    } finally {
      setLoading(false);
    }
  }, [noun, taskId, titleNoun]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  useEffect(() => {
    if (googleMapsApiKey) return undefined;
    let cancelled = false;
    const loadAppConfig = async () => {
      try {
        const response = await apiClient.get("/olitask-service/api/v1/app-config", {
          headers: { "O-SKIP-WORKSPACE-CONTEXT": "1" },
        });
        if (cancelled) return;
        setGoogleMapsApiKey(googleMapsKeyFromConfigResponse(response));
      } catch {
        if (!cancelled) setGoogleMapsApiKey("");
      }
    };
    loadAppConfig();
    return () => {
      cancelled = true;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!task || !["heading", "navigation"].includes(stageKey)) return undefined;
    let cancelled = false;
    const loadRouteOrigin = async () => {
      const location = await currentLocationPing(userId, destinationCoords);
      if (!cancelled) {
        setRouteOrigin(location ? { latitude: location.latitude, longitude: location.longitude } : destinationCoords);
      }
    };
    loadRouteOrigin();
    return () => {
      cancelled = true;
    };
  }, [destinationCoords?.latitude, destinationCoords?.longitude, stageKey, task, userId]);

  useEffect(() => {
    const canTransmit =
      taskId &&
      task &&
      destinationCoords &&
      canActAsAcceptedHelper &&
      status === "NAVIGATING" &&
      ["heading", "navigation"].includes(stageKey);
    if (!canTransmit) return undefined;

    let cancelled = false;
    let latestCoords = null;
    let watchId = null;
    let intervalId = null;
    let posting = false;

    const postNavigationPing = async () => {
      if (cancelled || posting || !latestCoords) return;
      const snapshot = buildWebNavigationMapSnapShot({
        helper: latestCoords,
        destination: destinationCoords,
        distance: routeInfoRef.current.distance,
        duration: routeInfoRef.current.duration,
        address: routeAddressRef.current,
      });
      if (!snapshot) return;

      posting = true;
      try {
        const clientId = localStorage.getItem("clientId") || null;
        await apiClient.post(`/olitask-service/api/v1/location/task/${taskId}/navigation/ping`, {
          location: {
            latitude: latestCoords.latitude,
            longitude: latestCoords.longitude,
            source: "HELPER",
            clientId,
            actorId: userId || null,
            pingAt: null,
          },
          source: "HELPER",
          taskStatus: status,
          sourceApp: O_CLIENT_APP,
          clientId,
          address: routeAddressRef.current || "—",
          eat: routeInfoRef.current.duration || "—",
          ead: routeInfoRef.current.distance || "—",
          mapSnapShot: snapshot,
        });
      } catch {
        // Keep trying while the care giver remains on the navigation screen.
      } finally {
        posting = false;
      }
    };

    const updatePosition = (position) => {
      latestCoords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      setRouteOrigin(latestCoords);
      void postNavigationPing();
    };

    if (typeof navigator !== "undefined" && navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        updatePosition,
        () => {},
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 2000 },
      );
      navigator.geolocation.getCurrentPosition(
        updatePosition,
        () => {},
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
      );
    }

    intervalId = window.setInterval(() => {
      void postNavigationPing();
    }, 2000);

    return () => {
      cancelled = true;
      if (watchId !== null && navigator.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [canActAsAcceptedHelper, destinationCoords, stageKey, status, task, taskId, userId]);

  useTaskStatusPolling({
    taskId,
    task,
    refreshTask: loadTask,
    enabled: Boolean(task),
  });

  useTaskDirectPolling({
    taskId,
    refreshTask: loadTask,
    enabled: Boolean(task && ["heading", "navigation", "progress"].includes(stageKey)),
    intervalMs: 3000,
  });

  useEffect(() => {
    if (stageKey === "progress" && status === "COMPLETED") {
      navigate(`${baseTaskPath}/${taskId}/completed`, { replace: true });
    }
  }, [baseTaskPath, navigate, stageKey, status, taskId]);

  useEffect(() => {
    let cancelled = false;
    const loadReviews = async () => {
      if (!taskId || status !== "COMPLETED") {
        setExistingReviews([]);
        return;
      }
      try {
        const response = await apiClient.get(`/olitask-service/api/v1/task-reviews/task/${taskId}`);
        if (!cancelled) {
          setExistingReviews(Array.isArray(response?.data) ? response.data : []);
        }
      } catch {
        if (!cancelled) setExistingReviews([]);
      }
    };
    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [status, taskId]);

  const stagePing = async () => {
    return currentLocationPing(userId, taskCoordinates(task));
  };

  const runStageAction = async (key, successMessage, request, nextPath) => {
    if (!taskId) return;
    try {
      setProcessingKey(key);
      await request();
      message.success(successMessage);
      await loadTask();
      if (nextPath) navigate(nextPath);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || `Unable to complete this ${noun} action.`);
    } finally {
      setProcessingKey("");
    }
  };

  const confirmStageAction = ({ title, content, okText, danger = false, action }) => {
    Modal.confirm({
      title,
      content,
      okText,
      okButtonProps: { danger },
      cancelText: "Not now",
      onOk: action,
    });
  };

  const startNavigation = () =>
    runStageAction("navigate", "Navigation started.", async () => {
      const location = await currentLocationPing(userId, taskCoordinates(task), { requirePermission: true });
      if (!location) throw new Error("Location permission is required to start navigation.");
      await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/navigate`, location, { headers: {} });
    }, taskPath("heading"));

  const markArrived = () =>
    confirmStageAction({
      title: "Arrived?",
      content: "Do you want to confirm that you have arrived?",
      okText: "Arrived",
      action: () =>
        runStageAction("arrived", "Arrival confirmed.", async () => {
          await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/arrived`, await stagePing(), {
            headers: taskActionHeaders(userId),
          });
        }),
    });

  const startTask = () =>
    confirmStageAction({
      title: `Start ${titleNoun}?`,
      content: `This will mark the ${noun} as started and move it to In Progress.`,
      okText: "Start",
      action: () =>
        runStageAction("start", `${titleNoun} started.`, async () => {
          await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/start`, await stagePing(), {
            headers: taskActionHeaders(userId),
          });
        }, taskPath("progress")),
    });

  const completeTask = () =>
    confirmStageAction({
      title: "Mark as completed?",
      content: `This will mark the ${noun} as completed.`,
      okText: "Mark as Completed",
      action: () =>
        runStageAction("complete", `${titleNoun} marked as completed.`, async () => {
          await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/completed`, await stagePing(), {
            headers: taskActionHeaders(userId),
          });
        }, taskPath("completed")),
    });

  const submitCancel = () => {
    if (!cancelReason.trim()) {
      message.warning("Please provide a reason for cancellation.");
      return;
    }
    runStageAction("cancel", `${titleNoun} cancelled successfully.`, async () => {
      await apiClient.put(
        `/olitask-service/api/v1/tasks/${taskId}/cancel`,
        {
          pingLocation: await stagePing(),
          cancelReason: cancelReason.trim(),
          photos: [],
        },
        { headers: taskActionHeaders(userId) },
      );
    }, taskPath());
  };

  const submitTip = () => {
    const amount = Number(tipAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      message.warning("Enter a valid tip amount.");
      return;
    }
    runStageAction("tip", "Tip submitted successfully.", async () => {
      await apiClient.post(`/olitask-service/api/v1/tasks/${taskId}/tip`, {
        amount,
        currency: firstText(task?.taskCharges?.currency, task?.currency, "USD"),
      });
      setTipAmount("");
      setSubmittedTip(true);
    });
  };

  const submitReview = () => {
    if (!reviewText.trim()) {
      message.warning("Please enter a review.");
      return;
    }
    runStageAction("review", "Review submitted successfully.", async () => {
      await apiClient.post(
        "/olitask-service/api/v1/task-reviews",
        {
          taskId,
          reviewerId: userId,
          source: reviewSource,
          description: reviewText.trim(),
          rating,
          photos: [],
        },
        { headers: taskActionHeaders(userId) },
      );
      setReviewText("");
      setSubmittedReview(true);
    });
  };

  const openPhotoGallery = (photos, index = 0) => setPhotoGallery({ photos, index });
  const closePhotoGallery = () => setPhotoGallery({ photos: [], index: 0 });
  const shiftPhotoGallery = (direction) => {
    setPhotoGallery((current) => {
      if (!current.photos.length) return current;
      return {
        ...current,
        index: (current.index + direction + current.photos.length) % current.photos.length,
      };
    });
  };
  const handleRouteLiveStatus = useCallback((nextStatus) => {
    if (nextStatus && nextStatus !== status) loadTask({ silent: true });
  }, [loadTask, status]);

  return (
    <div className="my-requests-screen my-task-details-page my-task-stage-page">
      {stageKey === "progress" ? (
        <section className="my-task-progress-live-card">
          <div className="my-task-progress-live-card__heading">
            <span className="my-task-progress-live-card__icon">
              <StarOutlined />
            </span>
            <div>
              <Title level={2}>You’re live on the {noun}</Title>
              <p>Time is running — stay focused.</p>
            </div>
            <Button className="my-task-progress-refresh" loading={loading} onClick={() => loadTask()}>
              <ReloadOutlined />
              Refresh
            </Button>
          </div>

          <div className="my-task-progress-timer-card">
            <div className="my-task-progress-timer-card__header">
              <span className="my-task-stage-live-dot" />
              <strong>Elapsed Time</strong>
            </div>
            <div className="my-task-progress-timer-card__glow">
              <TaskElapsedTimer
                startedAt={task?.helperArrivedAt || task?.startedAt || task?.updatedAt}
                stoppedAt={status === "COMPLETED" ? firstText(task?.helperCompletedAt, task?.completedAt, task?.updatedAt) : null}
              />
            </div>
          </div>
        </section>
      ) : stageKey === "completed" ? (
        <section className="my-task-completed-mobile-head">
          <Title level={2}>{titleNoun.toUpperCase()} COMPLETED</Title>
        </section>
      ) : ["heading", "navigation"].includes(stageKey) ? (
        <section className="my-task-route-mobile-head">
          <Title level={2}>{routeScreenTitle}</Title>
        </section>
      ) : (
        <section className={`my-task-stage-hero is-${stageDisplay.tone}`}>
          <div>
            <span>{stageDisplay.eyebrow}</span>
            <Title level={2}>{stageDisplay.title}</Title>
            <p>{stageDisplay.text}</p>
          </div>
          {task ? (
            <button type="button" onClick={() => navigate(taskPath())}>
              {titleNoun} Details
            </button>
          ) : null}
        </section>
      )}

      {error ? (
        <Alert showIcon type="error" message={`Unable to load ${noun}`} description={error} className="my-requests-alert" />
      ) : null}

      {loading && !task ? (
        <div className="my-task-details-page__loading">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : null}

      {task ? (
        <>
          <section className="my-task-stage-panel">
            {stageKey !== "completed" ? (
              <div className="my-task-stage-panel__status">
                <TaskStatusProgress status={task.status} cancelStatus={task.cancel?.cancelTaskStatus} />
              </div>
            ) : null}

            {stageKey === "heading" || stageKey === "navigation" ? (
              <div className="my-task-route-flow-card">
                <div className="my-task-route-map-header">
                  <div>
                    <EnvironmentOutlined />
                    <strong>{routeAddressLabel}</strong>
                  </div>
                  {!showFullRouteAddress ? (
                    <span>Approximate area shown until the {noun} is accepted.</span>
                  ) : null}
                </div>
                <TaskRouteMap
                  apiKey={googleMapsApiKey}
                  origin={routeOrigin || destinationCoords}
                  destination={destinationCoords}
                  title={routeAddressLabel}
                  addressLabel={routeAddressLabel}
                  showExactDestination={showFullRouteAddress}
                  onRouteInfo={setRouteInfo}
                  taskId={taskId}
                  liveUserId={routeLiveUserId}
                  liveUserIds={routeLiveUserIds}
                  onLiveAddress={setRouteLiveAddress}
                  onLiveStatus={handleRouteLiveStatus}
                />
                <TaskHeadingInfoCard
                  actor={routeActor}
                  actorType={routeActorType}
                  taskId={taskId}
                  eta={routeInfo.duration}
                  distance={routeInfo.distance}
                  currentStreet={routeLiveAddress || routeAddressLabel}
                  taskStatus={status}
                  canConfirmArrived={stageKey === "heading" && canRouteConfirmArrived}
                  canStartTask={stageKey === "heading" && canRouteStartTask}
                  canCancel={canCancel}
                  processingKey={processingKey}
                  onConfirmArrived={markArrived}
                  onStartTask={startTask}
                  onCancelTask={() => navigate(taskPath("cancel"))}
                  onDetails={() => navigate(taskPath())}
                  onActorOpen={setSelectedActor}
                  cancelLabel={`Cancel ${titleNoun} Post`}
                />
              </div>
            ) : null}

            {stageKey === "progress" ? (
              <div className="my-task-progress-actions-card">
                <div className="my-task-progress-actions-card__stack">
                  {canHelperComplete ? (
                    <Button type="primary" className="my-task-progress-action is-primary" loading={processingKey === "complete"} onClick={completeTask}>
                      <FlagOutlined />
                      Mark as Completed
                    </Button>
                  ) : null}
                  <Button className="my-task-progress-action is-details" onClick={() => navigate(taskPath())}>
                    <InfoCircleOutlined />
                    {titleNoun} Details
                  </Button>
                  {canCancel ? (
                    <Button className="my-task-progress-action is-danger" onClick={() => navigate(taskPath("cancel"))}>
                      <CloseCircleOutlined />
                      Cancel {titleNoun} Post
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {stageKey === "cancel" ? (
              <div className="my-task-stage-form">
                <label>
                  <span>Reason</span>
                  <textarea
                    value={cancelReason}
                    onChange={(event) => setCancelReason(event.target.value)}
                    placeholder={`Please explain why you are cancelling this ${noun}...`}
                    disabled={Boolean(processingKey)}
                  />
                </label>
                <div className="my-task-stage-form__actions">
                  <Button type="primary" className="my-requests-action-button is-primary" loading={processingKey === "cancel"} onClick={submitCancel}>
                    Submit
                  </Button>
                  <Button className="my-requests-action-button is-danger" disabled={Boolean(processingKey)} onClick={() => navigate(-1)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}

            {stageKey === "completed" ? (
              <div className="my-task-stage-completed-screen">
                <p className="my-task-stage-completed-message">This {noun} has been marked as completed.</p>
                {canTip ? (
                  <div className="my-task-completed-card my-task-completed-tip-row">
                    <div className="my-task-completed-amount-row">
                      <input
                        value={tipAmount}
                        onChange={(event) => setTipAmount(event.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder="Tip amount"
                        disabled={Boolean(processingKey)}
                        inputMode="decimal"
                      />
                      <span>{firstText(task?.taskCharges?.currency, task?.currency, "USD")}</span>
                    </div>
                    <Button type="primary" className="my-task-completed-tip-button" loading={processingKey === "tip"} onClick={submitTip}>
                      Tip Care Giver
                    </Button>
                  </div>
                ) : null}
                {canReview ? (
                  <div className="my-task-completed-card">
                    <div className="my-task-stage-rating">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          type="button"
                          key={value}
                          className={value <= rating ? "is-active" : ""}
                          onClick={() => setRating(value)}
                          disabled={Boolean(processingKey)}
                        >
                          <StarOutlined />
                        </button>
                      ))}
                    </div>
                    <textarea
                      className="my-task-completed-review-input"
                      value={reviewText}
                      onChange={(event) => setReviewText(event.target.value)}
                      placeholder="Leave a review"
                      disabled={Boolean(processingKey)}
                    />
                    <Button type="primary" className="my-task-completed-review-button" loading={processingKey === "review"} onClick={submitReview}>
                      Submit Review
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

        </>
      ) : null}

      <PhotoGalleryModal
        gallery={photoGallery}
        onClose={closePhotoGallery}
        onShift={shiftPhotoGallery}
        onSelect={(index) => setPhotoGallery((current) => ({ ...current, index }))}
      />
    </div>
  );
}

export function ActorProfilePage({ actorType = "requester" }) {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const roleLabel = actorType === "helper" ? "Care Giver" : "Requester";
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [photoViewMode, setPhotoViewMode] = useState("PROFILE");
  const [resolvedPhotos, setResolvedPhotos] = useState({ profile: "", security: "" });
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(paths.myTasks);
  };

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      if (!actorId) {
        setError(`${roleLabel} ID is missing.`);
        setLoading(false);
        return;
      }

      const endpoints = actorType === "helper"
        ? [
            `/olitask-service/api/v1/helper-profiles/${actorId}`,
            `/olitask-service/api/v1/helpers/${actorId}`,
            `/identity-service/api/v1/users/${actorId}`,
          ]
        : [
            `/olitask-service/api/v1/requester-profiles/${actorId}`,
            `/olitask-service/api/v1/requesters/${actorId}`,
            `/identity-service/api/v1/users/${actorId}`,
          ];

      try {
        setLoading(true);
        setError("");
        let loaded = null;
        for (const endpoint of endpoints) {
          try {
            const response = await apiClient.get(endpoint);
            loaded = response?.data?.data || response?.data;
            if (loaded) break;
          } catch {
            // Try the next profile endpoint.
          }
        }
        if (!loaded) throw new Error(`Unable to load ${roleLabel.toLowerCase()} profile.`);
        if (!cancelled) setProfile(loaded);
      } catch (err) {
        if (!cancelled) setError(err?.message || `Unable to load ${roleLabel.toLowerCase()} profile.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [actorId, actorType, roleLabel]);

  const currentUserId = firstText(
    userIdOf(auth?.user),
    userIdOf(auth?.userProfile),
    userIdOf(auth?.profile),
    userIdOf(auth?.currentUser),
    userIdOf(auth),
  );
  const profileWorkspaceId = firstText(
    profile?.workspaceId,
    profile?.helperWorkspaceId,
    profile?.requesterWorkspaceId,
    profile?.workspace?.id,
    profile?.helperWorkspace?.id,
    profile?.requesterWorkspace?.id,
  );
  const isOwnProfile = Boolean(currentUserId && actorProfileId(profile) && String(currentUserId) === String(actorProfileId(profile)));
  const isSameWorkspace = Boolean(selectedWorkspaceId && profileWorkspaceId && String(selectedWorkspaceId) === String(profileWorkspaceId));
  const canViewPrivateProfileInfo = isOwnProfile || isSameWorkspace;
  const editProfilePath = paths.profile;

  useEffect(() => {
    let cancelled = false;

    const loadPhotoUrls = async () => {
      if (!profile || !canViewPrivateProfileInfo) {
        setResolvedPhotos({ profile: "", security: "" });
        return;
      }

      const profileSource = profilePhotoSource(profile);
      const securitySource = securityPhotoSource(profile);
      const nextPhotos = {
        profile: profileSource.url,
        security: securitySource.url,
      };

      const [signedProfile, signedSecurity] = await Promise.all([
        profileSource.fileId ? resolveSignedPhotoUrl(profileSource.fileId) : Promise.resolve(""),
        securitySource.fileId ? resolveSignedPhotoUrl(securitySource.fileId) : Promise.resolve(""),
      ]);

      if (cancelled) return;
      setResolvedPhotos({
        profile: signedProfile || nextPhotos.profile,
        security: signedSecurity || nextPhotos.security,
      });
    };

    loadPhotoUrls();

    return () => {
      cancelled = true;
    };
  }, [canViewPrivateProfileInfo, profile]);

  const quickRows = actorProfileRows(profile, roleLabel, { canViewContactDetails: canViewPrivateProfileInfo });
  const bioText = actorBioRows(profile)?.[0]?.value || "No bio available.";
  const skills = profileSkills(profile);
  const profilePhotoUrl = canViewPrivateProfileInfo ? resolvedPhotos.profile || profilePhoto(profile) : "";
  const securityPhotoUrl = canViewPrivateProfileInfo ? resolvedPhotos.security || securityPhoto(profile) : "";
  const hasSecurityPhoto = Boolean(securityPhotoUrl);
  const activePhotoUrl = photoViewMode === "SECURITY" && securityPhotoUrl
    ? securityPhotoUrl
    : profilePhotoUrl;
  const activePhotoSource = photoViewMode === "SECURITY"
    ? securityPhotoSource(profile)
    : profilePhotoSource(profile);
  const activePhotoFallbackUrl = canViewPrivateProfileInfo && activePhotoSource.fileId
    ? `${STORAGE_BASE_URL}/api/v1/files/${encodeURIComponent(activePhotoSource.fileId)}`
    : "";
  const activePhotoLabel = photoViewMode === "SECURITY" && securityPhotoUrl
    ? "Security Photo"
    : "Profile Photo";
  const verified = isVerifiedProfile(profile);
  const countValue = firstText(
    profile?.completedTasks,
    profile?.completedTaskCount,
    profile?.taskCount,
    0,
  );
  const countLabel = actorType === "helper" ? "shifts" : "requests";
  const ratingValue = Number(profile?.averageRating ?? profile?.rating);
  const roundedRating = Number.isFinite(ratingValue) ? Math.round(ratingValue) : 0;
  const sectionCaption = roleLabel.toLowerCase();
  const quickInfoIcon = (label) => {
    if (label === "Email") return <MailOutlined />;
    if (label === "Phone") return <PhoneOutlined />;
    if (label === "Location") return <EnvironmentOutlined />;
    if (label === "Sex") return <IdcardOutlined />;
    if (label === "Account Status") return <SafetyCertificateOutlined />;
    return <CheckSquareOutlined />;
  };

  return (
    <div className="my-requests-screen my-task-details-page my-actor-profile-page">
      <section className="my-task-details-page__bar">
        <button
          type="button"
          className="my-task-details-page__back"
          onClick={goBack}
          aria-label="Go back"
        >
          <LeftOutlined />
        </button>
        <div>
          <Title level={2}>{roleLabel} Details</Title>
        </div>
      </section>

      {error ? (
        <Alert
          showIcon
          type="error"
          message={`Unable to load ${roleLabel.toLowerCase()} profile`}
          description={error}
          className="my-requests-alert"
        />
      ) : null}

      {loading && !profile ? (
        <div className="my-task-details-page__loading">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : null}

      {profile ? (
        <Space direction="vertical" size={14} className="my-requests-drawer__content">
          <section className="my-actor-profile-hero">
            {isOwnProfile ? (
              <Button
                type="text"
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => navigate(editProfilePath)}
                className="my-actor-profile-edit-button"
                aria-label="Edit profile"
                title="Edit profile"
              />
            ) : null}

            {canViewPrivateProfileInfo ? (
              <div className="my-actor-profile-photo-switch">
                <button
                  className={photoViewMode === "PROFILE" ? "is-active" : ""}
                  type="button"
                  onClick={() => setPhotoViewMode("PROFILE")}
                >
                  <UserOutlined /> Profile Photo
                </button>
                <button
                  className={[
                    photoViewMode === "SECURITY" ? "is-active" : "",
                    hasSecurityPhoto ? "" : "is-disabled",
                  ].filter(Boolean).join(" ")}
                  type="button"
                  onClick={() => {
                    if (hasSecurityPhoto) setPhotoViewMode("SECURITY");
                  }}
                  disabled={!hasSecurityPhoto}
                >
                  <SafetyCertificateOutlined /> Security Photo
                </button>
              </div>
            ) : null}

            <div className="my-actor-profile-photo">
              {activePhotoUrl ? (
                <img
                  src={activePhotoUrl}
                  alt=""
                  onError={(event) => {
                    if (activePhotoFallbackUrl && event.currentTarget.src !== activePhotoFallbackUrl) {
                      event.currentTarget.src = activePhotoFallbackUrl;
                    }
                  }}
                />
              ) : (
                <UserOutlined />
              )}
            </div>

            <div className="my-actor-profile-photo-mode">
              {canViewPrivateProfileInfo ? (
                <>
                  {activePhotoLabel === "Security Photo" ? <SafetyCertificateOutlined /> : <UserOutlined />}
                  Showing {activePhotoLabel}
                </>
              ) : (
                <>
                  <SafetyCertificateOutlined />
                  Photos visible to the same workspace or yourself
                </>
              )}
            </div>

            <Title level={2}>{personName(profile) || `${roleLabel} Profile`}</Title>
            <Text className="my-actor-profile-joined">Joined {joinedText(profile)}</Text>

            <div className="my-actor-profile-badges">
              <span>
                <CheckSquareOutlined /> {countValue} {countLabel}
              </span>
              <span className={verified ? "is-verified" : "is-unverified"}>
                <SafetyCertificateOutlined /> {verified ? "Verified Account" : "Not Verified"}
              </span>
              <span>
                <StarOutlined /> {ratingText(profile)} rating
              </span>
            </div>
          </section>

          <section className="my-actor-profile-card">
            <div className="my-actor-profile-section-head">
              <div>
                <strong>Ratings & Reviews</strong>
                <span>How other users rate this {sectionCaption}</span>
              </div>
              <em>{ratingText(profile)}</em>
            </div>
            <div className="my-actor-profile-stars" aria-label={`${ratingText(profile)} rating`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarOutlined key={star} className={star <= roundedRating ? "is-filled" : ""} />
              ))}
            </div>
            <button className="my-actor-profile-view-link" type="button" onClick={() => setReviewsOpen(true)}>
              View Reviews
            </button>
          </section>

          <section className="my-actor-profile-card">
            <div className="my-actor-profile-section-head">
              <div>
                <strong>Quick Info</strong>
                <span>A snapshot of this {sectionCaption}</span>
              </div>
            </div>
            <div className="my-actor-profile-info-list">
              {quickRows.map((row) => (
                <div className="my-actor-profile-info-row" key={row.label}>
                  <span className="my-actor-profile-info-icon">{quickInfoIcon(row.label)}</span>
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
          </section>

          <section className="my-actor-profile-card my-actor-profile-card--bio">
            <div className="my-actor-profile-section-head">
              <div>
                <strong>Bio</strong>
                <span>About this {sectionCaption}</span>
              </div>
            </div>
            <Paragraph className="my-actor-profile-bio">{bioText}</Paragraph>
          </section>

          {actorType === "helper" && skills.length ? (
            <section className="my-actor-profile-card">
              <div className="my-actor-profile-section-head">
                <div>
                  <strong>Skills</strong>
                  <span>Tap a skill to see experience and description</span>
                </div>
                <em>{skills.length}</em>
              </div>
              <div className="my-actor-profile-skills">
                {skills.map((skill, index) => (
                  <button
                    className={[
                      "my-actor-profile-skill-chip",
                      index % 3 === 0 ? "is-primary" : "",
                      index % 3 === 1 ? "is-soft" : "",
                      index % 3 === 2 ? "is-gold" : "",
                    ].filter(Boolean).join(" ")}
                    key={skill.id || skill.name}
                    type="button"
                    onClick={() => setSelectedSkill(skill)}
                  >
                    <span className="my-actor-profile-skill-chip__top">
                      <strong>{skill.name}</strong>
                      <i><InfoCircleOutlined /></i>
                    </span>
                    <small>{skill.proficiencyLevel || "N/A"} • {skill.yearsOfExperience ?? 0} yrs</small>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <Modal
            open={Boolean(selectedSkill)}
            footer={null}
            onCancel={() => setSelectedSkill(null)}
            centered
            className="my-actor-profile-skill-modal"
            width={520}
          >
            {selectedSkill ? (
              <div className="my-actor-profile-skill-detail">
                <div className="my-actor-profile-skill-detail__head">
                  <div>
                    <span className="my-actor-profile-skill-detail__badge">
                      <StarOutlined /> Skill Details
                    </span>
                    <Title level={3}>{selectedSkill.name || "Skill"}</Title>
                    <Text>{selectedSkill.proficiencyLevel || "N/A"} • {selectedSkill.yearsOfExperience ?? 0} years experience</Text>
                  </div>
                </div>

                <div className="my-actor-profile-skill-detail__stats">
                  <span><RocketOutlined /> {selectedSkill.proficiencyLevel || "N/A"}</span>
                  <span className="is-gold"><ClockCircleOutlined /> {selectedSkill.yearsOfExperience ?? 0} yrs</span>
                </div>

                {selectedSkill.description ? (
                  <div className="my-actor-profile-skill-detail__section">
                    <strong>Description</strong>
                    <p>{selectedSkill.description}</p>
                  </div>
                ) : null}

                {selectedSkill.synonyms ? (
                  <div className="my-actor-profile-skill-detail__section">
                    <strong>Related Terms</strong>
                    <p>{selectedSkill.synonyms}</p>
                  </div>
                ) : null}

                {!selectedSkill.description && !selectedSkill.synonyms ? (
                  <div className="my-actor-profile-skill-detail__section">
                    <p>No additional skill details available yet.</p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </Modal>

          <ActorReviewsDrawer
            open={reviewsOpen}
            onClose={() => setReviewsOpen(false)}
            actorId={actorProfileId(profile) || actorId}
            actorType={actorType}
            actorName={personName(profile)}
            averageRating={profile?.averageRating ?? profile?.rating}
          />
        </Space>
      ) : null}
    </div>
  );
}
